/**
 * [LEAD-ASSIGNMENT 2026-10-13, user] Lead Assignment — the emails of the two lead mailboxes (leadmaster1 / leadmaster2,
 * read every 5 minutes into SEO_Hubspot_Raw.Leads_Mailbox_Dump), one day at a time, each marked "Leads" or "Not Leads".
 *
 * What the user asked for:
 *   • a date filter — "whatever date we select for that day all the data we will see there";
 *   • "in the middle on the top … a toggle switch": Raw Data | Leads | Irrelevant Leads. Raw Data = that day's emails
 *     not marked yet (every email, replies and notices too); Leads = Leads_Identified; Irrelevant Leads =
 *     Irrelevant_Leads;
 *   • columns Date, First Name, Last, EmailAddress, Prospect Email Copy ("shown normally … when i click on it … the popup
 *     for the mail body text"), Website, Phone Number, Lead Generated By, Geography ("the country from the mail body"),
 *     and a column with the two buttons 'Leads' / 'Not Leads';
 *   • "Leads" = tag "Lead" + a copy in Leads_Identified; "Not Leads" = tag "Not Lead" + a copy in Irrelevant_Leads;
 *     the row then leaves Raw Data (it stays in the mailbox dump with its tag). There is no undo — moving a row back
 *     would mean deleting it from a table.
 *   • A day's Leads = its emails marked Lead plus the Leads Tracker 2.0 leads ASSIGNED that day, whose email may have
 *     come a day or more before (it shows its own date). The same prospect's emails sit one above the other.
 * Server: routes/leadAssignment.ts + services/leadAssignment.ts.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useTransition } from 'react';
import { createPortal } from 'react-dom';
import styled, { createGlobalStyle, DefaultTheme, useTheme } from 'styled-components';
import DataTable from '../components/DataTable';
import SingleDateCalendar from '../components/SingleDateCalendar';
import GlassSelect from '../components/GlassSelect';
import PureInk from '../components/PureInk'; // [PURE-INK 2026-10-15]
import { SlideViewport, SlidePane, slidePos } from '../components/ViewSlide';
import { isForbidden } from '../components/LoadState';
import { SkeletonBox } from '../components/Skeleton';
import { toast, toastError, toastProgress, toastSuccess } from '../components/toastService';
import { fluidDialog, fluidScrim, fluidCascade } from '../components/Fluid';
import { useRealtime } from '../hooks/useRealtime';
import { usePresence } from '../hooks/usePresence';
import { clickEndsPress, isPlainMousePress } from '../hooks/usePressAction';
import { useSaveQueue, queuedToast } from '../hooks/useSaveQueue';
import { useIsMobile } from '../hooks/useIsMobile'; // [PHONE-LAYOUT 2026-10-22]
import { fluid } from '../styles/motion';
import { pillTrack, pillSegment } from '../styles/buttons';
import {
  lgSurface, lgSquircle, lgRim, lgPress, lgControl, lgControlBg, lgControlHover, lgCardSheen, lgButtonPrimary, lgButtonSecondary,
  lgButtonDanger, lgSunken, lgTile, lgOnWhite, lgHair, LG_PRESS, LgPressProps,
} from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02, user] the glass of the rest of the app, on this tab's controls and windows
import {
  fetchLeadDay, fetchLeadEmailThread, leadFileUrl, markLeadEmail, setLeadMark, undoLeadNotLead, undoLeadToRaw, peekCache, invalidateGetCacheByPrefix,
  runAssignmentProcess, fetchLeadReport, LeadReport, NalPerson, // [LEAD-GEN-TAB] [GEN-NAL-RECORDS]
  runAddAssignee, AssigneeHoldRow, AddAssigneeResult, AssignPart, // [ASSIGNEE-C] [ASSIGNEE-HOLD] [ASSIGN-ALL] [ASSIGN-BY-TIME]
  fetchAssigneeNames, saveAssigneeName, AssigneeNamesView, // [ASSIGNEE-NAMES]
  startLeadJob, fetchLeadJob, LeadJob, LeadJobKind, EnrichResult, AssignHubspotResult, LeadsRawResult, // [ENRICH-DATA] [ASSIGN-HUBSPOT] [LEADS-RAW-APP]
  stopLeadJob, // [E-FAST 2026-10-16]
  LeadDay, LeadMark, LeadRow, LeadTag, LeadView, MailFile, MailThread, ReadyDay, ReadyRow, fetchReadyDay,
} from '../services/api';
import { Column, SortConfig } from '../types';
import { useAuth } from '../contexts/AuthContext'; // [COL-RESIZE] each person keeps their own column widths
import { isLeadReportOnly, leadsNoDock } from '../config/sections'; // [TL-REPORT-ONLY 2026-10-19]
import { layoutMax } from '../utils/appZoom'; // [APP-FIT 2026-10-26] laptop width rules follow the laid-out width

/* ── helpers ── */

const dark = (p: { theme: DefaultTheme }) => p.theme.colorScheme === 'dark';
/** The mailbox dump starts on 12 Oct 2026 — no day before it has emails. */
const FIRST_DAY = '2026-10-12';
/** Today in India (the dump's days are IST days). en-CA writes YYYY-MM-DD. */
const IST_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' });
const todayIst = () => IST_DAY.format(new Date());
/** [LEAD-DATE 2026-10-13] A lead may be listed up to this many days ahead (the server checks the same limit). */
const LATEST_DAYS_AHEAD = 30;
/** [LEADS-3-DAYS 2026-10-16, user] "for harshit and shivam, they will only use the month filter for today, yesterday and day
 *  before yesterday … so only for 3 days they will see the data": the Leads type picks the day before yesterday to today
 *  (the server refuses the other days — routes/leadAssignment.ts).
 *  [LEADS-5-DAYS 2026-10-19, user] "… they can see the data for last 5 days that's it and not beyond that": today and the 5 days
 *  before it (6 India days — the old "last 2 days" was today + 2), the same number as the server's. */
const LEADS_TEAM_DAYS = 6;
/** "2026-10-13" + n days → "2026-10-14". */
const addDays = (ymd: string, n: number) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};
const MONTHS =['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "2026-10-12 11:27:11" (already IST) → "12 Oct, 11:27 AM". */
const emailTime = (s: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/.exec(s || '');
  if (!m) return s || '—';
  const h = Number(m[4]);
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}, ${h % 12 || 12}:${m[5]} ${h >= 12 ? 'PM' : 'AM'}`;
};
/** "2026-10-12" → "12 Oct 2026". */
const dayLabel = (d: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d || '');
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}` : d;
};
const MARKED_FMT = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
/** [LEADS-IDENTIFIED-FILL] A HubSpot date (UTC ISO) as its day in India, with the year: "28 Jul 2025" — the page's own
 *  month names (Intl writes September "Sept"). India has no daylight saving: UTC + 5:30 always. */
const hubspotDay = (iso: string) => {
  const t = Date.parse(iso);
  if (isNaN(t)) return iso;
  const d = new Date(t + 330 * 60000);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};
const markedTime = (iso: string | null) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? MARKED_FMT.format(d).replace(' am', ' AM').replace(' pm', ' PM') : '';
};
const errorText = (e: any, fallback: string): string => e?.response?.data?.error || fallback;
/** Who an email is about, for messages: the name, else the website, else the address. */
const whoOf = (r: LeadRow) => [r.firstName, r.lastName].filter(Boolean).join(' ') || r.website || r.email || 'This email';

/* [NO-NOT-LEAD 2026-10-27, user] "for the not lead button and the irrelevant making data, it will not be there from now and only lead and
   nal will be there": no Irrelevant Leads list any more (not loaded, not in the switch) and no Not Leads button — wrong data is NAL with
   its note, and a fixed email of the same website the same day replaces it (server [LEAD-WINS] / One NAL per website). */
const VIEWS: readonly LeadView[] = ['raw', 'leads'];
/** [LEADS-RAW-ONLY 2026-10-14] The lists a person of the Leads type may load. */
const RAW_ONLY: readonly LeadView[] = ['raw'];
/** [LEAD-GEN-TAB 2026-10-14, user] "in the pill where we have tabs raw mailbox, leads identified and irrelevant in that only add
 *  the last one lead generator report": the switch = the three lists, then the Lead Generator Report (a page of its own). */
type PageTab = LeadView | 'report';
const TABS: readonly PageTab[] = ['raw', 'leads', 'report']; // [NO-NOT-LEAD 2026-10-27] Irrelevant Leads is gone
/** [LEADS-TEAM-TABS 2026-10-14, user] "for the lead identified and irrelevant i said that it will be restricted for shivam and
 *  harshit so remove these 2 from there view, they will only see raw mailbox and lead generator report" (the server still
 *  refuses those lists to the Leads type). */
const LEADS_TEAM_TABS: readonly PageTab[] = ['raw', 'report'];
/** [TL-REPORT-ONLY 2026-10-19, user] "in the lead management page they will only see the lead generator report and nothing else": a
 *  team leader — exactly the four the server names (services/leadReportOnly.ts → /api/auth/me lead_report_only; user: "for these 4
 *  members … there are other person also who has the access for the team sameer and also for this so they should not work like
 *  this") — has the report tab only and loads no list. */
const TL_TABS: readonly PageTab[] = ['report'];
const NO_LISTS: readonly LeadView[] = [];
// [PILL-LABELS 2026-10-13, user] "in the raw data heading to the toggle switch dont show the number, it will only show Raw
// Mailbox Data · in the toggle switch leads will changed to 'Leads Identified'".
// [NAL-RECORDS-PAGE 2026-10-27, user] "lead generator report toggle will be now 'NAL Records'"
const VIEW_LABEL: Record<PageTab, string> = { raw: 'Raw Mailbox Data', leads: 'Leads Identified', irrelevant: 'Irrelevant Leads', report: 'NAL Records' };
/** [RUN-IN-PLACE 2026-10-15] the Assignment Process steps by their letter — the running step's name in the progress capsule */
// [DATA-STATUS-FETCH 2026-10-19] AB = Date Status (ROW) + Date Status (Non ROW) as ONE step, every row of the day
const BUSY_NAME = { AB: 'Data Status Fetch', C: 'Add Assignee', D: 'Enrich Data', E: 'Assign to HubSpot', F: 'Transfer to Leads Raw' } as const;
type Counts = LeadDay['counts'];
const NO_SORT: SortConfig = { sortBy: '', sortOrder: 'asc' };

/* [LEAD-SORT 2026-10-15, user] "in the lead management tab for all the columns header add the header click sort thing just
   like we have in the application": a header click sorts the list by that column — the app's DataTable rule (Stats, Delayed
   Payments, Month on Month): the first click A→Z / low→high / oldest first, the same header again turns it round, and a
   blank counts as the lowest value (as everywhere in the app). Equal values keep the list's own order. Until a header is
   clicked each list keeps its own order. The Undo / Action button columns hold no value, so they have no sort. */
const SORT_TEXT = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
/** A cell's value for the sort: a date → its time (ours "YYYY-MM-DD HH:MM:SS" / UTC ISO, the sheet's "DD/MM/YYYY"), a plain
 *  number / id / amount → the number, else the text; blank → null. */
const sortKey = (v: unknown): number | string | null => {
  if (v == null) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'boolean') return v ? 1 : 0;
  const s = String(v).trim();
  if (!s) return null;
  let m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(s);
  if (m) return Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0));
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (m) return Date.UTC(+m[3], +m[2] - 1, +m[1]);
  const num = s.replace(/[$,\s]/g, '');
  return /^-?\d+(\.\d+)?$/.test(num) ? Number(num) : s;
};
const compareKeys = (x: number | string | null, y: number | string | null): number => {
  if (x === null || y === null) return x === y ? 0 : x === null ? -1 : 1;
  return typeof x === 'number' && typeof y === 'number' ? x - y : SORT_TEXT.compare(String(x), String(y));
};
/** The rows in a sort's order (a new array; the same array back with no sort). */
function sortedBy<T>(rows: T[], sort: SortConfig, valueOf: (row: T, key: string) => unknown): T[] {
  if (!sort.sortBy) return rows;
  const dir = sort.sortOrder === 'desc' ? -1 : 1;
  return rows.map((r, i) => ({ r, i, k: sortKey(valueOf(r, sort.sortBy)) }))
    .sort((a, b) => compareKeys(a.k, b.k) * dir || a.i - b.i)
    .map((x) => x.r);
}
/** A header click: a new column → ascending; the same column → the other way round. */
const nextSort = (prev: SortConfig, key: string): SortConfig => ({ sortBy: key, sortOrder: prev.sortBy === key && prev.sortOrder === 'asc' ? 'desc' : 'asc' });
const UNSORTED_COLS = new Set(['undo', 'action']);
const withSort = (cols: Column[]): Column[] => cols.map((c) => (UNSORTED_COLS.has(c.key) ? c : { ...c, sortable: true }));
const cellOf = (row: unknown, key: string): unknown => (row as Record<string, unknown>)[key];
/** New emails arrive from the mailbox job every 5 minutes, which no version number reports: look again every minute. */
const REFRESH_MS = 60_000;
/** The switch's height (the app's pill: 3 px track + 34 px segment + 3 px) — the title and the date sit on its line. */
const SWITCH_H = 42;
type LeadsSub = 'identified' | 'ready';
const LEADS_SUBS: { key: LeadsSub; label: string }[] = [
  { key: 'identified', label: 'Leads Data' }, // [LEADS-DATA-LABEL 2026-10-14, user] "rename this below lead identified to only 'leads data'"
  { key: 'ready', label: 'Ready to be Assigned' },
];

/* ── layout ── */

/* [AUTO-FIT 2026-10-14 → APP-FIT 2026-10-26] This page was the first one laid out as on the user's 1920 px screen and scaled
   to the window with its own CSS zoom. The whole app does that now (utils/appZoom, on <html>), so the page has no zoom of its
   own any more (two would scale it twice); the dock is scaled with the page, so its reach is a plain 88 page px. */

/* [LEAD-FULL-HEIGHT 2026-10-14, user] "the table should show just like it is showing in payment tab and when i want to see
   the end data then it will come above the tab switch": the Payments layout — the page fills the card (a $flush card in
   AppLayout) and never scrolls itself; the top bar and the line above a table stay, only the table scrolls, and its own
   bottom pad (TableScroll) lifts the last row above the floating dock at the very end. */
const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  padding: 4px 2px 0;
  color: ${(p) => p.theme.text}; /* [PURE-INK 2026-10-15] what has no colour of its own reads the pure ink too */

  /* [PHONE-LAYOUT 2026-10-22] phones: the parts sit 10 px apart (18 on the laptop), so the table gets the room */
  /* [PHONE-LAYOUT 2026-10-23, user] ...and NOTHING is fixed at the top: the whole page (title, switch, filters, chips,
     table) is ONE scroll ("in the phone view nothing will be fixed on the top side otherwise how will i see the data for
     the below"). The table and the page view below take their full height (see TableScroll, PaneViewport, InlinePage). */
  @media (max-width: 768px) {
    gap: 10px;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
  }
`;

/* Title left, the switch in the exact middle, the date right — all three on the top line (the slim bar under the switch
   hangs below it). Phones: all three centred, one under the other.
   [HEADER-BAND 2026-10-13, user] "left side heading is not looking good, make it properly": the Payments page's toolbar —
   12 px / 20 px inside, its soft strip and the line under it, reaching the card's edges (the title was 3 px from the
   card's edge here vs 21 px on Payments). */
/* [STACKED-HEADER 2026-10-14, user] "make this like the old one, the toggle switch will be in middle the main one and below
   that the other one, left side heading and right side the date" — back from the one-row header ([ONE-ROW] 10-13). */
const TopBar = styled.div`
  flex-shrink: 0; /* [LEAD-FULL-HEIGHT] */
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: start;
  gap: 16px;
  margin: -4px -2px 0; /* Wrap's own padding: the strip reaches the card's edges */
  padding: 12px 20px;
  background: ${(p) => (dark(p)
    ? 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.04) 100%)'
    : 'linear-gradient(180deg, rgba(241,245,249,0.85) 0%, rgba(229,232,231,0.72) 100%)')};
  border-bottom: 1px solid ${(p) => (dark(p) ? 'rgba(255,255,255,0.10)' : 'rgba(22,25,23,0.10)')};
  box-shadow: inset 0 1px 0 ${(p) => (dark(p) ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.55)')};

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
    justify-items: center;
    gap: 10px; /* [PHONE-LAYOUT 2026-10-22] was 12 */
    padding: 10px 12px;
  }
`;

/* [HEADER 2026-10-13, user] "remove the icon and the second row line, it will show 'Lead Management' and it will be on the
   top corner left only, just like in the payment page on the top left corner we have payment written there": the
   Payments page's title (16 px, bold), on the switch's line. */
const PageTitle = styled.h1`
  height: ${SWITCH_H}px;
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  margin: 0;

  @media (max-width: 768px) { height: auto; text-align: center; }
  /* [PHONE-LAYOUT 2026-10-22] phones: the shared phone page title, 17 px heavy, centred */
  @media (max-width: 768px) { font-size: 17px; font-weight: 800; }
`;

/* [ASSIGNEE-NAMES 2026-10-16, user] "on the left of the main toggle, i want to add a button for the 'Assignee Names'": the title's
   column holds the title and, at its right end (next to the switch), that button — admins only. */
const TitleBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
  @media (max-width: 768px) { justify-content: center; }
  /* [PHONE-LAYOUT 2026-10-22] phones: the title and Assignee Names share the line; a very narrow phone puts the button under */
  @media (max-width: 768px) { flex-wrap: wrap; gap: 8px 10px; }
`;
/* [LIQUID-GLASS 2026-11-02, user] a glass capsule (light edge, shine, a squish and a glow where pressed); 38 px as before */
const NamesBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  height: 38px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  border: 1px solid ${(p) => (dark(p) ? 'transparent' : 'rgba(22,25,23,0.10)')};
  background-color: ${lgControlBg};
  background-image: ${lgCardSheen};
  box-shadow: ${(p) => (dark(p) ? '0 1px 1px rgba(0,0,0,0.25), 0 10px 24px -12px rgba(0,0,0,0.6)' : '0 1px 2px rgba(22,25,23,0.06), 0 8px 20px -10px rgba(22,25,23,0.24)')};
  color: ${(p) => p.theme.text};
  & > * { position: relative; z-index: 1; }
  ${lgRim}
  ${lgPress}
  &:hover { border-color: ${(p) => (dark(p) ? '#4acd8b' : p.theme.green600)}; background-color: ${lgControlHover}; }

  /* [PHONE-LAYOUT 2026-10-22] phones: a little tighter, so it stays on the title's line on a 360 px phone */
  @media (max-width: 768px) { height: 36px; padding: 0 12px; gap: 6px; }
`;

const DateBox = styled.div`
  justify-self: end;
  min-height: ${SWITCH_H}px;
  display: flex;
  align-items: center;
  /* [ASSIGN-PROCESS] the Assignment Process box, then the date; [PAGE-VIEW 2026-10-14] the Single page view button first —
     8 px apart so all three fit at 1366 px with the switch still in the exact middle */
  gap: 8px;
  @media (max-width: 768px) { justify-self: center; }
  /* [PHONE-LAYOUT 2026-10-22] phones: the toolbar spans the card and wraps into rows only when needed: the page's own
     dropdown at its label's width (SubSelect), the Single page view / Stop button, Assignment Process, then the date. The
     date is always the last control here; it keeps its one line and fills what is left of its row. */
  /* [GEN-RANGE 2026-10-27] the one date only (a button) — the report's two dates (GenDates) have their own phone rule */
  @media (max-width: 768px) {
    justify-self: stretch;
    width: 100%;
    min-width: 0;
    flex-wrap: wrap;
    & > button:last-child { flex: 1 0 auto; padding: 0 10px; gap: 8px; }
  }
`;

/* [GEN-RANGE 2026-10-27, user] "in the lead generator report right now i get one date filter but add 2 date filter here, one for the
   start date and one for the end date just like it is in the team sameer report": on the report the date box holds a Start date and
   an End date, each with its caption on its left (Team Sameer Report's DateField). Phones: the two side by side across the card, each
   caption above its date (Team Sameer Report's phone rule). */
const GenDates = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  .f { display: flex; align-items: center; gap: 8px; }
  .cap { font-size: 12px; font-weight: 600; color: ${(p) => p.theme.muted}; white-space: nowrap; }

  @media (max-width: 768px) {
    flex: 1 1 100%;
    min-width: 0;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    .f { flex-direction: column; align-items: stretch; gap: 4px; min-width: 0; }
    .f > button { width: 100%; }
  }
`;

/* [ASSIGN-PROCESS 2026-10-14, user] "on the just left of the date dropdown add a dropdown for 'Assignment Process' and it will
   have values in the dropdown A,B,C and later i will tell you what to do there": the app's GlassSelect in the date box's
   size (38 px, radius 10, 13 px); "Assignment Process" until a value is picked. It does nothing yet. */
const AssignSelect = styled(GlassSelect)`
  && {
    min-width: 168px; /* [PAGE-VIEW 2026-10-14] was 190: "Assignment Process" needs ~172, and the new button needs the room */
    box-sizing: border-box;
    display: flex;
    align-items: center;
    height: 38px;
    padding: 0 34px 0 14px;
    border-radius: 999px; /* [LIQUID-GLASS 2026-11-02] the glass capsule (liquid) at the date box's 38 px */
    font-size: 13px;
    font-weight: 500;
  }
  /* [PHONE-LAYOUT 2026-10-22] phones: no fixed width; it shares its row with the date and takes all the room the date
     leaves ("Assignment Process" needs about 165 px; the date keeps just its own width there) */
  @media (max-width: 768px) {
    && { min-width: 0; flex: 100 1 150px; padding: 0 26px 0 10px; }
  }
`;

/* [E-FAST 2026-10-16, user] "do it have the option to stop if midway": while Assign to HubSpot runs, Stop sits left of the
   Assignment Process box — the app's red button, a 38 px square with the stop sign (■): with a word it pushed the right-hand box
   11 px over the main switch at 1366 px (measured). The run ends after the group of rows in hand (each of them assigned AND
   noted), never inside one; the rest wait for the next run. */
const StopBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonDanger}
  && { width: 38px; height: 38px; flex-shrink: 0; padding: 0; border-radius: 50%; } /* [LIQUID-GLASS 2026-11-02] round red glass */
  &:disabled { cursor: default; }
`;

/* ── the Raw Data | Leads | Irrelevant Leads switch (the app's pill, Sales Pipeline's sliding knob) ── */

const Pill = styled.div`
  position: relative;
  display: inline-flex;
  align-items: stretch;
  max-width: 100%;
  ${pillTrack}
  /* [LIQUID-GLASS 2026-11-02, user] the track is pressed into the bar; the knob is glossy and slides the theme switch's way */
  background: ${(p) => (dark(p) ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
  border: 1px solid transparent;
  box-shadow: ${(p) => (dark(p)
    ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)'
    : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.08)')};

  /* [PHONE-LAYOUT 2026-10-22] phones: the pill keeps its full length and its row (MiddleToggle) swipes instead — so the
     track runs on past the screen's edge with the next page's name peeking, which tells there is more (a pill that scrolled
     inside itself kept its round end at the edge and looked complete). Auto margins centre it when it fits (the Leads
     team's two pages) and start it at the left edge when it does not. Was: overflow-x auto on the pill itself. */
  @media (max-width: 768px) {
    flex: none;
    max-width: none;
    margin: 0 auto;
  }
`;

const PillKnob = styled.div<{ $left: number; $width: number; $ready: boolean }>`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 0;
  width: ${(p) => p.$width}px;
  transform: translateX(${(p) => p.$left}px);
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(255,255,255,0.32), rgba(255,255,255,0) 55%), ${(p) => (dark(p) ? '#4acd8b' : p.theme.green600)};
  box-shadow: 0 2px 8px ${(p) => (dark(p) ? 'rgba(74,205,139,0.30)' : 'rgba(20,107,84,0.30)')}, inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(0,0,0,0.12);
  /* [LIQUID-GLASS 2026-11-02] the theme switch's slide (0.42 s, the same curve); off until it has been drawn in place once */
  transition: ${(p) => (p.$ready ? 'transform 0.42s cubic-bezier(0.32, 0.72, 0, 1), width 0.42s cubic-bezier(0.32, 0.72, 0, 1)' : 'none')};
  opacity: ${(p) => (p.$width ? 1 : 0)};
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const PillOpt = styled.button<{ $active: boolean }>`
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: none;
  background: transparent;
  cursor: pointer;
  ${pillSegment}
  color: ${(p) => (p.$active ? (dark(p) ? '#06281d' : '#ffffff') : p.theme.muted)};
  transition: color 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(:disabled) { color: ${(p) => (p.$active ? (dark(p) ? '#06281d' : '#ffffff') : p.theme.text)}; }
  /* [LEADS-RAW-ONLY 2026-10-14] a view the Leads type cannot open: seen, greyed, a small lock */
  &:disabled { cursor: not-allowed; opacity: 0.5; }
  .lock { font-size: 10px; }

  @media (max-width: 768px) {
    padding: 8px 12px; /* [PHONE-LAYOUT 2026-10-22] the shared compact phone switch: 13 px (was 12 px, 7 / 11 px) */
    font-size: 13px;
    gap: 5px;
    white-space: nowrap;
  }
`;

const PillCount = styled.span<{ $active: boolean }>`
  min-width: 18px;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  line-height: 16px;
  text-align: center;
  font-variant-numeric: tabular-nums;
  background: ${(p) => (p.$active
    ? (dark(p) ? 'rgba(6,40,29,0.16)' : 'rgba(255,255,255,0.24)')
    : (dark(p) ? 'rgba(255,255,255,0.08)' : 'rgba(22,25,23,0.07)'))};
`;

/* The app's pill with the sliding knob — the page switch in the middle of the top bar. [TOGGLE-SWAP 2026-10-15, user] it is
   the page switch again (that morning, [VIEW-DROPDOWN], the pages were a dropdown and this pill held each page's own toggle;
   that toggle is now SubSelect). The knob moves on the press; the page follows as a transition, and a second press while it
   catches up is ignored (the app's toggles: quiet and instant). */
interface PillOption<K extends string> { key: K; label: string; count?: number | null; tip?: string }
function KnobPill<K extends string>({ value, options, onPick, ariaLabel }: {
  value: K; options: PillOption<K>[]; onPick: (k: K) => void; ariaLabel: string;
}) {
  const isMobile = useIsMobile(); // [PHONE-LAYOUT 2026-10-22] the phone's sideways switch (below)
  const [picked, setPicked] = useState<K | null>(null);
  const [pending, startPick] = useTransition();
  useEffect(() => { if (!pending) setPicked(null); }, [pending, value]);
  const shown = picked ?? value;
  const pillRef = useRef<HTMLDivElement>(null);
  const optRefs = useRef<Partial<Record<K, HTMLButtonElement | null>>>({});
  const [knob, setKnob] = useState({ left: 0, width: 0 });
  const [ready, setReady] = useState(false); // [LIQUID-GLASS 2026-11-02] no slide-in when the tab first shows
  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);
  const measure = useCallback(() => {
    const el = optRefs.current[shown];
    if (el) setKnob((k) => (k.left === el.offsetLeft && k.width === el.offsetWidth ? k : { left: el.offsetLeft, width: el.offsetWidth }));
  }, [shown]);
  const countsKey = options.map((o) => (o.count == null ? '' : o.count)).join('|');
  useLayoutEffect(() => { measure(); }, [measure, countsKey]);
  // The labels change size at the phone width and when a count grows a digit: measure again.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !pillRef.current) return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(pillRef.current);
    return () => ro.disconnect();
  }, [measure]);
  /* [PHONE-LAYOUT 2026-10-22] phones: the four pages do not fit one phone line, so the switch swipes sideways — the page
     picked is brought into the middle of it (on the press and when the page opens). The laptop never runs this. */
  useEffect(() => {
    if (!isMobile) return;
    const row = pillRef.current?.parentElement; // MiddleToggle — the row that swipes on a phone
    const el = optRefs.current[shown];
    if (!row || !el || row.scrollWidth <= row.clientWidth + 1) return;
    const rr = row.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    const left = Math.max(0, row.scrollLeft + (er.left - rr.left) - (row.clientWidth - er.width) / 2);
    if (typeof row.scrollTo === 'function') row.scrollTo({ left, behavior: 'smooth' }); else row.scrollLeft = left;
  }, [isMobile, shown]);
  const pick = (k: K) => {
    if (k === shown || pending) return;
    setPicked(k);
    startPick(() => onPick(k));
  };
  const pressedRef = useRef(false);
  const press = (k: K) => ({
    onPointerDown: (e: React.PointerEvent) => {
      pressedRef.current = isPlainMousePress(e);
      if (pressedRef.current) pick(k);
    },
    onClick: (e: React.MouseEvent) => {
      if (clickEndsPress(pressedRef, e)) return; // the press already switched
      pick(k);
    },
  });
  return (
    <Pill ref={pillRef} role="tablist" aria-label={ariaLabel}>
      <PillKnob $left={knob.left} $width={knob.width} $ready={ready} />
      {options.map((o) => (
        <PillOpt
          key={o.key}
          ref={(el) => { optRefs.current[o.key] = el; }}
          type="button"
          role="tab"
          aria-selected={shown === o.key}
          $active={shown === o.key}
          title={o.tip}
          {...press(o.key)}
        >
          {o.label}
          {o.count != null && <PillCount $active={shown === o.key}>{o.count}</PillCount>}
        </PillOpt>
      ))}
    </Pill>
  );
}

/* ── table cells ── */

/* [ONE-LINE 2026-10-13, user] "add the horizontal scroll in the table but it should show the data in single row only,
   like right now email, lead generated by, website gets in 2 lines, i want it to be in single line": every cell keeps to
   ONE line and the table scrolls sideways inside this box. The box is about a screen tall and scrolls down too, so the
   sideways scroll bar and the column heads stay in view (DataTable parentScroll: its sticky head pins to this box). */
/* [LEAD-FULL-HEIGHT 2026-10-14] It fills its view down to the card's bottom (was a box capped at 100vh − 296 / 330 px); the
   88 px pad INSIDE the scroll is the dock clearance, as on Payments: it costs room only at the very end, where it lets the
   last row come up above the dock. */
const TableScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-bottom: 88px; /* the dock's reach (bottom 18 + about 68 tall) — [APP-FIT 2026-10-26] scaled with the page */
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;

  th, td { white-space: nowrap; }
  /* [ROW-POPUP 2026-10-13] the whole row opens the email */
  tbody tr[data-row-index] { cursor: pointer; }

  /* [PHONE-LAYOUT 2026-10-23, user] phones: as tall as its rows (no box of its own to scroll up / down in) — it only
     swipes sideways; the page scrolls. The 88 px end pad still lifts the last row above the dock. */
  @media (max-width: 768px) {
    flex: none;
    min-height: 0;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    overscroll-behavior-y: auto;
  }
`;

/* [LEAD-FULL-HEIGHT] The views take the rest of the page under the top bar; each one is a column — its line (colour key /
   Lead Status counts) on top, the scrolling table filling the rest. */
const PaneViewport = styled(SlideViewport)`
  flex: 1;
  min-height: 0;

  & > [data-lead-pane] {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  /* [PHONE-LAYOUT 2026-10-23, user] phones: the views take their full height and the page scrolls */
  @media (max-width: 768px) {
    flex: none;
    min-height: auto;
    & > [data-lead-pane] { height: auto; }
  }
`;

/* The top bar's middle (on the title / date line): [TOGGLE-SWAP 2026-10-15, user] the page switch (KnobPill). */
const MiddleToggle = styled.div`
  display: flex;
  justify-content: center;
  max-width: 100%;
  min-width: 0;

  /* [PHONE-LAYOUT 2026-10-22] phones: the switch's row is the sideways swipe (no scroll bar shown); see Pill */
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }
`;

/* [TOGGLE-SWAP 2026-10-15, user] The page's own choice (Raw: All · Need attention · Duplicate · NAL; Leads Identified: Leads
   Data · Ready to be Assigned) as a dropdown, the first thing in the right-hand box, left of the Single page view button; the
   Assignment Process box's look. (Was, the same morning: the page switch as this dropdown — [VIEW-DROPDOWN].) */
const SubSelect = styled(AssignSelect)`
  && { min-width: 200px; }
  /* [PHONE-LAYOUT 2026-10-23, user] phones: only as wide as its label ("it can come in a same row if we reduce the size of
     the dropdown"), so "All · 26", the Single page view button and the date share ONE row; the date fills what is left.
     A row that cannot fit wraps the date onto the next line — nothing is cut. */
  @media (max-width: 768px) {
    && { min-width: 0; flex: 0 1 auto; }
  }
`;

/* The Prospect Email Copy: one line, cut with "…" — the whole email is in the row's popup. */
const CopyPreview = styled.span`
  display: inline-block;
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;

  /* [PHONE-LAYOUT 2026-10-22] phones: a shorter preview (the whole email opens on a tap of the row), less to swipe past */
  @media (max-width: 768px) { max-width: 240px; }
`;
/* [READY-WIDTHS 2026-10-14, user] "there are few columns which has so long text fix the width for that column so that it should
   look good on the table": Enrich Data's long values (LinkedIn Description up to ~1,900 characters, Company Keyword ~3,000 —
   columns 11,000–19,000 px wide) stay on one line in a fixed width with "…"; the whole value shows on hover. */
const ClipText = styled.span<{ $w: number }>`
  display: inline-block;
  max-width: ${(p) => p.$w}px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  vertical-align: bottom;

  /* [PHONE-LAYOUT 2026-10-22] phones, Raw Mailbox Data's NAL Note (a person's note, never long): the whole note, wrapped in
     its column (at most the laptop's width) — a phone has no hover to show the rest. Ready to be Assigned: the shorter values
     (LinkedIn link, Industry, Designation, Deal Record ID — the columns under 300 px) wrap the same way; the long enrichment
     texts (LinkedIn Description, Company Keyword, Company Specialities — up to ~3,000 characters) keep the laptop's one line,
     or one row would be a screen tall. */
  @media (max-width: 768px) {
    [data-lead-pane='raw'] & { white-space: normal; overflow: visible; text-overflow: clip; overflow-wrap: anywhere; }
    ${(p) => (p.$w < 300 ? "[data-lead-pane='leads'] & { white-space: normal; overflow: visible; text-overflow: clip; overflow-wrap: anywhere; }" : '')}
  }
`;

const Muted = styled.span`
  color: ${(p) => p.theme.muted};
`;

/* A small tag: the look of the app's ROW tag (components/RowTagBadge: the word on a soft card), by tone.
   [NO-CHIP-DOT 2026-10-14, user] "from this remove the dots" — no dot before the word any more. */
type Tone = 'green' | 'amber' | 'violet' | 'blue' | 'teal' | 'grey' | 'red' | 'yellow';
const TONE: Record<Tone, { light: [string, string]; dark: [string, string] }> = {
  red: { light: ['220,38,38', '#b91c1c'], dark: ['248,113,113', '#fca5a5'] },
  // [RAW-COLOURS 2026-10-14, user] "make the colour yellow" — a deep yellow on the light theme so the text stays readable
  yellow: { light: ['202,138,4', '#854d0e'], dark: ['250,204,21', '#facc15'] }, // [PURE-INK] 6 : 1 (#a16207 was 4.3 : 1)
  green: { light: ['26,159,125', '#0b5e49'], dark: ['74,205,139', '#7de5ab'] }, // [PURE-INK] 5.7 : 1 on tinted rows (#127a5f was 3.9)
  // [PURE-INK 2026-10-15] a shade deeper on the light theme: 6 : 1 on its tint (#b45309 was 4.4 : 1)
  amber: { light: ['217,119,6', '#92400e'], dark: ['251,191,36', '#fcd34d'] },
  violet: { light: ['139,92,246', '#6d28d9'], dark: ['139,92,246', '#c4b5fd'] },
  blue: { light: ['37,99,235', '#1d4ed8'], dark: ['96,165,250', '#93c5fd'] },
  teal: { light: ['13,148,136', '#115e59'], dark: ['45,212,191', '#5eead4'] }, // [PURE-INK] 6 : 1 on tinted rows (#0f766e was 4.4)
  // [PURE-INK 2026-10-15, user] "not use the light shade of the colours": a grey chip's words are the pure ink (its tint stays grey)
  grey: { light: ['107,114,128', '#000000'], dark: ['156,163,175', '#ffffff'] },
};

/* [RAW-COLOURS 2026-10-14, user] The conditional formatting of the user's "Auto data fetch" sheet ("Leads" tab) as TEXT
   colours on Raw Mailbox Data only ("you can make the text colour add to the data … will be added to the raw mailbox data
   only"), with a key above the table saying what each colour means. The rules as the user gave them:
   Email:   REGEXMATCH(LOWER(email), "(^|[._+-@])(icloud|comcast|mac|support|service|reception|admin|helpdesk|customercare|
            sales|customerservice|enquiries|enquery|reservations)([._+-@]|$)")
   Website: REGEXREPLACE(website, "https?://|www.", "") → the part before the first "/" has 2 or more dots.
            [NO-UK-YELLOW 2026-10-16, user] "if i get the website with the yellow highlight but the website has .uk at the end
            so no need to show the yellow colour to that data": a website ending in .uk (x.co.uk, x.org.uk) is not yellow.
   Website (green in the sheet): the same website in an earlier lead email within 30 days — [NO-WEBSITE-GREEN 2026-10-16,
            user] removed ("I don't want to use that conditional formatting, so remove that"); the server no longer works it out.
   Phone:   the sheet tests J = RIGHT(digits of the phone, 10): 10 digits there, and the first 3 are NOT a US area code
            ([2-9]XX and not one of the toll-free / 555 / Caribbean / Canadian codes below). */
const ROLE_EMAIL_RE = /(^|[._+\-@])(icloud|comcast|mac|support|service|reception|admin|helpdesk|customercare|sales|customerservice|enquiries|enquery|reservations)([._+\-@]|$)/;
const roleEmail = (v: string | null | undefined) => !!v && ROLE_EMAIL_RE.test(v.toLowerCase());
const manyDots = (v: string | null | undefined) => {
  if (!v) return false;
  const domain = /^[^/]+/.exec(v.replace(/https?:\/\/|www\./g, ''));
  if (domain && /\.uk$/i.test(domain[0].trim())) return false; // [NO-UK-YELLOW] a .uk website is never yellow
  return !!domain && (domain[0].match(/\./g) || []).length >= 2;
};
const NOT_US_AREA = new Set(('800 822 833 844 855 866 877 888 555 242 246 264 268 284 340 345 441 473 649 664 670 671 684 709 721 '
  + '758 767 784 787 809 829 849 867 868 869 876 204 226 236 249 250 257 263 289 306 343 354 365 367 368 382 403 416 418 428 431 '
  + '437 438 450 468 474 506 514 519 548 579 581 584 587 604 613 639 647 672 683 705 709 742 753 778 780 807 819 825 873 879 902 '
  + '905 942').split(' '));
const notUsPhone = (v: string | null | undefined) => {
  const digits = String(v ?? '').replace(/\D/g, '');
  if (digits.length < 10) return false;
  const area = digits.slice(-10, -7);
  return !(/^[2-9]\d{2}$/.test(area) && !NOT_US_AREA.has(area));
};
/** [DETAIL-COLOURS 2026-10-14] One colour per rule for the Raw table's cells AND the email page's details (the left side of
 *  the popup / Single page view) — null = no colour. */
const emailTone = (v: string | null | undefined): Tone | null => (roleEmail(v) ? 'yellow' : null);
const websiteTone = (v: string | null | undefined): Tone | null => (manyDots(v) ? 'yellow' : null); // [NO-WEBSITE-GREEN] no green
const phoneTone = (v: string | null | undefined): Tone | null => (notUsPhone(v) ? 'yellow' : null);
/* [RAW-COLOURS 2026-10-14, user] "make the colour yellow for email and phone number and in the website it will have 2
   colours yellow and green" (as the sheet: yellow everywhere, green for a website that came before).
   [NO-COLOUR-KEY 2026-10-14, user] the "Colours:" key line above the Raw table is gone for everyone ("Remove for
   everyone"); the colours themselves stay. */
const Flagged = styled.span<{ $tone: Tone }>`
  color: ${(p) => TONE[p.$tone][dark(p) ? 'dark' : 'light'][1]};
  font-weight: 700;
`;
/** [DETAIL-COLOURS] a detail's value, in its rule's colour when it has one. */
const detailText = (tone: Tone | null, v: string | null | undefined) => (v ? (tone ? <Flagged $tone={tone}>{v}</Flagged> : v) : '—');
/* [LEAD-STATUS-COUNTS 2026-10-14, user] "in the leads data section, above the table, I want to see the data like the count of
   fresh rows, the count of regenerated rows, and the count of duplicate rows": the day's Leads Data rows by their Lead
   Status (the Leads Tracker 2.0 values Fresh / Re-generated / Duplicate). */
const LEAD_STATUS_COUNTS: { key: string; label: string; tone: Tone }[] = [
  { key: 'fresh', label: 'Fresh', tone: 'green' },
  { key: 'regenerated', label: 'Re-generated', tone: 'blue' },
  { key: 'duplicate', label: 'Duplicate', tone: 'amber' },
];
const statusKey = (v: string | null | undefined) => String(v ?? '').toLowerCase().replace(/[^a-z]/g, '');

const ColourKey = styled.div`
  flex-shrink: 0; /* [LEAD-FULL-HEIGHT] stays above the scrolling table */
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 18px;
  padding: 2px 4px 12px;
  font-size: 12px;
  line-height: 18px;
  color: ${(p) => p.theme.muted};

  .t { font-weight: 700; color: ${(p) => p.theme.text}; }
`;
/** [LEAD-STATUS-COUNTS] the same line with count chips (22 px tall): 32 px in all, like the colour key. */
const StatusCounts = styled(ColourKey)`
  padding: 0 4px 10px;
  .g2 { margin-left: 14px; } /* [ROW-STATUS-COUNTS] the second group starts a little further on */
  .end { margin-left: auto; } /* [DONE-COUNTS 2026-10-16] "N Processed / Assigned of M rows" at the line's right end */

  /* [PHONE-LAYOUT 2026-10-22] phones: ONE row of chips that swipes sideways inside itself (on a 360 px phone the
     wrapped chips took three lines and the fixed top about 48 % of the screen); closer together. */
  @media (max-width: 768px) {
    flex-wrap: nowrap;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
    gap: 6px 8px;
    padding: 0 2px 6px;
    .g2 { margin-left: 4px; }
    & > * { flex: none; white-space: nowrap; }
  }
`;

const Chip = styled.span<{ $tone: Tone }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 6px;
  line-height: 1;
  padding: 4px 9px;
  white-space: nowrap;
  font-size: 11.5px;
  font-weight: 700;
  color: ${(p) => TONE[p.$tone][dark(p) ? 'dark' : 'light'][1]};
  /* [NO-CHIP-BORDER 2026-10-14, user] "remove the outer border from this": no visible edge — kept transparent, so every chip
     keeps its exact size (no row moves). */
  border: 1px solid transparent;
  background: rgba(${(p) => TONE[p.$tone][dark(p) ? 'dark' : 'light'][0]}, 0.12);
  /* [LIQUID-GLASS 2026-11-02, user] the count line above a table: small glossy capsules (a shine, a light top edge), same colours */
  ${StatusCounts} & {
    border-radius: 999px;
    background-image: ${(p) => (dark(p) ? 'linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0) 60%)' : 'linear-gradient(180deg, rgba(255,255,255,0.70), rgba(255,255,255,0) 60%)')};
    box-shadow: inset 0 1px 0 ${(p) => (dark(p) ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.9)')}, 0 1px 2px ${(p) => (dark(p) ? 'rgba(0,0,0,0.25)' : 'rgba(22,25,23,0.08)')};
  }
`;

const Chips = styled.span<{ $wrap?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  ${(p) => (p.$wrap ? 'flex-wrap: wrap; & > * { white-space: normal; line-height: 1.35; }' : '')}
`;

const cutTextTo = (s: string | null | undefined, n: number) => { const t = String(s || ''); return t.length > n ? `${t.slice(0, n - 1)}…` : t; };

/** [RAW-STATUS 2026-10-13, user] "what rows are up that we can make to the leads and what rows are of no use, what actually
 *  those rows tell us" — read from the row's own conversation in the lead mailbox (the team's forwards and replies are kept
 *  too; server rawStatus):
 *   To map (green) — a lead email nobody has acted on: the ones to decide. "Unread" = still unread in Gmail ([NO-2.0-TAG
 *     2026-10-14] the amber "In 2.0" chip is gone)
 *   Forwarded to … / Asked: … (blue) — the team acted, the outcome is still open
 *   NAL - … (red) / Duplicate lead (amber) — the verdict, in the team's (or the Leads Dept's) own words
 *   Already in Leads (teal) / Leads Dept: … · Reply (grey) — an email inside a conversation; "↳" names the email it
 *     belongs to ([user] "what row is related to the previous one")
 *   Not a lead email (grey) — no lead details, no verdict
 *  [NO-MARK-TAG 2026-10-13, user] "when i map the duplicate and nal then dont show the tag on the row, because we have
 *  already made the colour for it and it goes to the bottom": the tab's own Duplicate / NAL marks show only as the row's
 *  colour (yellow / red) and its place — no chip. */
const TagCell: React.FC<{ row: LeadRow; full?: boolean }> = ({ row, full }) => {
  // full = the popup: every word, wrapped, with the time — the table keeps one line ("single-line + horizontal scroll").
  const cutText = (s: string | null | undefined, n: number) => (full ? `${s || ''}${row.kindAt && s === row.kindText ? ` · ${emailTime(row.kindAt)}` : ''}` : cutTextTo(s, n));
  const k = row.kind ?? (row.alreadyInLeads ? 'in-leads' : null);
  const said = row.kindText || '';
  const when = row.kindAt ? ` (${emailTime(row.kindAt)})` : '';
  const main = k === 'to-map' ? <Chip $tone="green" title="A lead email nobody has acted on yet — decide it">To map</Chip>
    : k === 'waiting' ? <Chip $tone="blue" title={`The team acted on it, the outcome is still open — ${said}${when}`}>{cutText(said || 'Waiting', 48)}</Chip>
      : k === 'nal' ? <Chip $tone="red" title={`Not a lead — the team's verdict${when}: ${said}`}>{cutText(said || 'NAL', 48)}</Chip>
        : k === 'duplicate' ? <Chip $tone="amber" title={`A duplicate — the verdict${when}: ${said}`}>{cutText(said || 'Duplicate lead', 48)}</Chip>
          : k === 'in-leads' ? <Chip $tone="teal" title="An email inside a conversation whose lead is already under Leads">Already in Leads</Chip>
            : k === 'reply' ? <Chip $tone="grey" title={`An answer inside a conversation — not a lead itself${said ? `: ${said}` : ''}`}>{cutText(said || 'Reply', 44)}</Chip>
              : k === 'not-lead' ? <Chip $tone="grey" title={`No Name / Email / Website lines in it${said ? ` — ${said}${when}` : ''}`}>Not a lead email</Chip>
                : null;
  if (!main) return <Muted>—</Muted>;
  return (
    <Chips $wrap={full}>
      {main}
      {row.unread && <Chip $tone="violet" title="Still unread in the lead mailbox (Gmail)">Unread</Chip>}
      {/* [NO-2.0-TAG 2026-10-14, user] "for the tag column it has this yellow tag, i dont want to see that, remove it" — the
          amber "In 2.0 · <day> → <assignee>" chip is not shown any more. */}
      {row.about && <Muted title={`Belongs to the email: ${row.about}`}>↳ {full ? `Belongs to: ${row.about}` : cutTextTo(row.about, 52)}</Muted>}
    </Chips>
  );
};

/** [RAW-FILTER 2026-10-13, user] "this will only show all, Need attention (rows which dont have row colour in the raw data
 *  side), duplicate, nal — that's it": the filter above Raw Data goes by the row's colour — the tab's own marks. */
// [DUP-AFTER-OFF 2026-10-14, user] "remove duplicate after assignment no need of this now": the 5th section "Duplicate
// (After Assignment)" is gone from the filter (the server still sends `afterAssignment` with Raw; nothing reads it).
type RawFilter = 'all' | 'attention' | 'duplicate' | 'nal';
const RAW_FILTERS: { key: RawFilter; label: string; tip: string }[] = [
  { key: 'all', label: 'All', tip: 'Every email of the day still in Raw Mailbox Data' },
  { key: 'attention', label: 'Need attention', tip: 'The rows with no colour — not marked Duplicate or NAL yet' },
  { key: 'duplicate', label: 'Duplicate', tip: 'The rows marked Duplicate (yellow)' },
  { key: 'nal', label: 'NAL', tip: 'The rows marked NAL (red)' },
];
const inFilter = (r: LeadRow, f: RawFilter) => (f === 'all' ? true
  : f === 'duplicate' ? !!r.duplicate
    : f === 'nal' ? !!r.nal
      : !r.duplicate && !r.nal);
/** [DUPLICATE] [NAL-MARK] Raw Data's order (as the server sends it): unmarked rows by time, then the Duplicate block, then
 *  the NAL block — each block by when it was marked. So a row just marked goes to the END of its block, and a row whose
 *  mark comes off goes back among the unmarked rows by its time; every other row keeps its place. */
const markRank = (r: LeadRow) => (r.nal ? 2 : r.duplicate ? 1 : 0);
const placeRow = (rows: LeadRow[], row: LeadRow): LeadRow[] => {
  const rest = rows.filter((r) => r.id !== row.id);
  const rank = markRank(row);
  const i = rank === 0
    ? rest.findIndex((r) => markRank(r) > 0 || r.date > row.date || (r.date === row.date && r.id > row.id))
    : rest.findIndex((r) => markRank(r) > rank);
  return i < 0 ? [...rest, row] : [...rest.slice(0, i), row, ...rest.slice(i)];
};

/** [LEAD-SORT] The Tag column's words (TagCell's main chip). */
const tagText = (r: LeadRow): string | null => {
  const k = r.kind ?? (r.alreadyInLeads ? 'in-leads' : null);
  const said = r.kindText || '';
  return k === 'to-map' ? 'To map' : k === 'waiting' ? said || 'Waiting' : k === 'nal' ? said || 'NAL'
    : k === 'duplicate' ? said || 'Duplicate lead' : k === 'in-leads' ? 'Already in Leads'
      : k === 'reply' ? said || 'Reply' : k === 'not-lead' ? 'Not a lead email' : null;
};
/** [LEAD-SORT] What a Raw Mailbox Data / Leads Data / Irrelevant Leads column sorts by — what its cell shows. */
const leadSortValue = (view: LeadView) => (r: LeadRow, key: string): unknown => {
  switch (key) {
    case 'alreadyInLeads': return tagText(r); // Tag
    case 'actions': return markRank(r); // not marked · Duplicate (old marks — [NO-DUP-BUTTON]) · NAL
    case 'markedBy': return r.markedBy ? `${r.markedBy} ${r.markedAt ?? ''}` : null; // who, then when
    case 'businessType': return r.businessType || 'Scale Ranking';
    case 'region': return view === 'leads' ? (r.region === 'ROW' ? 'ROW' : null) : r.region; // Leads Data's ROW Status shows ROW only
    default: return cellOf(r, key);
  }
};
const LEAD_SORT_VALUE: Record<LeadView, (r: LeadRow, key: string) => unknown> = {
  raw: leadSortValue('raw'), leads: leadSortValue('leads'), irrelevant: leadSortValue('irrelevant'),
};
/** [LEAD-SORT] Ready to be Assigned: the cell's value; [READY-ROW-BLANK] ROW Status = ROW or blank, as shown. */
const readySortValue = (r: ReadyRow, key: string): unknown => (key === 'rowStatus' ? (r.rowStatus === 'ROW' ? 'ROW' : null) : cellOf(r, key));

/** [US-ROW 2026-10-13, user] "if the mailbody has the country related to us, usa, united states or canada then it will
 *  work US and for the others it will show 'ROW'"; no Country line → "—". */
const RegionCell: React.FC<{ region: LeadRow['region'] }> = ({ region }) => (region
  ? <Chip $tone={region === 'US' ? 'blue' : 'violet'}>{region}</Chip>
  : <Muted>—</Muted>);

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

/* [LIQUID-GLASS 2026-11-02, user] glossy green / glass capsules (styles/liquidGlass.ts) at the old size (32 px, 14 px sides), so no
   table row gets taller; the plain ones keep a thin edge in light (they sit on white rows) */
const LeadBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonPrimary}
  height: 32px;
  padding: 0 14px;
`;

const NotLeadBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonSecondary}
  ${lgOnWhite}
  height: 32px;
  padding: 0 14px;
`;

/* [DUPLICATE-TOGGLE] [NAL-MARK 2026-10-13, user] Duplicate / NAL are switches: on = the mark's colour with a tick, and the
   same click takes the mark off again ("if i click on the duplicate again the process will become undo"). */
const MarkBtn = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $on: boolean; $tone: 'amber' | 'red' }>`
  ${lgButtonSecondary}
  ${lgOnWhite}
  height: 32px;
  padding: 0 14px;
  /* [LIQUID-GLASS 2026-11-02] on = the mark's colour in the glass: a tinted capsule with a tinted light edge */
  ${(p) => {
    if (!p.$on) return '';
    const [rgb, ink] = TONE[p.$tone][dark(p) ? 'dark' : 'light'];
    return `
      color: ${ink};
      background-color: rgba(${rgb}, 0.14);
      box-shadow: inset 0 0 0 1px rgba(${rgb}, 0.45), 0 1px 2px rgba(0,0,0,0.08);
      &::before { background: linear-gradient(180deg, rgba(${rgb}, 0.75), rgba(${rgb}, 0.15) 45%, rgba(${rgb}, 0.35)); }
      &:hover:not(:disabled) { background-color: rgba(${rgb}, 0.2); }
    `;
  }}
`;

/** The mark switch — in a table row and in the popup / Single page view.
 *  [NO-DUP-BUTTON 2026-10-19, user] "in the raw mailbox data i dont want the duplicate button … remove that button and for the
 *  previous days if the row is in duplicate section then leave it as it is, but from now onwards the duplicate button in the rows
 *  will not be there": only NAL is left. The rows already marked Duplicate keep their mark — yellow, in their block, under the
 *  Duplicate filter — and the server takes no new Duplicate mark (routes/leadAssignment.ts). */
/* [NAL-NOTE 2026-10-22] NAL asks why first (NalNoteDialog) — the mark is saved only with its note; the hint shows the note. */
const MarkButtons: React.FC<{ row: LeadRow; onToggle: (m: LeadMark) => void }> = ({ row, onToggle }) => (
  <>
    <MarkBtn type="button" $on={!!row.nal} $tone="red" aria-pressed={!!row.nal} onClick={() => onToggle('nal')}
      title={row.nal ? `Marked NAL${row.nalNote ? `: ${row.nalNote}` : ''} — click again to take the mark off` : 'NAL: write why it is NAL, then it stays in Raw Data, at the very bottom, red'}>
      {row.nal && <i className="fas fa-check" />} NAL
    </MarkBtn>
  </>
);
/** [NAL-NOTE 2026-10-22] where a mark was clicked — the popup / Single page view step on to the next email once it is saved */
type MarkFrom = 'table' | 'popup' | 'page';

const Marked = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  b { font-weight: 600; font-size: 12.5px; }
  span { font-size: 11.5px; color: ${(p) => p.theme.muted}; }
`;

/* ── the email popup — [ROW-POPUP 2026-10-13] the size of the screen ── */

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: ${(p) => (dark(p) ? 'rgba(0, 0, 0, 0.35)' : 'rgba(20, 30, 25, 0.18)')};
  backdrop-filter: blur(12px) saturate(115%); /* [LIQUID-GLASS 2026-11-02] Edit Mapping's shade (was 14 px, 140 %) */
  -webkit-backdrop-filter: blur(12px) saturate(115%);
  ${(p) => fluidScrim(p.$open)}

  @media (max-width: 768px) { padding: 0; }
`;

/* [LIQUID-GLASS 2026-11-02, user] The windows are the app's frosted glass now (styles/liquidGlass.ts lgSurface — Edit Mapping and
   Add Payment): a see-through tint with a fine grain, a light top edge and a soft bottom reflection, smooth corners. (Was the
   old glass: blur 48 px with a 180 % colour boost.) The email window fills a phone's screen, so there it has no edge line and
   no round corners. */
const Box = styled.div<{ $open: boolean }>`
  ${(p) => fluidDialog({ open: p.$open })}
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  color: ${(p) => p.theme.text};
  ${lgSurface(38)}
  ${lgSquircle(24)}

  @media (max-width: 768px) { border-radius: 0; &::before { display: none; } }
`;

const PopTop = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  border-bottom: 1px solid ${(p) => p.theme.line};

  .who { flex: 1 1 auto; min-width: 0; }
  /* [COPY-FIELD 2026-10-19] the name / subject keep one line (… when long) and their copy button sits right after them */
  h3 { display: flex; align-items: center; gap: 8px; margin: 0; font-size: 18px; font-weight: 800; min-width: 0; }
  .subject { display: flex; align-items: center; gap: 6px; margin-top: 3px; font-size: 13px; color: ${(p) => p.theme.muted}; min-width: 0; }
  h3 .t, .subject .t { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  @media (max-width: 768px) {
    flex-wrap: wrap;
    padding: 12px 14px;
    .who { flex-basis: 100%; }
    gap: 10px; /* [PHONE-LAYOUT 2026-10-22] the name / subject, then the buttons' rows */
    h3 { font-size: 16px; }
  }
`;

const Nav = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  .pos { font-size: 12.5px; font-weight: 600; color: ${(p) => p.theme.muted}; font-variant-numeric: tabular-nums; margin-right: 4px; }

  /* [PHONE-LAYOUT 2026-10-22] phones: the row's buttons on a line of their own (equal widths), then "N of M" at the left with
     Previous / Next (and Close) at the right */
  @media (max-width: 768px) {
    flex: 1 1 100%;
    flex-wrap: wrap;
    justify-content: flex-end;
    .pos { margin-right: auto; }
  }
`;

const NavBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonSecondary}
  ${lgOnWhite}
  height: 32px; /* [LIQUID-GLASS 2026-11-02] a glass capsule at the old size */
  padding: 0 14px;
  @media (max-width: 768px) { height: 36px; } /* [PHONE-LAYOUT 2026-10-22] a finger-sized Previous / Next */
`;

/* [LIQUID-GLASS 2026-11-02] the round glass close button of the app's windows */
const CloseX = styled.button.attrs<LgPressProps>(LG_PRESS)`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  padding: 0;
  border: none;
  color: ${(p) => p.theme.text};
  cursor: pointer;
  ${lgControl}
  ${lgPress}
  &:hover { color: ${(p) => p.theme.greenInk}; }
`;

const PopMain = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(300px, 400px) minmax(0, 1fr);

  ${layoutMax(900)} {
    grid-template-columns: minmax(0, 1fr);
    overflow-y: auto;
  }
  /* [PHONE-LAYOUT 2026-10-22] phones, the Single page view: no box of its own — the whole page view scrolls as one */
  @media (max-width: 768px) {
    [data-page-view] & { flex: none; overflow: visible; }
  }
`;

/* The lead's details, one per line: label left, value right. */
const Details = styled.dl`
  margin: 0;
  padding: 18px 20px;
  overflow-y: auto;
  border-right: 1px solid ${(p) => p.theme.line};
  display: grid;
  grid-template-columns: 132px minmax(0, 1fr);
  align-content: start;
  gap: 11px 14px;

  dt { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: ${(p) => p.theme.greenInk}; padding-top: 2px; } /* [PURE-INK] greenInk: 4.7 : 1 on the light popup (green600 4.3), dark the same #4acd8b */
  dd { margin: 0; font-size: 13.5px; overflow-wrap: anywhere; }
  /* [COPY-FIELD 2026-10-19] the copy button flows right after the value's last character, like a word — the value keeps the whole
     width (a long email no longer breaks a letter early, as a side-by-side button made it); the minus margins keep the rows'
     height as it was */
  dd > [data-copy] { margin: -3px 0 -3px 6px; vertical-align: middle; }

  ${layoutMax(900)} { border-right: none; border-bottom: 1px solid ${(p) => p.theme.line}; overflow: visible; }
  /* [PHONE-LAYOUT 2026-10-22] phones: a narrower label column, so the values get the line; the bigger copy button keeps the
     lines' height (minus margins) */
  @media (max-width: 768px) {
    padding: 14px;
    grid-template-columns: 108px minmax(0, 1fr);
    gap: 10px 12px;
    dd > [data-copy] { margin: -5px 0 -5px 6px; }
  }
`;

/* [COPY-FIELD 2026-10-19, user] "for the single page view on the left side … after every text it should have a button to copy the
   data … it will somehow help the harshit and shivam also as they cant copy all the text so they can simply click on that box and
   copy the single text … that feature will work for all and it will also be shown to harshit and shivam account": a small copy
   button after each value of the email page's left side (and the name + subject above it) — for everyone, the Leads type too, and
   in the row popup as well (the same page). The tab's [NO-COPY] rule stays for everything else: non-admins still cannot select or
   copy the page — the button hands over ONE value on purpose. No hover hint ([NO-HINTS]); the icon turns into a tick and the
   message says what was copied. */
/* [COPY-WHITE 2026-10-19, user] "make the copy button white in colour and not the green": the page's own ink — pure white on the
   dark theme (pure black on the light one, where white would not show — the [PURE-INK] rule); no green, the tick too. */
const CopyBtn = styled.button<{ $done: boolean }>`
  flex: none;
  width: 22px;
  height: 22px;
  display: inline-grid;
  place-items: center;
  padding: 0;
  /* [LIQUID-GLASS 2026-11-02] a tiny glass button (shine, a squish and a glow where pressed); still the page's ink, still a tick */
  position: relative;
  isolation: isolate;
  overflow: hidden;
  border: 1px solid ${(p) => (p.$done ? p.theme.text : dark(p) ? 'rgba(255,255,255,0.14)' : 'rgba(22,25,23,0.16)')};
  border-radius: 7px;
  background-color: ${(p) => (p.$done ? (dark(p) ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.08)') : lgControlBg(p))};
  background-image: ${lgCardSheen};
  box-shadow: ${(p) => (dark(p) ? '0 1px 1px rgba(0,0,0,0.25)' : '0 1px 2px rgba(22,25,23,0.08)')};
  color: ${(p) => p.theme.text};
  font-size: 10.5px;
  line-height: 1;
  cursor: pointer;
  ${lgPress}

  &:hover { border-color: ${(p) => p.theme.text}; background-color: ${(p) => (dark(p) ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.95)')}; }
  &:focus-visible { outline: 2px solid ${(p) => p.theme.text}; outline-offset: 1px; }

  /* [PHONE-LAYOUT 2026-10-22] phones: a finger-sized copy button (22 px is hard to hit) */
  @media (max-width: 768px) { width: 28px; height: 28px; font-size: 12px; }
`;

/** [COPY-FIELD] Puts `text` on the clipboard. The Clipboard API first (the app runs on https / localhost); else the copy command
 *  with the text set ON its copy event — for non-admins this tab cancels every copy and blocks selecting ([NO-COPY]), so the usual
 *  "select a hidden textarea" fallback would copy nothing. */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
  } catch { /* not allowed here — the copy command below */ }
  let put = false;
  const onCopy = (e: ClipboardEvent) => {
    if (!e.clipboardData) return;
    e.clipboardData.setData('text/plain', text);
    e.preventDefault();
    put = true;
  };
  document.addEventListener('copy', onCopy, true);
  try { document.execCommand('copy'); } catch { /* refused */ } finally { document.removeEventListener('copy', onCopy, true); }
  return put;
}

/** [COPY-FIELD] The copy button for one value (nothing when there is no value). */
const CopyValue: React.FC<{ label: string; text: string | null | undefined }> = ({ label, text }) => {
  const value = String(text ?? '').trim();
  const [done, setDone] = useState(false);
  useEffect(() => { setDone(false); }, [value]);
  useEffect(() => {
    if (!done) return undefined;
    const t = window.setTimeout(() => setDone(false), 1400);
    return () => window.clearTimeout(t);
  }, [done]);
  if (!value) return null;
  const onClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (await copyToClipboard(value)) { setDone(true); toastSuccess(`${label} copied`); } else toastError(`Could not copy the ${label}.`);
  };
  return (
    <CopyBtn type="button" $done={done} aria-label={`Copy ${label}`} data-copy={label} data-lg-press="" onClick={onClick}>
      <i className={done ? 'fas fa-check' : 'fas fa-copy'} aria-hidden="true" />
    </CopyBtn>
  );
};

/** [COPY-FIELD] One line of the left side: the label, the value as shown, and the copy button for `copy` (what the value says). */
const Field: React.FC<{ label: string; copy?: string | null; children: React.ReactNode }> = ({ label, copy, children }) => (
  <>
    <dt>{label}</dt>
    <dd><span className="val">{children}</span><CopyValue label={label} text={copy} /></dd>
  </>
);

/* ── the email itself — [MAIL-CONTENT 2026-10-13, user] like the actual mail: the conversation we hold, each email's
   own layout (reply trail, signature, pictures) on white paper, its attachments ── */

const MailPane = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  overflow: hidden;

  ${layoutMax(900)} { overflow: visible; }
`;

/* The emails of this conversation we hold (the lead + replies), oldest first; the one on show is marked. */
const Convo = styled.div`
  flex: 0 0 auto;
  max-height: 150px;
  overflow-y: auto;
  border-bottom: 1px solid ${(p) => p.theme.line};
  padding: 8px 12px;

  .title { font-size: 11px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; color: ${(p) => p.theme.muted}; padding: 2px 8px 6px; }
`;

const ConvoRow = styled.button<{ $on: boolean }>`
  display: grid;
  grid-template-columns: 14px minmax(0, 200px) minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  width: 100%;
  padding: 6px 8px;
  border: none;
  border-radius: 10px;
  /* [LIQUID-GLASS 2026-11-02] the email on show: a soft green glass highlight (a shine, a light top edge, a green line) */
  background-color: ${(p) => (p.$on ? (dark(p) ? 'rgba(74,205,139,0.14)' : 'rgba(26,159,125,0.10)') : 'transparent')};
  background-image: ${(p) => (!p.$on ? 'none' : dark(p) ? 'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0) 70%)' : 'linear-gradient(180deg, rgba(255,255,255,0.70), rgba(255,255,255,0) 70%)')};
  box-shadow: ${(p) => (!p.$on ? 'none' : `inset 0 1px 0 ${dark(p) ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.85)'}, inset 0 0 0 1px ${dark(p) ? 'rgba(74,205,139,0.28)' : 'rgba(26,159,125,0.22)'}`)};
  color: ${(p) => p.theme.text};
  font: inherit;
  font-size: 12.5px;
  text-align: left;
  cursor: pointer;

  &:hover { ${(p) => (p.$on ? '' : `background-color: ${dark(p) ? 'rgba(255,255,255,0.05)' : 'rgba(22,25,23,0.04)'};`)} }
  .dot { width: 8px; height: 8px; border-radius: 50%; background: ${(p) => (p.$on ? (dark(p) ? '#4acd8b' : p.theme.green600) : p.theme.line)}; }
  .from { font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .subj { color: ${(p) => p.theme.muted}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .when { color: ${(p) => p.theme.muted}; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .team { font-weight: 700; font-size: 10.5px; color: ${(p) => p.theme.muted}; margin-left: 6px; text-transform: uppercase; letter-spacing: 0.4px; }

  /* [PHONE-LAYOUT 2026-10-22] phones: two lines — who + when, then the subject under them with the whole width */
  @media (max-width: 768px) {
    grid-template-columns: 14px minmax(0, 1fr) auto;
    gap: 2px 10px;
    .when { grid-column: 3; grid-row: 1; }
    .subj { grid-column: 2 / -1; grid-row: 2; }
    .team { font-size: 11px; }
  }
`;

/* From / To / Cc / Date — as an email client shows them. */
const MailHead = styled.div`
  flex: 0 0 auto;
  padding: 14px 20px 10px;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 3px 12px;
  font-size: 12.5px;

  .k { color: ${(p) => p.theme.muted}; }
  .v { overflow-wrap: anywhere; }
  .from { font-weight: 700; font-size: 13.5px; }
  .subject { grid-column: 1 / -1; font-size: 15px; font-weight: 700; margin-bottom: 4px; overflow-wrap: anywhere; }

  @media (max-width: 768px) { padding: 12px 14px 8px; } /* [PHONE-LAYOUT 2026-10-22] the phone's 14 px sides */
`;

const Note = styled.div`
  flex: 0 0 auto;
  margin: 0 20px 8px;
  font-size: 12px;
  color: ${(p) => p.theme.muted};

  @media (max-width: 768px) { margin: 0 14px 8px; } /* [PHONE-LAYOUT 2026-10-22] */
`;

/* The email on white paper, in a frame that runs nothing (sandbox: no scripts, its own empty origin). */
const Paper = styled.iframe`
  flex: 1 1 auto;
  min-height: 0;
  width: calc(100% - 32px);
  margin: 0 16px 16px;
  border: 1px solid ${(p) => (dark(p) ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)')};
  border-radius: 12px;
  background: #fff;

  ${layoutMax(900)} { flex: 0 0 auto; height: 70vh; }
  /* [PHONE-LAYOUT 2026-10-22] phones: the paper takes the width less 10 px each side */
  @media (max-width: 768px) { width: calc(100% - 20px); margin: 0 10px 10px; }
`;

/* No HTML kept (a plain-text email, or one no longer in Gmail): the text, with the quoted trail indented like a reply. */
const PlainMail = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  margin: 0 16px 16px;
  padding: 16px 20px;
  border-radius: 12px;
  background: #fff;
  /* [PURE-INK 2026-10-15, user] "not use the light shade of the colours": the white sheet's words are pure black (were Gmail's
     #202124 and its #5f6368 grey); the quoted trail stays told apart by its line and indent. */
  color: #000000;
  font: 14px/1.55 Arial, Helvetica, sans-serif;
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  .quote { margin: 4px 0; padding-left: 10px; border-left: 2px solid #ccc; color: #000000; }
  .wrote { color: #000000; }
  .attach { white-space: normal; clear: both; margin-top: 24px; padding-top: 12px; border-top: 1px solid #e0e0e0; }
  .attach-title { margin-bottom: 10px; font: 600 12px/1.4 Arial, sans-serif; color: #000000; }
  .attach figure { margin: 0 0 16px; }
  .attach img { display: block; max-width: 100%; height: auto; border: 1px solid #e0e0e0; border-radius: 6px; }
  .attach figcaption { margin-top: 5px; font-size: 12px; color: #000000; }
  .attach .card { display: inline-flex; align-items: center; gap: 12px; max-width: 100%; box-sizing: border-box; margin: 0 10px 10px 0;
    padding: 10px 14px; border: 1px solid #dadce0; border-radius: 8px; background: #f8f9fa; vertical-align: top; }
  .attach .badge { flex: 0 0 auto; min-width: 40px; padding: 7px 6px; border-radius: 5px; color: #fff; font: 700 11px/1 Arial, sans-serif; text-align: center; }
  .attach .name { display: block; font-weight: 600; color: #000000; overflow-wrap: anywhere; }
  .attach .what { display: block; font-size: 12px; color: #000000; }

  ${layoutMax(900)} { overflow: visible; }
  /* [PHONE-LAYOUT 2026-10-22] phones: the white sheet less 10 px each side, 14 px inside */
  @media (max-width: 768px) { margin: 0 10px 10px; padding: 14px; }
`;

/* [ACTIONS-TOP 2026-10-14, user] "remove the button from bottom right and add these button on the left of the 1 of 45 thing":
   the row's buttons sit in the top bar, before "N of M", a thin line between them (was a bar at the bottom right). */
const TopActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 12px;
  margin-right: 4px;
  border-right: 1px solid ${(p) => p.theme.line};

  /* [PHONE-LAYOUT 2026-10-22] phones: the whole width, the buttons sharing it equally (no thin line: the row ends there) */
  @media (max-width: 768px) {
    flex: 1 1 100%;
    padding-right: 0;
    margin-right: 0;
    border-right: 0;
    & > button { flex: 1 1 0; min-width: 0; height: 36px; justify-content: center; }
  }
`;

interface PopupProps {
  row: LeadRow | null;          // the row on show (null = closed)
  view: LeadView;
  pos: number;                  // its place in the list, 1-based
  total: number;
  onPrev: (() => void) | null;  // null = first row
  onNext: (() => void) | null;  // null = last row
  onClose: () => void;
  onLead: (r: LeadRow) => void;
  onMark: (r: LeadRow, m: LeadMark) => void;  // [DUPLICATE-TOGGLE] [NAL-MARK] Duplicate / NAL on or off
  onUndo: (r: LeadRow) => void;  // [UNDO-IRRELEVANT] Irrelevant Leads only
  keysOff: boolean;             // the "which day?" box is open over it
}

const kb = (n: number) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
/* [ATTACH-IN-MAIL 2026-10-13, user] "it should show the text that some attachment is there, for the images I will see
   fully, for the file like thing it will have the text only for the file type" — an HTML email gets this from the server
   (attachmentsHtml, inside the email); a text-only email shows the same after its text. Nothing downloads. */
const FILE_KINDS: [RegExp, string, string][] = [ // tested on the MIME type, then on the file name (as on the server)
  [/spreadsheet|excel|\.xls[xm]?$/i, 'Excel spreadsheet', '#188038'],
  [/csv|\.csv$/i, 'CSV file', '#188038'],
  [/pdf|\.pdf$/i, 'PDF document', '#d93025'],
  [/msword|wordprocessing|\.docx?$/i, 'Word document', '#1a73e8'],
  [/presentation|powerpoint|\.pptx?$/i, 'PowerPoint presentation', '#e8710a'],
  [/zip|compressed|rar|7z/i, 'Compressed archive', '#5f6368'],
  [/^text\/|\.txt$/i, 'Text file', '#5f6368'],
  [/^image\//i, 'Picture', '#9334e6'],
  [/^audio\//i, 'Audio file', '#5f6368'],
  [/^video\//i, 'Video file', '#5f6368'],
];
const OTHER_FILE: [RegExp, string, string] = [/./, 'File', '#5f6368'];
const fileKind = (mime: string, name: string) => FILE_KINDS.find(([re]) => re.test(mime)) || FILE_KINDS.find(([re]) => re.test(name)) || OTHER_FILE;
const AttachList: React.FC<{ files: MailFile[] }> = ({ files }) => (files.length ? (
  <div className="attach" aria-label="Attachments">
    <div className="attach-title">{files.length} attachment{files.length > 1 ? 's' : ''}</div>
    {files.map((f) => {
      const name = f.filename || (f.image ? 'picture' : 'file');
      if (f.image && f.kept) {
        return <figure key={f.id}><img src={leadFileUrl(f.id, true)} alt={name} loading="lazy" /><figcaption>{name} · {kb(f.size)}</figcaption></figure>;
      }
      const [, label, color] = fileKind(f.mime, name);
      const ext = (/\.([a-z0-9]{1,5})$/i.exec(name)?.[1] || label.split(' ')[0]).toUpperCase();
      return (
        <div key={f.id} className="card">
          <span className="badge" style={{ background: color }}>{ext}</span>
          <span><span className="name">{name}</span><span className="what">{label} · {kb(f.size)}{f.kept ? '' : ' · bigger than the dashboard keeps'}</span></span>
        </div>
      );
    })}
  </div>
) : null);

/** No HTML kept: the text with "> …" lines grouped as an indented quote and "On … wrote:" lines in grey. */
const PlainText: React.FC<{ text: string; files?: MailFile[] }> = ({ text, files = [] }) => {
  const blocks: { quote: boolean; lines: string[] }[] = [];
  for (const line of String(text || '').replace(/\r/g, '').split('\n')) {
    const quote = /^\s*>/.test(line);
    const last = blocks[blocks.length - 1];
    const clean = quote ? line.replace(/^\s*>\s?/, '') : line;
    if (last && last.quote === quote) last.lines.push(clean); else blocks.push({ quote, lines: [clean] });
  }
  return (
    <PlainMail>
      {blocks.map((b, i) => (b.quote
        ? <div key={i} className="quote">{b.lines.join('\n')}</div>
        : <React.Fragment key={i}>{b.lines.map((l, j) => (/^On .+wrote:\s*$/.test(l.trim())
          ? <div key={j} className="wrote">{l}</div>
          : <React.Fragment key={j}>{l}{j < b.lines.length - 1 ? '\n' : ''}</React.Fragment>))}{'\n'}</React.Fragment>))}
      <AttachList files={files} />
    </PlainMail>
  );
};

/** [MAIL-CONTENT 2026-10-13, user] The email as it was sent — kept in our database, so it reads the same without the
 *  mailbox: the conversation we hold (the lead and its replies), From / To / Cc / Date, and the email's own HTML (layout,
 *  reply trail, signature, pictures) on white paper in a frame that runs nothing — [ATTACH-IN-MAIL] its attachments
 *  listed inside it, after the text (pictures in full, other files as text; nothing downloads). */
const MailView: React.FC<{ thread: MailThread | null; failed: boolean; currentId: number }> = ({ thread, failed, currentId }) => {
  const [shownId, setShownId] = useState(currentId);
  useEffect(() => { setShownId(currentId); }, [currentId]);
  const noCopy = !useAuth().user?.is_admin; // [NO-COPY]
  if (failed) return <MailPane><Note style={{ marginTop: 18 }}>Could not load this email. Use Next / Previous and come back, or close and click the row again.</Note></MailPane>;
  if (!thread || thread.id !== currentId) {
    return (
      <MailPane>
        <MailHead>
          <div className="subject"><SkeletonBox $w="55%" $h="15px" /></div>
          <span className="k">From</span><span className="v"><SkeletonBox $w="40%" $h="12px" /></span>
          <span className="k">Date</span><span className="v"><SkeletonBox $w="25%" $h="12px" /></span>
        </MailHead>
        <PlainMail>{['96%', '88%', '92%', '70%', '84%', '58%'].map((w, i) => <SkeletonBox key={i} $w={w} $h="13px" style={{ marginBottom: 10 }} />)}</PlainMail>
      </MailPane>
    );
  }
  const m = thread.messages.find((x) => x.id === shownId) || thread.messages.find((x) => x.id === currentId) || thread.messages[0];
  if (!m) return <MailPane><Note style={{ marginTop: 18 }}>This email is no longer in the mailbox dump.</Note></MailPane>;
  const from = [m.fromName, m.fromEmail ? `<${m.fromEmail}>` : ''].filter(Boolean).join(' ') || '—';
  return (
    <MailPane>
      {thread.messages.length > 1 && (
        <Convo>
          <div className="title">This conversation · {thread.messages.length} emails</div>
          {thread.messages.map((x) => (
            <ConvoRow key={x.id} type="button" $on={x.id === m.id} onClick={() => setShownId(x.id)} title={x.id === currentId ? 'The email of this row' : 'Another email of this conversation'}>
              <span className="dot" />
              <span className="from">{x.fromName || x.fromEmail || '—'}{x.id === currentId ? ' · this row' : ''}{x.sent && <span className="team">Team reply</span>}</span>
              <span className="subj">{x.subject || '(no subject)'}</span>
              <span className="when">{emailTime(x.date)}</span>
            </ConvoRow>
          ))}
        </Convo>
      )}
      <MailHead>
        <div className="subject">{m.subject || '(no subject)'}</div>
        <span className="k">From</span><span className="v from">{from}{m.sent ? ' · our team’s reply' : ''}</span>
        {m.to && (<><span className="k">To</span><span className="v">{m.to}</span></>)}
        {m.cc && (<><span className="k">Cc</span><span className="v">{m.cc}</span></>)}
        <span className="k">Date</span><span className="v">{emailTime(m.date)}</span>
      </MailHead>
      {m.contentStatus === null && <Note>Still collecting this email&apos;s pictures and files from the mailbox — showing its text for now.</Note>}
      {m.contentStatus === 'gone' && <Note>This email is no longer in the mailbox — its text, kept here, is shown.</Note>}
      {m.contentStatus === 'error' && <Note>This email&apos;s pictures and files could not be collected — its text is shown.</Note>}
      {m.html
        // A new frame whenever the email's HTML changes (its pictures / files arrived later): a reused frame can keep
        // painting the old page for a moment after its srcdoc changes.
        ? <Paper key={`${m.id}:${m.html.length}`} aria-label="The email" sandbox="allow-popups allow-popups-to-escape-sandbox" srcDoc={noCopy ? noCopyDoc(m.html) : m.html} />
        : <PlainText text={m.text || 'This email has no text.'} files={m.files} />}
    </MailPane>
  );
};

/** [ROW-POPUP 2026-10-13, user] "when someone click on that row then it will open a popup of the screen size and it will
 *  have the proper details for the mail and on the top right it will have the next and previous button … and it will
 *  have the lead and no leads button also so that if someone is using the big screen then he can do operations as well
 *  from there". ← / → also step, Esc closes. Raw Data shows the three buttons; Leads / Irrelevant Leads show who marked it.
 *  [MAIL-CONTENT 2026-10-13] The right side is the whole email (MailView). */
/** [PAGE-VIEW 2026-10-14] The email page itself — the popup's inside. `inline`: shown in place of the table (the Single page
 *  view button): no Close, and ← → only while it is the view on show and nobody is typing or has a box / list open. */
type EmailPageProps = Omit<PopupProps, 'row' | 'onClose'> & { row: LeadRow; onClose: (() => void) | null; inline?: boolean };
const EmailPage: React.FC<EmailPageProps> = ({ row: r, view, pos, total, onPrev, onNext, onClose, onLead, onMark, onUndo, keysOff, inline = false }) => {
  const [thread, setThread] = useState<MailThread | null>(null);
  const [failed, setFailed] = useState(false);
  const rowId = r.id;
  useEffect(() => {
    let live = true;
    setFailed(false);
    fetchLeadEmailThread(rowId)
      .then((t) => { if (live) setThread(t); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [rowId]);
  const keys = useRef({ onPrev, onNext, onClose, keysOff, inline });
  keys.current = { onPrev, onNext, onClose, keysOff, inline };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = keys.current;
      if (k.keysOff) return;
      if (k.inline) {
        const t = e.target as HTMLElement | null;
        if (t && typeof t.closest === 'function' && t.closest('input, textarea, select, [contenteditable="true"]')) return;
        // a box or a list OPEN on screen — some stay in the page between uses, hidden or parked off screen (the App Alerts
        // drawer sits beyond the right edge)
        const open = Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"], [role="listbox"]')).some((el) => {
          const q = el.getBoundingClientRect();
          const cs = getComputedStyle(el);
          return q.width > 0 && q.height > 0 && q.right > 0 && q.left < window.innerWidth && q.bottom > 0 && q.top < window.innerHeight
            && cs.visibility !== 'hidden' && Number(cs.opacity) > 0.05;
        });
        if (open) return;
      }
      if (e.key === 'Escape' && k.onClose) k.onClose();
      else if (e.key === 'ArrowLeft' && k.onPrev) k.onPrev();
      else if (e.key === 'ArrowRight' && k.onNext) k.onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const mine = thread && thread.id === r.id ? thread.messages.find((x) => x.id === r.id) || null : null;
  return (
    <>
        <PopTop>
          <div className="who">
            {/* [COPY-SOME 2026-10-19, user] "i dont want the copy button to be shown of these data points": no copy button after the
                name or the subject any more (was [COPY-FIELD]) */}
            <h3><span className="t">{whoOf(r)}</span></h3>
            <div className="subject"><span className="t">{mine ? (mine.subject || '(no subject)') : '…'}</span></div>
          </div>
          <Nav>
            {view === 'raw' && (
              <TopActions>
                <LeadBtn type="button" onClick={() => onLead(r)}>Leads</LeadBtn>
                {/* [NO-NOT-LEAD 2026-10-27] no Not Leads button: Leads or NAL */}
                <MarkButtons row={r} onToggle={(m) => onMark(r, m)} />
              </TopActions>
            )}
            {view === 'irrelevant' && (
              <TopActions>
                {/* [UNDO-IRRELEVANT 2026-10-13, user] back to Raw Data, to be decided again */}
                <NotLeadBtn type="button" onClick={() => onUndo(r)}><i className="fas fa-rotate-left" /> Undo — back to Raw Data</NotLeadBtn>
              </TopActions>
            )}
            <span className="pos">{pos} of {total}</span>
            <NavBtn type="button" onClick={onPrev || undefined} disabled={!onPrev} aria-label="Previous email">
              <i className="fas fa-chevron-left" /> Previous
            </NavBtn>
            <NavBtn type="button" onClick={onNext || undefined} disabled={!onNext} aria-label="Next email">
              Next <i className="fas fa-chevron-right" />
            </NavBtn>
            {onClose && <CloseX type="button" onClick={onClose} aria-label="Close"><i className="fas fa-times" /></CloseX>}
          </Nav>
        </PopTop>
        <PopMain>
          {/* [COPY-FIELD 2026-10-19, user] a value with a copy button after it (Field `copy`); Attachments is a count — none.
              [COPY-SOME 2026-10-19, user] "i dont want the copy button to be shown of these data points, so remove it": Date, First
              Name, Last, Lead Generated By (Name), Team Manager, Geography, US / ROW and Tag have none now; Email Address, Website,
              Phone Number, Lead Generated By (and Marked By) keep theirs */}
          <Details>
            <Field label="Date">{emailTime(r.date)}</Field>
            <Field label="First Name">{r.firstName || '—'}</Field>
            <Field label="Last">{r.lastName || '—'}</Field>
            {/* [DETAIL-COLOURS 2026-10-14, user] "when someone is seeing the full view then on the left side, this data should
                also have the colour as per the formatting" — the Raw table's colours (Raw Mailbox Data only, as the table) */}
            <Field label="Email Address" copy={r.email}>{detailText(view === 'raw' ? emailTone(r.email) : null, r.email)}</Field>
            <Field label="Website" copy={r.website}>{detailText(view === 'raw' ? websiteTone(r.website) : null, r.website)}</Field>
            <Field label="Phone Number" copy={r.phone}>{detailText(view === 'raw' ? phoneTone(r.phone) : null, r.phone)}</Field>
            <Field label="Lead Generated By" copy={r.leadBy}>{r.leadBy || '—'}</Field>
            <Field label="Lead Generated By (Name)">{r.leadByName || '—'}</Field>
            <Field label="Team Manager">{r.teamManager || '—'}</Field>
            <Field label="Geography">{r.geography || '—'}</Field>
            <Field label="US / ROW"><RegionCell region={r.region} /></Field>
            {view === 'raw' && <Field label="Tag"><TagCell row={r} full /></Field>}
            {/* [NAL-NOTE 2026-10-22] why it is NAL. [NAL-NOTE-COL 2026-10-22, user] "on the single view also on this part … the note should
                be there": the line is always there after Tag — the note, else "—" (not NAL, or marked before the note) */}
            {view === 'raw' && <Field label="NAL Note">{r.nalNote || '—'}</Field>}
            {view !== 'raw' && (
              <Field label="Marked By" copy={r.markedBy ? `${r.markedBy}${r.markedAt ? ` · ${markedTime(r.markedAt)}` : ''}` : null}>
                {r.markedBy || '—'}{r.markedAt ? ` · ${markedTime(r.markedAt)}` : ''}
              </Field>
            )}
            <dt>Attachments</dt><dd><span className="val">{mine ? (mine.files.length ? `${mine.files.length}` : 'none') : '…'}</span></dd>
          </Details>
          <MailView thread={thread} failed={failed} currentId={r.id} />
        </PopMain>
    </>
  );
};

const EmailPopup: React.FC<PopupProps> = ({ row, onClose, ...rest }) => {
  const pres = usePresence(row, fluid.dialogOutMs);
  const shown = !!row && pres.entered;
  const r = row ?? pres.value; // keeps its words while it closes
  if (!pres.mounted || !r) return null;
  return createPortal(
    <Overlay $open={shown} data-reload-block={row ? '' : undefined} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Box $open={shown} role="dialog" aria-modal="true" aria-label="Lead email">
        {/* while it closes (row null) it keeps its words, and its keys rest */}
        <EmailPage {...rest} row={r} onClose={onClose} keysOff={rest.keysOff || !row} />
      </Box>
    </Overlay>,
    document.body,
  );
};

/* [PAGE-VIEW 2026-10-14, user] The email page in place of the table: the popup's inside on the page's card, ending above
   the floating dock (the dock's reach, as the table's own bottom pad). */
const InlinePage = styled.div<{ $noDock?: boolean }>`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: row; /* [PAGE-LIST 2026-10-19] the emails' list (20 %) | the email's page (PageMain) */
  /* [LEADS-NO-DOCK 2026-10-14] the Leads type has no dock, so the page runs down to the card's bottom */
  margin-bottom: ${(p) => (p.$noDock ? '16px' : '88px')}; /* the dock's reach — [APP-FIT 2026-10-26] scaled with the page */
  /* [LIQUID-GLASS 2026-11-02, user] a glass card frame (styles/liquidGlass.ts lgTile, smooth corners) */
  ${lgSquircle(16)}
  ${lgTile}
  overflow: hidden;

  /* [PHONE-LAYOUT 2026-10-22] phones: one column — the emails' list as a sideways strip on top, then the email's page (its
     buttons, its details, the email) at its full height. [PHONE-LAYOUT 2026-10-23, user] It no longer scrolls by itself:
     the page does (clip keeps the rounded corners without making a scroll box). */
  @media (max-width: 768px) {
    flex: none;
    flex-direction: column;
    overflow: clip;
  }
`;
const PageEmpty = styled.div`
  flex: 1;
  display: grid;
  place-items: center;
  padding: 24px;
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => p.theme.muted};
`;

/* [PAGE-LIST 2026-10-19, user] "now i want to make the layout change to the raw mailbox data … on the left side i want to give the 20
   percent of this page and it will show the subject line of the mail and when someone click on it then he will see the single page
   view for that data … so that someone can move the data also in the single page view with the left sidebar and this feature will be
   with the harshit and shivam also": the Single page view's own list — the emails the page goes through (the same list, order and
   filter as its "N of M"), each by its subject line; the open one lit (a bar in the page's ink, no green) and kept in view; a click
   opens it. Marked emails keep the table's colours (NAL red, Duplicate yellow). Admins see it with the page view on; the Leads type
   (Shivam / Harshit) always — their Raw Mailbox Data is always the page view. */
const ListPane = styled.nav`
  flex: 0 0 20%;
  max-width: 20%;
  min-width: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  border-right: 1px solid ${(p) => p.theme.line};

  /* [PHONE-LAYOUT 2026-10-22] phones: a strip across the top that swipes sideways (one card per email) */
  @media (max-width: 768px) {
    flex: none;
    max-width: none;
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    overscroll-behavior-y: auto; /* an up / down swipe on the strip scrolls the page view */
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
    border-right: 0;
    border-bottom: 1px solid ${(p) => p.theme.line};
  }
`;
const ListItem = styled.button<{ $mark: 'nal' | 'duplicate' | null }>`
  display: block;
  width: 100%;
  margin: 0;
  padding: 10px 12px 10px 13px;
  border: 0;
  border-bottom: 1px solid ${(p) => p.theme.line};
  border-left: 3px solid transparent;
  background-color: ${(p) => (p.$mark === 'nal' ? (dark(p) ? 'rgba(220, 38, 38, 0.18)' : 'rgba(220, 38, 38, 0.11)')
    : p.$mark === 'duplicate' ? (dark(p) ? 'rgba(250,204,21,0.11)' : 'rgba(250,204,21,0.18)') : 'transparent')}; /* the table's rowBgFn */
  color: ${(p) => p.theme.text};
  font: inherit;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.35;
  text-align: left;
  cursor: pointer;

  .s { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; overflow-wrap: anywhere; }
  &:hover { background-image: linear-gradient(${(p) => (dark(p) ? 'rgba(255,255,255,0.05), rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035), rgba(0,0,0,0.035)')}); }
  /* [LIQUID-GLASS 2026-11-02] the open email: a soft glass bar (lighter at the top, a light top edge); its ink bar and the NAL /
     Duplicate tint stay */
  &[aria-current='true'] {
    border-left-color: ${(p) => p.theme.text};
    font-weight: 700;
    background-image: linear-gradient(180deg, ${(p) => (dark(p) ? 'rgba(255,255,255,0.13), rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.035), rgba(0,0,0,0.075)')});
    box-shadow: inset 0 1px 0 ${(p) => (dark(p) ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.9)')}, inset 0 -1px 0 ${(p) => (dark(p) ? 'rgba(0,0,0,0.25)' : 'rgba(22,25,23,0.06)')};
  }
  &:focus-visible { outline: 2px solid ${(p) => p.theme.text}; outline-offset: -2px; }

  /* [PHONE-LAYOUT 2026-10-22] phones: a card about two thirds of the strip wide, the next one peeking in */
  @media (max-width: 768px) {
    flex: 0 0 auto;
    width: 64%;
    max-width: 260px;
    border-bottom: 0;
    border-right: 1px solid ${(p) => p.theme.line};
  }
`;
/* [PAGE-LIST] the email's page beside the list: its top bar, then its details + mail (as the page was) */
const PageMain = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) { flex: none; } /* [PHONE-LAYOUT 2026-10-22] its full height, under the strip */
`;
const PageList: React.FC<{ rows: LeadRow[]; currentId: number | null; onPick: (idx: number) => void }> = ({ rows, currentId, onPick }) => {
  const paneRef = useRef<HTMLElement>(null);
  useEffect(() => { // the open email stays in view (Previous / Next, ← →, after a mark)
    const el = paneRef.current?.querySelector<HTMLElement>('[aria-current="true"]');
    if (el) el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [currentId]);
  return (
    <ListPane ref={paneRef} aria-label="Emails" data-page-list="">
      {rows.map((r, i) => (
        <ListItem key={r.id} type="button" $mark={r.nal ? 'nal' : r.duplicate ? 'duplicate' : null} aria-current={r.id === currentId}
          onClick={() => onPick(i)} data-page-item={r.id}>
          <span className="s">{r.subject || '(no subject)'}</span>
        </ListItem>
      ))}
    </ListPane>
  );
};

/* [LEAD-GEN-REPORT 2026-10-14, user] "on the left of single page view i want to add a button for the 'Lead Generator Report' …
   it will show a new page and which will have the lead generated by unique names and the names of them and then the unique
   count as per websites and the other column will have the count for the nal which will pick from the raw mailbox data for
   the email and the other column will have the duplicate count which will pick from the lead status and the other column will
   have the total" → later the same day: "in the pill where we have tabs raw mailbox, leads identified and irrelevant in that
   only add the last one lead generator report and when someone click on it then it will show the report and this will be read
   by all who has this page access" — a TAB of its own now (the button is gone), worked out on the server
   (services/leadAssignment.ts leadGeneratorReport), so the Leads team reads it without Leads Data. Per lead generator of the day:
     Unique     different websites among ALL their emails of the day (Leads Data rows of any Lead Status + their NAL emails)
     NAL        their emails of the day marked NAL in Raw Mailbox Data
     Duplicate  their Leads Data rows with Lead Status "Duplicate"
     Total      Unique − NAL − Duplicate ([GEN-TOTAL 2026-10-14, user] "total will be unique-nal-duplicate")
   "make the table divide for the ROW persons and for the US persons": two tables, by the person's ROW Status in the lead
   generators table (ROW → ROW, anyone else → US). [GEN-SIDE-BY-LEAD 2026-10-16, user] now by each lead's OWN ROW tag (the
   tiles' rule) so the two tables add up to the ROW / USA tiles — someone who sends both kinds is in both tables (server).
   "for the kpi … left side it will show total for the today count … then the row and us … from this 169 the us and row is
   showing and on the half right it will show the lead status thing … the breakup for the leads data 169 rows": two halves —
   left the day's Leads Data total and its ROW / US split, right the same rows by Lead Status (+ any not processed yet). */
const ReportWrap = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  /* the report's rows open nothing (TableScroll's rows are emails — their pointer is for the popup) */
  tbody tr[data-row-index] { cursor: default; }

  /* [PHONE-LAYOUT 2026-10-23, user] phones: its full height, the page scrolls */
  @media (max-width: 768px) {
    flex: none;
    min-height: auto;
  }
`;
/* [GEN-KPIS 2026-10-15, user] one strip — the Lead Status half is gone ("remove the lead status kpi from this page, no need for
   this"). [GEN-KPIS-2 2026-10-15, user] its tiles: NAL (the Raw Mailbox Data rows marked NAL) · Leads Count before Assignment
   (the Leads Data rows) · Duplicate Leads (Leads Data rows with Lead Status Duplicate) · Leads for Assignment (the Ready to be
   Assigned rows) · ROW · USA ("the bifurcation of the leads for assignment … from the ROW status column").
   [GEN-NO-CAP 2026-10-15, user] the line above the tiles is gone ("remove this line from lead generator report"). */
const KpiStrip = styled.div`
  flex-shrink: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-bottom: 16px;

  .tiles { display: flex; gap: 10px; }

  /* [PHONE-LAYOUT 2026-10-22] phones: the six tiles are a sideways swipe row (two in full and the next one peeking),
     each snapping to the left edge */
  @media (max-width: 768px) {
    margin-bottom: 8px;
    .tiles {
      gap: 8px;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      overscroll-behavior-x: contain;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      &::-webkit-scrollbar { display: none; }
    }
  }
`;
/* the My Sales KPI tiles (MySalesPage Kpi / KpiLabel / KpiValue); $main = the day's total. [GEN-KPIS-2] no min-width: 0 — a
   tile is never narrower than its label's longest word; otherwise all are the same width. [GEN-KPI-LABELS 2026-10-15] the
   longer names ("Count of ROW leads assigned today") wrap onto a second line instead of being cut, and the number sits at the
   foot of every tile so the six numbers stay on one line. */
const Kpi = styled.div<{ $main?: boolean }>`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: default;
  padding: 9px 13px;
  /* [LIQUID-GLASS 2026-11-02, user] glass cards (lgTile, smooth corners); the main tile keeps its green, glossy */
  ${lgSquircle(10)}
  ${lgTile}
  ${(p) => (p.$main ? `
    background-color: ${dark(p) ? 'rgba(74,205,139,0.10)' : 'rgba(26,159,125,0.08)'};
    border-color: ${dark(p) ? 'rgba(74,205,139,0.50) rgba(74,205,139,0.30) rgba(74,205,139,0.36)' : 'rgba(26,159,125,0.30) rgba(26,159,125,0.28) rgba(26,159,125,0.34)'};
  ` : '')}

  .l { font-size: 9.5px; font-weight: 700; line-height: 1.3; text-transform: uppercase; letter-spacing: 0.4px; color: ${(p) => p.theme.muted}; }
  .v { margin-top: 3px; font-size: 17px; font-weight: 800; line-height: 1.1; font-variant-numeric: tabular-nums; letter-spacing: 0.2px; color: ${(p) => p.theme.text}; }
  .of { margin-left: 5px; font-size: 11px; font-weight: 600; color: ${(p) => p.theme.muted}; }

  /* [PHONE-LAYOUT 2026-10-22] phones: a fixed tile width in the swipe row (two tiles and a sliver of the next), wide enough
     that every name takes at most two lines; label above, number at the foot, labels 11 px */
  @media (max-width: 768px) {
    flex: 0 0 calc((100% - 16px) / 2.15);
    min-width: 0;
    scroll-snap-align: start;
    padding: 8px 10px;
    .l { font-size: 11px; letter-spacing: 0.2px; overflow-wrap: anywhere; }
    .v { margin-top: 6px; font-size: 18px; }
  }
`;
/* [NAL-RECORDS-PAGE 2026-10-27, user] "in the below toggle the us and row will be removed and only the data for nal record will show, so
   like all the kpi will remain as it is and below that it will only show the nal records data": the US | ROW | NAL records switch
   ([GEN-SIDE-SWITCH 2026-10-19] / [GEN-NAL-RECORDS 2026-10-22]) and the US / ROW lead generator tables are gone — the KPIs, then the
   NAL records table. (The server still works the generators out: the Team Sameer Report's columns read them.) */
const GenGroup = styled.section`
  margin-bottom: 18px;

  h4 { margin: 0 0 8px; font-size: 13px; font-weight: 800; color: ${(p) => p.theme.text}; }
  h4 span { font-weight: 600; color: ${(p) => p.theme.muted}; }
`;
/* [GEN-RED 2026-10-14, user] "make the values in the duplicate and nal column in red colour, it should feel the negative value
   so colouring the value to red will look good". [PURE-INK 2026-10-15, user] "not use the light shade of the colours": a 0 was
   a softer red (half strength) — now as clear as any number. */
const NegNum = styled.span`
  color: ${(p) => p.theme.danger}; /* [PURE-INK] the theme's red: dark #f87171 as before, light #c81e1e (5 : 1 on grey; #dc2626 was 4.4) */
  font-weight: 700;
`;
/* [GEN-TEAMS 2026-10-14] teams A–Z, then Team Freelancer, then people not in the lead generators list ("No team") — the NAL
   records table's order. */
const TEAM_LAST = ['Team Freelancer', 'No team'];
/* [GEN-NAL-RECORDS 2026-10-22, user] "it will have the column where team name and members will be there and on the other columns it will
   have the wbeiste name only for the nal records for their name in the raw mailbox data" — layout the user picked: "All in one cell". One
   row per person (their name; their email when they are not in the lead generators table) with the websites of their NAL emails of the
   day, comma separated, oldest first; under their team like the US / ROW tables (teams A–Z, Team Freelancer + No team last), a Total line
   = the NAL Leads tile. Every NAL email of the day, US and ROW together (server leadGeneratorReport nalRecords). */
type NalRow = NalPerson & { id: string; kind?: 'team' | 'total'; who: string; count: number; people?: number };
/* [GEN-NAL-COPY 2026-10-22, user] "after every webiste there add a button for copy it and no one will have the access on this to drag and
   select the text other than admins": the [COPY-FIELD] copy button right after each website (everyone — it hands over that ONE website);
   selecting / dragging the text stays admins-only by the tab's [NO-COPY] rule (body[data-no-copy] for everyone else, the report too). The
   minus margins keep the rows' height. */
/* [GEN-NAL-WHY 2026-10-22, user] "after every website in the bracket it should show the nal note … so that they see the webiste and the
   reason along with it" + [GEN-NAL-WHY-BELOW] "make the bracket thing below the webiste name": each website = the website with its copy
   button, its (note) on the line under it.
   [GEN-NAL-BAR 2026-10-22, user] "after one webiste data add this | and then the other website so like only one column for the webiste and
   all will be in that" (the one-column-per-website layout tried before is gone): ONE "NAL websites" column, a person's websites side by
   side (oldest first), a "|" between one website and the next; a long list wraps inside the cell (at most 1400 px wide) and a long note
   inside its website (at most 340 px). */
const NalSites = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: flex-start;
  row-gap: 6px;
  max-width: 1400px;
  white-space: normal;
  vertical-align: middle;
  .e { display: inline-block; max-width: 340px; }
  .w { white-space: nowrap; }
  .why { display: block; margin-top: 2px; font-size: 12px; line-height: 1.35; }
  .bar { padding: 0 12px; }
  [data-copy] { margin: -3px 0 -3px 5px; vertical-align: middle; }

  /* [PHONE-LAYOUT 2026-10-22] phones: a person's websites one under the other in a column of their own (each with its copy
     button and its note under it) — side by side they ran up to 1,400 px wide; the "|" between them is not needed in a list */
  @media (max-width: 768px) {
    flex-direction: column;
    row-gap: 8px;
    max-width: 280px;
    .e { max-width: 280px; }
    .bar { display: none; }
    [data-copy] { margin: -5px 0 -5px 6px; }
  }
`;
const NAL_COLUMNS: Column[] = withSort([ // [LEAD-SORT] NAL websites sorts by how many
  { key: 'who', label: 'Team / Lead Generated By Name', render: (v: string, row: NalRow) => (row.kind
    ? <><b>{v}</b> <Muted>· {row.people} lead generator{row.people === 1 ? '' : 's'}</Muted></>
    : v) },
  { key: 'sites', label: 'NAL websites', render: (v: string[], row: NalRow) => (row.kind === 'total'
    ? <b>{row.count} NAL website{row.count === 1 ? '' : 's'}</b>
    : row.kind ? null : (
      <NalSites data-nal-sites="">
        {v.map((site, i) => {
          const why = row.notes?.[i]; // [GEN-NAL-WHY] its NAL note, under it
          return (
            <React.Fragment key={`${i}:${site}`}>
              {i ? <span className="bar" aria-hidden="true">|</span> : null}
              <span className="e" data-nal-site={i}>
                <span className="w">{site}<CopyValue label="Website" text={site} /></span>
                {why ? <span className="why" data-nal-why="">({why})</span> : null}
              </span>
            </React.Fragment>
          );
        })}
      </NalSites>
    )) },
]);
const nalValue = (r: NalRow, key: string): unknown => (key === 'sites' ? r.count : cellOf(r, key));
function nalTeamRows(list: NalPerson[], sort: SortConfig = NO_SORT): NalRow[] {
  const byTeam = new Map<string, NalRow[]>();
  for (const p of list) {
    const t = p.team || 'No team';
    if (!byTeam.has(t)) byTeam.set(t, []);
    byTeam.get(t)!.push({ ...p, id: p.key, who: p.name || p.leadBy || '—', count: p.sites.length });
  }
  const rank = (t: string) => TEAM_LAST.indexOf(t) + 1; // 0 = a named team
  const dir = sort.sortOrder === 'desc' ? -1 : 1;
  const teams = [...byTeam.entries()].map(([t, members]) => {
    const line: NalRow = { id: `team:${t}`, kind: 'team', key: `team:${t}`, leadBy: t, name: null, team: t, sites: [], who: t,
      count: members.reduce((s, m) => s + m.count, 0), people: members.length };
    return { t, line, members: sortedBy(members, sort, nalValue) }; // the server's order: most NAL websites first
  });
  teams.sort((a, b) => rank(a.t) - rank(b.t) || (sort.sortBy ? compareKeys(sortKey(nalValue(a.line, sort.sortBy)), sortKey(nalValue(b.line, sort.sortBy))) * dir : 0) || a.t.localeCompare(b.t));
  const out: NalRow[] = [];
  for (const { line, members } of teams) out.push(line, ...members);
  if (list.length) {
    out.push({ id: 'total', kind: 'total', key: 'total', leadBy: 'Total', name: null, team: null, sites: [], who: 'Total',
      count: list.reduce((s, p) => s + p.sites.length, 0), people: list.length });
  }
  return out;
}
const GeneratorReport: React.FC<{ from: string; to: string; active: boolean; storageKey: string }> = ({ from, to, active, storageKey }) => {
  // [GEN-RANGE 2026-10-27] one report per Start date + End date (both days in)
  const rangeKey = `${from}|${to}`;
  const [rep, setRep] = useState<{ key: string; data: LeadReport | null; error: string | null }>({ key: '', data: null, error: null });
  const keyNow = useRef(rangeKey);
  keyNow.current = rangeKey;
  // quiet = a background refresh: a failure keeps what is on show
  const load = useCallback((quiet: boolean) => {
    const k = `${from}|${to}`;
    invalidateGetCacheByPrefix(['/lead-assignment/report|']);
    fetchLeadReport(from, to).then((data) => { if (keyNow.current === k) setRep({ key: k, data, error: null }); })
      .catch((e) => {
        if (keyNow.current !== k) return;
        setRep((s) => (quiet && s.key === k && s.data ? s : { key: k, data: null, error: errorText(e, 'Could not load the report.') }));
      });
  }, [from, to]);
  useEffect(() => { if (active) load(false); }, [active, load]);
  // a mark / an Assignment Process run anywhere bumps 'leadAssignment'; new emails: every minute while on show
  useRealtime(['leadAssignment'], () => { if (active) load(true); });
  useEffect(() => {
    if (!active) return undefined;
    const t = window.setInterval(() => { if (!document.hidden) load(true); }, REFRESH_MS);
    return () => window.clearInterval(t);
  }, [active, load]);
  const data = rep.key === rangeKey ? rep.data : null;
  const failed = rep.key === rangeKey && !!rep.error;
  const loading = !data && !failed;
  // [GEN-RANGE] the words for the dates: "on 21 Oct 2026" / "from 1 Oct 2026 to 21 Oct 2026"; "today" stays in three tile names
  // only while the report shows one day
  const oneDay = from === to;
  const when = oneDay ? `on ${dayLabel(from)}` : `from ${dayLabel(from)} to ${dayLabel(to)}`;
  const assigned = oneDay ? ' today' : '';
  const assignedHint = oneDay ? ' today' : ' on these days';
  const n = (v: number | undefined) => (loading ? <SkeletonBox $w="34px" $h="17px" /> : failed ? '—' : v ?? 0);
  const st = data?.status;
  const isDarkReport = (useTheme() as DefaultTheme).colorScheme === 'dark';
  // [GEN-TEAMS] a team line lightly tinted, the Total line in the app's green
  const genBg = useCallback((row: { kind?: 'team' | 'total' }) => (row.kind === 'total' ? (isDarkReport ? 'rgba(74,205,139,0.14)' : 'rgba(26,159,125,0.10)')
    : row.kind === 'team' ? (isDarkReport ? 'rgba(255,255,255,0.055)' : 'rgba(22,25,23,0.05)') : undefined), [isDarkReport]);
  const [nalSort, setNalSort] = useState<SortConfig>(NO_SORT); // [GEN-NAL-RECORDS] its own columns, its own sort
  const onNalSort = useCallback((key: string) => setNalSort((s) => nextSort(s, key)), []);
  // [GEN-NAL-RECORDS 2026-10-22] the NAL records table — the count = the NAL Leads tile
  const nalList = data?.nalRecords || [];
  const nalSites = nalList.reduce((s, p) => s + p.sites.length, 0);
  const nalGroup = () => (
    <GenGroup data-gen-group="nal">
      <h4>NAL records <span>· {loading ? '…' : `${nalSites} website${nalSites === 1 ? '' : 's'}`}</span></h4>
      <DataTable
        columns={NAL_COLUMNS}
        data={nalTeamRows(nalList, nalSort)}
        rowBgFn={genBg}
        sort={nalSort}
        onSort={onNalSort}
        loading={loading}
        disableMobileCards
        parentScroll
        resizeStorageKey={`${storageKey}-nal`}
        loadFailed={failed ? { what: 'the report', onRetry: () => load(false) } : undefined}
        empty={{ title: `No NAL records ${when}` }}
      />
    </GenGroup>
  );
  return (
    <ReportWrap data-gen-report="">
      {/* [GEN-KPIS-2 2026-10-15, user] NAL (Raw) · Leads Count before Assignment (Leads Data) · Duplicate Leads · Leads for
          Assignment (Ready to be Assigned) · ROW · USA (its ROW Status) */}
      {/* [GEN-KPI-LABELS 2026-10-15, user] the names and hover hints the user gave (the themed tooltip shows each `title`) */}
      {/* [GEN-RANGE 2026-10-27] Start date to End date: the same tiles, every day added up ("today" out of three names, see `assigned`) */}
      <KpiStrip aria-label={`NAL Leads, Leads Count before Assignment, Duplicate leads identified, ${oneDay ? "Today's assigned leads" : 'Assigned leads'}, ROW and US leads assigned${assigned}`}>
        <div className="tiles">
          <Kpi data-kpi="nal" title="NAL marked before the assignment"><div className="l">NAL Leads</div>
            <div className="v">{loading || failed ? n(data?.nal) : <NegNum>{data?.nal ?? 0}</NegNum>}</div></Kpi>
          <Kpi $main data-kpi="leads" title="Leads identified from the Raw data"><div className="l">Leads Count before Assignment</div><div className="v">{n(data?.total)}</div></Kpi>
          <Kpi data-kpi="duplicate" title="At the time of assignment these were duplicate as we already have a sales rep working on it"><div className="l">Duplicate leads identified</div>
            <div className="v">{loading || failed ? n(st?.duplicate) : <NegNum>{st?.duplicate ?? 0}</NegNum>}</div></Kpi>
          <Kpi data-kpi="ready" title={`Count of leads assigned${assignedHint}`}><div className="l">{oneDay ? "Today's assigned leads" : 'Assigned leads'}</div><div className="v">{n(data?.ready?.total)}</div></Kpi>
          <Kpi data-kpi="row" title={`ROW leads assigned${assignedHint}`}><div className="l">{`Count of ROW leads assigned${assigned}`}</div><div className="v">{n(data?.ready?.row)}</div></Kpi>
          <Kpi data-kpi="usa" title={`US leads assigned${assignedHint}`}><div className="l">{`Count of US leads assigned${assigned}`}</div><div className="v">{n(data?.ready?.us)}</div></Kpi>
        </div>
      </KpiStrip>
      {/* [NAL-RECORDS-PAGE 2026-10-27, user] no US | ROW switch any more: only the NAL records table under the KPIs */}
      <TableScroll data-gen-nal="">{nalGroup()}</TableScroll>
    </ReportWrap>
  );
};

/* [PAGE-VIEW 2026-10-14] The Single page view button — the date box's size, lit while the page view is on. */
/* [LIQUID-GLASS 2026-11-02, user] a round glass button; while on, a green glass glow */
const PageViewBtn = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $on: boolean }>`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  border-radius: 50%;
  font-size: 15px;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$on ? (dark(p) ? '#4acd8b' : p.theme.green600) : dark(p) ? 'transparent' : 'rgba(22,25,23,0.10)')};
  background-color: ${(p) => (p.$on ? (dark(p) ? 'rgba(74,205,139,0.16)' : 'rgba(26,159,125,0.12)') : lgControlBg(p))};
  background-image: ${lgCardSheen};
  box-shadow: ${(p) => (p.$on
    ? `0 0 0 3px ${dark(p) ? 'rgba(74,205,139,0.16)' : 'rgba(26,159,125,0.14)'}, 0 0 16px ${dark(p) ? 'rgba(74,205,139,0.22)' : 'rgba(26,159,125,0.18)'}`
    : dark(p) ? '0 1px 1px rgba(0,0,0,0.25), 0 10px 24px -12px rgba(0,0,0,0.6)' : '0 1px 2px rgba(22,25,23,0.06), 0 8px 20px -10px rgba(22,25,23,0.24)')};
  color: ${(p) => (p.$on ? p.theme.greenInk : p.theme.text)};
  & > * { position: relative; z-index: 1; }
  ${lgRim}
  ${lgPress}

  &:hover:not(:disabled) { border-color: ${(p) => (dark(p) ? '#4acd8b' : p.theme.green600)}; }
  &:disabled { opacity: 0.45; cursor: default; }
`;

/* [NO-COPY 2026-10-14, user] "make this thing in the lead management tab other then admins no one can drag and copy any data
   from it, no data copy thing will we allowed in this tab only admins can do this": while the tab is open for anyone who is
   not an admin, nothing on the page can be selected or dragged (the page sets body[data-no-copy]; it also blocks copy / cut /
   drag / right-click), and the email's own frame gets the same style (noCopyDoc) — it is sandboxed, so CSS is what reaches it. */
const NoCopyStyle = createGlobalStyle`
  body[data-no-copy], body[data-no-copy] * {
    -webkit-user-select: none !important;
    user-select: none !important;
    -webkit-user-drag: none !important;
  }
`;
const NO_COPY_CSS = '<style>*{-webkit-user-select:none!important;user-select:none!important;-webkit-user-drag:none!important}img{pointer-events:none!important}</style>';
const noCopyDoc = (html: string) => (/<head[^>]*>/i.test(html) ? html.replace(/<head[^>]*>/i, (h) => `${h}${NO_COPY_CSS}`) : `${NO_COPY_CSS}${html}`);

/* ── "which day?" — [LEAD-DATE 2026-10-13] ── */

/* Over the email popup too, so: a dark shade and only a LITTLE blur (popup over a window — RetentionPmPopup's recipe). */
const DayOverlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: ${(p) => (dark(p) ? 'rgba(10, 10, 10, 0.62)' : 'rgba(22, 25, 23, 0.42)')};
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  ${(p) => fluidScrim(p.$open)}

  @media (max-width: 768px) { padding: 8px; } /* [PHONE-LAYOUT 2026-10-22] the box can use the screen less 8 px a side */
`;

const DayBox = styled.div<{ $open: boolean }>`
  ${(p) => fluidDialog({ open: p.$open })}
  ${(p) => (p.$open ? fluidCascade('> *', 0, 20, 3) : '')}
  width: 440px;
  max-width: 100%;
  padding: 22px 24px 20px;
  /* [LIQUID-GLASS 2026-11-02, user] the app's frosted glass (lgSurface), smooth corners. A phone lets the box scroll, and a live
     blur on a box that scrolls can flash white in Chrome, so there the same glass is drawn without the live blur (a little more
     solid). */
  position: relative;
  color: ${(p) => p.theme.text};
  ${lgSurface(30)}
  ${lgSquircle(22)}
  @media (max-width: 768px) {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    background-color: ${(p) => (dark(p) ? 'rgba(30,31,32,0.97)' : 'rgba(250,251,250,0.98)')};
  }

  h3 { margin: 0 0 6px; font-size: 17px; font-weight: 700; }
  p { margin: 0 0 16px; font-size: 13.5px; line-height: 1.5; color: ${(p) => p.theme.muted}; }
  .pick { display: flex; justify-content: center; margin-bottom: 20px; }
  .buttons { display: flex; justify-content: flex-end; gap: 10px; }
  /* [LEADS-IDENTIFIED-WORDING 2026-10-14] "Add to Leads Identified · <day>" does not fit beside Cancel on a phone: stacked,
     full width, the Add button on top. */
  @media (max-width: 768px) {
    .buttons { flex-direction: column-reverse; }
    .buttons > button { width: 100%; justify-content: center; }
    /* [PHONE-LAYOUT 2026-10-22] never taller than the screen (it scrolls inside), 16 px sides, finger-sized buttons */
    max-height: calc(100dvh - 24px);
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 18px 16px 16px;
    .buttons > button { height: 40px; }
  }
`;

/** [LEAD-DATE 2026-10-13, user] "if someone click on the lead then it will also ask on what date this lead will go … the
 *  date selection should work for the same date for which this row is for and for the future". Up to LATEST_DAYS_AHEAD days
 *  ahead.
 *  [NO-PAST-DAY 2026-10-14, user] "if someone is getting the leads from the previous date and by mistake if he click the add to
 *  the lead on that previous date only then it should show error … as we have already assigned the data for the previous
 *  date … change the date to add to the current date or future date": the box starts on TODAY and no earlier day can be
 *  picked (an earlier email's own day is already assigned); a past day that still reaches Add (the day changed at midnight
 *  with the box open) is refused with that message — the server refuses it too. */
const pastDayText = (d: string) => `A lead can't be added to ${dayLabel(d)} — that day is already assigned. Pick today or a later day.`;
const LeadDayDialog: React.FC<{ row: LeadRow | null; onCancel: () => void; onConfirm: (r: LeadRow, day: string) => void }> = ({ row, onCancel, onConfirm }) => {
  const pres = usePresence(row, fluid.dialogOutMs);
  const shown = !!row && pres.entered;
  const r = row ?? pres.value;
  const own = r ? (r.receivedDay || r.date.slice(0, 10)) : '';
  const todayNow = todayIst();
  const first = own > todayNow ? own : todayNow; // [NO-PAST-DAY] today or later
  const [picked, setPicked] = useState(first);
  const rowId = row ? row.id : null;
  useEffect(() => { if (rowId != null) setPicked(first); }, [rowId, first]);
  const esc = useRef(onCancel);
  esc.current = onCancel;
  useEffect(() => {
    if (rowId == null) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); esc.current(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [rowId]);
  if (!pres.mounted || !r) return null;
  const latest = addDays(todayIst(), LATEST_DAYS_AHEAD);
  return createPortal(
    <DayOverlay $open={shown} data-reload-block={row ? '' : undefined} onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      {/* [LEADS-IDENTIFIED-WORDING 2026-10-14, user] "if someone mark it to lead and select the date, then on that day, it will
          go to the lead identify on the lead identify side only" — the box names where it goes (the rename of 10-13 missed it). */}
      <DayBox $open={shown} role="dialog" aria-modal="true" aria-label="Which day in Leads Identified">
        <h3>Add to Leads Identified</h3>
        <p>{whoOf(r)} came on {dayLabel(own)}. Which day of Leads Identified should it go to? Today or a later day — an earlier day is already assigned.</p>
        <div className="pick">
          <SingleDateCalendar value={picked} min={first} max={latest > first ? latest : first} onChange={setPicked} liquid /* [LIQUID-GLASS 2026-11-02] */ />
        </div>
        <div className="buttons">
          <NotLeadBtn type="button" onClick={onCancel}>Cancel</NotLeadBtn>
          <LeadBtn type="button" onClick={() => (picked < todayIst() ? toastError(pastDayText(picked)) : onConfirm(r, picked))}>Add to Leads Identified · {dayLabel(picked)}</LeadBtn>
        </div>
      </DayBox>
    </DayOverlay>,
    document.body,
  );
};

/* ── [NAL-NOTE 2026-10-22] why it is NAL ── */

/* [NAL-NOTE 2026-10-22, user] "when someone click on the nal button then a textbox will open with the save and cancel button and then
   perosn will enter the text and save it, if sosmone enter the nal button and dont write the text then the nal will also not be selected
   for that row, text is mantatory to map the nal there": the NAL button (table row, popup, Single page view) opens this box first. Save
   marks the row NAL with the note (Save stays off while the box is empty; Enter saves); Cancel, Esc or a click outside leave the row as
   it was. The server refuses a new NAL mark without its note too. Taking a mark off asks nothing. The rows marked NAL before keep their
   mark as they are ("for the recors which are already nal right now, dont make it blank, it will stay just like it is"). */
const NAL_NOTE_MAX = 500; // the server's limit (services/leadAssignment.ts NAL_NOTE_MAX)
const NalBox = styled(DayBox)`
  textarea {
    display: block;
    width: 100%;
    box-sizing: border-box;
    min-height: 92px;
    margin: 0 0 18px;
    resize: vertical;
    border-radius: 12px;
    padding: 10px 12px;
    font: inherit;
    font-size: 13.5px;
    line-height: 1.5;
    color: ${(p) => p.theme.text};
    /* [LIQUID-GLASS 2026-11-02] pressed into the glass; a soft green ring and glow while typing */
    ${lgSunken}
    transition: border-color 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1);
    /* no placeholder: under [PURE-INK] it would be full ink, like typed words — the line above says what to write */
  }
  /* [NO-COPY] the tab stops selecting for non-admins (body[data-no-copy] *) — the note is their own typing */
  && textarea { -webkit-user-select: text !important; user-select: text !important; }
`;
const NalNoteDialog: React.FC<{ row: LeadRow | null; onCancel: () => void; onSave: (r: LeadRow, note: string) => void }> = ({ row, onCancel, onSave }) => {
  const pres = usePresence(row, fluid.dialogOutMs);
  const shown = !!row && pres.entered;
  const r = row ?? pres.value; // keeps its words while it closes
  const [text, setText] = useState('');
  const boxRef = useRef<HTMLTextAreaElement>(null);
  const rowId = row ? row.id : null;
  useEffect(() => { // a new box: empty, the cursor in it
    if (rowId == null) return;
    setText('');
    boxRef.current?.focus();
  }, [rowId]);
  const esc = useRef(onCancel);
  esc.current = onCancel;
  useEffect(() => {
    if (rowId == null) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); esc.current(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [rowId]);
  if (!pres.mounted || !r) return null;
  const note = text.replace(/\s+/g, ' ').trim(); // one line, as the server keeps it
  const save = () => { if (row && note) onSave(row, note); };
  return createPortal(
    <DayOverlay $open={shown} data-reload-block={row ? '' : undefined} onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <NalBox $open={shown} role="dialog" aria-modal="true" aria-label="Why it is NAL" data-nal-note-dialog="">
        <h3>Mark NAL</h3>
        <p>{whoOf(r)} — write why it is NAL. The NAL mark is saved only with this note.</p>
        <textarea
          ref={boxRef}
          value={text}
          maxLength={NAL_NOTE_MAX}
          aria-label="NAL note"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); save(); } }}
        />
        <div className="buttons">
          <NotLeadBtn type="button" onClick={onCancel}>Cancel</NotLeadBtn>
          <LeadBtn type="button" onClick={save} disabled={!note}>Save</LeadBtn>
        </div>
      </NalBox>
    </DayOverlay>,
    document.body,
  );
};

/* ── [ASSIGNEE-HOLD 2026-10-16] the leads Add Assignee held ── */

/* [ASSIGNEE-HOLD 2026-10-16, user] "make sure to have this in the system that this should not happen again … add the logic to the
   lead management tab": 52 ROW leads CC'd Insha Haider while she was not in Team ROW - Sales, and Add Assignee gave them to
   others at random. Now such a lead is held (server services/leadAssignee.ts holdOf) and this window lists them after the run:
   leave them (fix the team in HubSpot, run Add Assignee again) or give them a name the usual way after all. */
const HoldBox = styled(DayBox)`
  width: 640px;
  .list {
    max-height: min(46vh / var(--app-zoom, 1), 420px); /* [APP-FIT 2026-10-26] the window in page px */
    overflow-y: auto;
    margin: 0 0 18px;
    border-radius: 12px;
    ${lgTile} /* [LIQUID-GLASS 2026-11-02] a glass card inside the window */
  }
  .item {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 4px 12px;
    padding: 10px 14px;
    font-size: 13px;
    line-height: 1.45;
    & + .item { border-top: 1px solid ${lgHair}; }
  }
  .site { font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .why { grid-column: 1 / -1; }
`;
/** [CC-NOT-ACTIVE 2026-11-03] what keeps the person on the Cc out of the lead's Active list (the server's `why`) */
const holdWhy = (p: AssigneeHoldRow['people'][number], row: boolean) => (p.why === 'no-user' ? 'not a HubSpot user yet'
  : `${p.team || 'no team'} — ${p.why === 'other-side' ? `Active for ${row ? 'US' : 'ROW'} leads only` : p.why === 'not-sales' ? 'not in a Sales team' : 'not Active'}`);
const HoldDialog: React.FC<{ held: { day: string; holds: AssigneeHoldRow[] } | null; busy: boolean; onLeave: () => void; onRelease: () => void }> = ({ held, busy, onLeave, onRelease }) => {
  const pres = usePresence(held, fluid.dialogOutMs);
  const shown = !!held && pres.entered;
  const h = held ?? pres.value;
  const esc = useRef(onLeave);
  esc.current = onLeave;
  const open = !!held;
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); esc.current(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);
  if (!pres.mounted || !h) return null;
  const n = h.holds.length;
  return createPortal(
    <DayOverlay $open={shown} data-reload-block={held ? '' : undefined} onClick={(e) => { if (e.target === e.currentTarget && !busy) onLeave(); }}>
      <HoldBox $open={shown} role="dialog" aria-modal="true" aria-label="Leads held by Add Assignee" data-hold-dialog="">
        <h3>{n} lead{n === 1 ? '' : 's'} held · {dayLabel(h.day)}</h3>
        <p>
          {/* [CC-NOT-ACTIVE 2026-11-03] */}
          {n === 1 ? 'Its email CCs' : 'Their email CCs'} someone who is not in the Active list of Assignee Names, so {n === 1 ? 'it gets' : 'they get'} no
          name: not someone at random, not anyone else. Make that person Active in Assignee Names — the next automatic run then gives
          the lead to them from the email. App Alerts shows these leads too. {/* [ASSIGN-ALL 2026-10-19] the window sits inside Assign to HubSpot's run */}
          Either way, Assign to HubSpot then goes on with the other leads.
        </p>
        <div className="list">
          {h.holds.map((x) => (
            <div className="item" key={x.id} data-hold-id={x.id}>
              <span className="site" title={x.website || x.email || ''}>{x.website || x.email || x.firstName || `#${x.id}`}</span>
              <Chip $tone={x.row ? 'violet' : 'blue'}>{x.row ? 'ROW' : 'US'}</Chip>
              <span className="why">
                CCs {x.people.map((p, i) => (
                  <React.Fragment key={p.email}>{i ? ', ' : ''}<Flagged $tone="amber">{p.name}</Flagged> ({holdWhy(p, x.row)})</React.Fragment>
                ))}
              </span>
            </div>
          ))}
        </div>
        <div className="buttons">
          <NotLeadBtn type="button" onClick={onLeave} disabled={busy}>Leave them</NotLeadBtn>
          <LeadBtn type="button" onClick={onRelease} disabled={busy} title="Named in the email, else the fewest — as Add Assignee does for every other lead">
            {busy ? 'Assigning…' : 'Assign them the usual way'}
          </LeadBtn>
        </div>
      </HoldBox>
    </DayOverlay>,
    document.body,
  );
};
/* ── [ASSIGNEE-NAMES 2026-10-16] who Add Assignee may use ── */

/* [ASSIGNEE-NAMES 2026-10-16, user] "it will have all the names from the sales team, team wise names and it will have the 2 boxes one
   for the active and one for the inactive and if the name has active … used in the assignment process … inactive … not used" + "they
   will start inactive and it will work as per the live sales names and the live team structure". Two boxes, each by team; a click
   moves a name to the other box and saves at once (server services/leadAssignee.ts setAssigneeNameActive). */
const NamesBox = styled(DayBox)`
  width: 880px;
  .cols { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 14px; margin: 0 0 16px; }
  /* [LIQUID-GLASS 2026-11-02] the Active / Inactive boxes are glass cards inside the window */
  .col { min-width: 0; display: flex; flex-direction: column; border-radius: 14px; ${lgTile} overflow: hidden; }
  .head { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; font-size: 13.5px; font-weight: 700; border-bottom: 1px solid ${lgHair}; }
  .list { height: min(50vh / var(--app-zoom, 1), 440px); overflow-y: auto; padding: 2px 0 8px; } /* [APP-FIT 2026-10-26] the window in page px */
  .team { display: flex; align-items: center; gap: 8px; padding: 12px 14px 4px; font-size: 12px; font-weight: 700; }
  .empty { padding: 18px 14px; font-size: 13px; }
  .sk { display: grid; gap: 12px; padding: 14px; }

  /* [PHONE-LAYOUT 2026-10-22] phones: Active above Inactive (one column), each list a short box of its own */
  @media (max-width: 768px) {
    .cols { grid-template-columns: minmax(0, 1fr); gap: 10px; }
    .list { height: auto; max-height: 26vh; }
  }
`;
const NameRow = styled.button<{ $to: 'active' | 'inactive' }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 7px 14px;
  border: 0;
  background: transparent;
  color: ${(p) => p.theme.text};
  font-size: 13.5px;
  text-align: left;
  cursor: pointer;
  transition: background 140ms ease;
  /* [LIQUID-GLASS 2026-11-02] under the mouse: a soft glass highlight (a light top edge) */
  &:hover:not(:disabled) {
    background: ${(p) => (dark(p) ? 'rgba(255,255,255,0.07)' : 'rgba(22,25,23,0.05)')};
    box-shadow: inset 0 1px 0 ${(p) => (dark(p) ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.9)')};
  }
  &:disabled { cursor: progress; }
  .go { flex-shrink: 0; font-size: 12px; font-weight: 600; color: ${(p) => TONE[p.$to === 'inactive' ? 'red' : 'green'][dark(p) ? 'dark' : 'light'][1]}; }
`;
type NamesPerson = AssigneeNamesView['teams'][number]['people'][number];
const AssigneeNamesDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const pres = usePresence(open || null, fluid.dialogOutMs);
  const shown = open && pres.entered;
  const [view, setView] = useState<AssigneeNamesView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => { // always the live list when it opens
    if (!open) return undefined;
    let live = true;
    setError(null);
    setView(null);
    fetchAssigneeNames().then((v) => { if (live) setView(v); })
      .catch((e: any) => { if (live) setError(errorText(e, 'Assignee Names could not be read from HubSpot — close and open it again.')); });
    return () => { live = false; };
  }, [open]);
  const esc = useRef(onClose);
  esc.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); esc.current(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);
  const setOne = (id: string, active: boolean) => (cur: AssigneeNamesView | null) => (cur
    ? { ...cur, teams: cur.teams.map((t) => ({ ...t, people: t.people.map((p) => (p.id === id ? { ...p, active } : p)) })) } : cur);
  const move = async (p: NamesPerson, active: boolean) => {
    if (busy.has(p.id)) return;
    setView(setOne(p.id, active)); // moves at once; the server's answer confirms it
    setBusy((s) => new Set(s).add(p.id));
    try {
      const v = await saveAssigneeName(p.id, active);
      const saved = v.teams.flatMap((t) => t.people).find((x) => x.id === p.id);
      setView((cur) => (cur ? { ...setOne(p.id, saved ? saved.active : active)(cur)!, stored: v.stored } : v));
    } catch (e: any) {
      setView(setOne(p.id, !active));
      toastError(errorText(e, `${p.name} was not moved — nothing was saved. Try again.`));
    } finally {
      setBusy((s) => { const n = new Set(s); n.delete(p.id); return n; });
    }
  };
  if (!pres.mounted) return null;
  const box = (active: boolean) => {
    const teams = (view?.teams || []).map((t) => ({ ...t, people: t.people.filter((x) => x.active === active) })).filter((t) => t.people.length);
    const n = teams.reduce((s, t) => s + t.people.length, 0);
    return (
      <div className="col" data-names-box={active ? 'active' : 'inactive'}>
        <div className="head"><span>{active ? 'Active' : 'Inactive'}</span><Chip $tone={active ? 'green' : 'grey'}>{view ? n : '…'}</Chip></div>
        <div className="list">
          {!view ? (
            <div className="sk">{[70, 55, 62, 48, 66, 52].map((w, i) => <SkeletonBox key={i} $w={`${w}%`} $h="14px" />)}</div>
          ) : teams.length ? teams.map((t) => (
            <div key={t.team} data-names-team={t.team}>
              <div className="team">{t.team}<Chip $tone={t.side === 'ROW' ? 'violet' : 'blue'}>{t.side}</Chip></div>
              {t.people.map((p) => (
                <NameRow key={p.id} type="button" $to={active ? 'inactive' : 'active'} data-name-id={p.id} disabled={busy.has(p.id)} aria-busy={busy.has(p.id)}
                  title={active ? `Move ${p.name} to Inactive — Add Assignee stops giving leads to them` : `Move ${p.name} to Active — Add Assignee may give leads to them`}
                  onClick={() => { void move(p, !active); }}>
                  <span>{p.name}</span>
                  <span className="go">{busy.has(p.id) ? <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                    : active ? <>Inactive <i className="fas fa-arrow-right" aria-hidden="true" /></> : <><i className="fas fa-arrow-left" aria-hidden="true" /> Active</>}</span>
                </NameRow>
              ))}
            </div>
          )) : <div className="empty">{active ? 'Nobody is Active.' : 'Nobody is Inactive.'}</div>}
        </div>
      </div>
    );
  };
  return createPortal(
    <DayOverlay $open={shown} data-reload-block={open ? '' : undefined} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <NamesBox $open={shown} role="dialog" aria-modal="true" aria-label="Assignee Names" data-assignee-names="">
        <h3>Assignee Names</h3>
        <p>Add Assignee gives leads only to the Active names — ROW leads to Team ROW - Sales, US leads to the other Sales teams. The people
          and their teams come from HubSpot, live; someone new in a Sales team starts Inactive. Click a name to move it — it saves at once.
          {/* [CC-NOT-ACTIVE 2026-11-03] */} A lead whose email CCs someone who is not Active waits with no name (App Alerts tells you) — once
          you make them Active, the next automatic run gives the lead to them.</p>
        {error ? <div className="empty" role="alert">{error}</div> : <div className="cols">{box(true)}{box(false)}</div>}
        <div className="buttons"><NotLeadBtn type="button" onClick={onClose}>Close</NotLeadBtn></div>
      </NamesBox>
    </DayOverlay>,
    document.body,
  );
};

/** [ASSIGNEE-HOLD] Ready to be Assigned's Assignee cell for a held row: why there is no name. */
const HoldNote = styled.span`
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  vertical-align: bottom;
  font-weight: 700;
  color: ${(p) => TONE.amber[dark(p) ? 'dark' : 'light'][1]};
`;

/* ── page ── */

/** One view's own table: its rows for the day on show, kept while the other views are on screen. */
type PaneState = { data: LeadDay | null; loading: boolean; error: string | null; forbidden: boolean };
const START: PaneState = { data: null, loading: true, error: null, forbidden: false };

const LeadAssignmentPage: React.FC = () => {
  // [LEADS-3-DAYS 2026-10-16] today follows the clock — looked at every 30 s; the page draws again only when the day changed
  const [today, setToday] = useState<string>(todayIst);
  useEffect(() => {
    const t = window.setInterval(() => { const n = todayIst(); setToday((p) => (p === n ? p : n)); }, 30_000);
    return () => window.clearInterval(t);
  }, []);
  const [day, setDay] = useState<string>(today);
  // [GEN-RANGE 2026-10-27] the Lead Generator Report's Start date + End date: the page's day until two dates are picked there; a day
  // picked on the other pages moves the report to that one day, as the report followed the page's day before
  const [genRange, setGenRange] = useState<{ from: string; to: string }>(() => ({ from: today, to: today }));
  const { user } = useAuth();
  const [view, setView] = useState<PageTab>(() => (isLeadReportOnly(user) ? 'report' : 'raw')); // [TL-REPORT-ONLY] a TL opens on the report
  // [LEAD-ASSIGNMENT 2026-10-13, user] "remove the table fade animation from this, it will just smoothly work for the
  // left and right": every view keeps its OWN table (all three load with the day), so the switch never rebuilds a table
  // — no rows rising in, no fade — and the panes only slide ($flat $quiet, components/ViewSlide).
  const [panes, setPanes] = useState<Record<LeadView, PaneState>>({ raw: START, leads: START, irrelevant: START });
  const [counts, setCounts] = useState<Counts | null>(null);
  // Rows whose Leads / Not Leads is on its way to the server: already gone from Raw Data.
  const [sending, setSending] = useState<ReadonlySet<number>>(() => new Set());
  // [ROW-POPUP] The row whose email is open (by id) and the view it was opened from.
  const [openId, setOpenId] = useState<number | null>(null);
  const [openView, setOpenView] = useState<LeadView>('raw');
  // [LEAD-DATE] The row whose "which day?" box is open, and whether the email popup steps on after it.
  const [askRow, setAskRow] = useState<LeadRow | null>(null);
  const askFromPopup = useRef(false);
  // [NAL-NOTE 2026-10-22] the row whose NAL button asks why (NalNoteDialog; null = closed) and where it was clicked
  const [nalAsk, setNalAsk] = useState<{ row: LeadRow; from: MarkFrom } | null>(null);
  const { enqueue } = useSaveQueue();
  // [ASSIGN-PROCESS 2026-10-14, user] "the lead assignment dropdown will only be seen by the admins"; A / B run the Assignment
  // Process ([ASSIGN-PROCESS-AB] below), C does nothing yet.
  const isAdmin = !!user?.is_admin;
  // [LEADS-RAW-ONLY 2026-10-14, user] "both person in the leads will see the layout for this but they cannot open this 2":
  // a person of the Leads type (User Management) sees the whole switch, but only Raw Mailbox Data opens; Leads Identified
  // and Irrelevant Leads are locked (the server refuses them too). Admins are never locked.
  const rawOnly = !!user && user.directory_role === 'leads' && !isAdmin;
  const rawOnlyRef = useRef(rawOnly);
  rawOnlyRef.current = rawOnly;
  // [TL-REPORT-ONLY 2026-10-19, user] a team leader sees the Lead Generator Report only — no Raw Mailbox Data, no email, no button; the
  // server refuses the rest for them as well. The Leads type's other rules (its days) still apply.
  const teamLeader = isLeadReportOnly(user);
  const teamLeaderRef = useRef(teamLeader);
  teamLeaderRef.current = teamLeader;
  const tabs = teamLeader ? TL_TABS : rawOnly ? LEADS_TEAM_TABS : TABS; // [LEADS-TEAM-TABS]
  useEffect(() => { if (!tabs.includes(view)) setView(tabs[0]); }, [tabs, view]); // [TL-REPORT-ONLY] never a tab they don't have
  // [NO-COPY 2026-10-14] not an admin: nothing on this tab can be selected, copied, cut, dragged out or right-clicked
  useEffect(() => {
    if (isAdmin) return undefined;
    const stop = (e: Event) => e.preventDefault();
    const kinds = ['copy', 'cut', 'dragstart', 'contextmenu', 'selectstart'];
    document.body.setAttribute('data-no-copy', '');
    kinds.forEach((k) => document.addEventListener(k, stop, true));
    return () => {
      document.body.removeAttribute('data-no-copy');
      kinds.forEach((k) => document.removeEventListener(k, stop, true));
    };
  }, [isAdmin]);
  /** The views this person may load: Raw Mailbox Data only for the Leads type. */
  const loadable = (): readonly LeadView[] => (teamLeaderRef.current ? NO_LISTS : rawOnlyRef.current ? RAW_ONLY : VIEWS); // [TL-REPORT-ONLY] none for a TL
  const [assignProcess, setAssignProcess] = useState('');
  // [ASSIGN-PROCESS-AB 2026-10-14] the A / B run on screen (null = none)
  const [assignBusy, setAssignBusy] = useState<keyof typeof BUSY_NAME | null>(null); // [DATA-STATUS-FETCH] AB · C · D · E · F
  // [ASSIGNEE-HOLD 2026-10-16] the leads the last Add Assignee held (its window), and whether "Assign them the usual way" is running
  const [held, setHeld] = useState<{ day: string; holds: AssigneeHoldRow[] } | null>(null);
  const [releasing, setReleasing] = useState(false);
  const [namesOpen, setNamesOpen] = useState(false); // [ASSIGNEE-NAMES 2026-10-16] the Assignee Names window
  // [ENRICH-DATA] [ASSIGN-HUBSPOT] how far the background step is ("45 of 186")
  const [jobProgress, setJobProgress] = useState<LeadJob['progress'] | null>(null);
  const [stopping, setStopping] = useState(false); // [E-FAST 2026-10-16] Stop pressed — Assign to HubSpot ends after the rows in hand
  // [ASSIGN-BY-TIME 2026-10-19] the rows the running Assign to HubSpot works on, as the server decided ('ROW' before 5 PM IST)
  const [assignPart, setAssignPart] = useState<AssignPart | null>(null);
  /* [RUN-IN-PLACE 2026-10-15, user] "the loading or processing thing will be show in the top middle area where we get the
     confirmation or failed thing messages": while a step runs, a capsule there says which one and how far it is ("Running Assign
     to HubSpot… — 45 of 186"); it closes when the step ends (its result message follows). The box itself only turns its arrow
     into a ring. */
  const progressToastRef = useRef<ReturnType<typeof toastProgress> | null>(null);
  useEffect(() => { // opens with the step, closes when it ends (or the page goes)
    if (!assignBusy) return undefined;
    const handle = toastProgress(`Running ${BUSY_NAME[assignBusy]}…`);
    progressToastRef.current = handle;
    return () => handle.close();
  }, [assignBusy]);
  useEffect(() => { // how far it is
    if (!assignBusy || !progressToastRef.current) return;
    // [ASSIGN-ALL 2026-10-19] Assign to HubSpot names its stage: Add Assignee → (held leads) → Enrich Data → Assigning → notes → Geography
    // → [ASSIGN-LEADS-RAW 2026-10-19] Transfer to Leads Raw
    const p = jobProgress;
    const stage = p?.phase === 'Geography' ? 'Geography' /* [GEOGRAPHY-A] */
      : p?.phase === 'Add Assignee' ? 'Add Assignee'
      : p?.phase === 'Held' ? `${p.done} held lead${p.done === 1 ? '' : 's'}: waiting for your answer`
      : p?.phase === 'Enrich Data' ? `Enrich Data${p.total ? ` ${p.done} of ${p.total}` : ''}`
      : p?.phase === 'Transfer to Leads Raw' && assignBusy === 'E' ? `Transfer to Leads Raw${p.total ? ` ${p.done} of ${p.total}` : ''}`
      : p && p.total ? `${assignBusy === 'E' && p.phase === 'Assign to HubSpot' ? 'Assigning ' : ''}${p.done} of ${p.total}${p.phase === 'Notes' ? ' notes' : ''}` : '';
    // [ASSIGN-BY-TIME 2026-10-19] the ROW run (before 5 PM IST) says so: "— ROW leads · Enrich Data 10 of 30"
    const side = assignBusy === 'E' && assignPart === 'ROW' ? 'ROW leads' : '';
    const step = stage || side ? ` — ${[side, stage].filter(Boolean).join(' · ')}` : '';
    progressToastRef.current.update(`Running ${BUSY_NAME[assignBusy]}…${step}${stopping ? ' · stopping after these rows' : ''}`); // [E-FAST]
  }, [assignBusy, jobProgress, stopping, assignPart]);
  // [PAGE-VIEW 2026-10-14, user] "before assignment process i want to add a button which will change the below table data to
  // the single page view … when i click that button table rows will disappear and it will show that page which i get when
  // i click the row": one email at a time in place of the table. Each view keeps its own place (the email on show; when it
  // leaves the list, the one now at the same place).
  const [pageView, setPageView] = useState(false);
  // [LEADS-PAGE-ONLY 2026-10-14, user] "when i click on the single page view … i want the same thing to go for the leads persons
  // … they will not see the table, they will only see the single view for the data, only for the 2 shivam and harshit": for
  // the Leads type (today exactly those two) Raw Mailbox Data is ALWAYS the page view — no table, no button to switch.
  const pageViewOn = rawOnly || pageView;
  const NO_PAGE: Record<LeadView, number | null> = { raw: null, leads: null, irrelevant: null };
  const [pageIds, setPageIds] = useState<Record<LeadView, number | null>>(NO_PAGE);
  const pageLast = useRef<Record<LeadView, number>>({ raw: 0, leads: 0, irrelevant: 0 });
  // [DUPLICATE-TINT 2026-10-13, user] "when someone click on duplicate then the row will also show/have the tag": besides
  // the amber "Duplicate" tag (Tag column, far right now that the table scrolls sideways) the whole row gets a light amber
  // tint, so it is seen without scrolling.
  // [NAL-MARK 2026-10-13, user] "the duplicate row becomes yellow … the nal row will become reddish".
  const isDark = (useTheme() as DefaultTheme).colorScheme === 'dark';
  // [NAL-COLOUR 2026-10-14, user] "for the nal row colour in the lead management side i want the same colour as we use in the
  // unmap rows in the payment tab" — PaymentsPage rowBgFn's red, both themes.
  const rowBgFn = useCallback((r: LeadRow) => (r.nal ? (isDark ? 'rgba(220, 38, 38, 0.18)' : 'rgba(220, 38, 38, 0.11)')
    : r.duplicate ? (isDark ? 'rgba(250,204,21,0.11)' : 'rgba(250,204,21,0.18)') : undefined), [isDark]);

  const dayRef = useRef(day); dayRef.current = day;
  const panesRef = useRef(panes); panesRef.current = panes;
  // Tags each view's loads so a slower earlier answer can never land over a newer one (another day).
  const reqIds = useRef<Record<LeadView, number>>({ raw: 0, leads: 0, irrelevant: 0 });

  /** skeleton = a day nobody has seen; seeded = shown at once from the app's cache, refreshed quietly; silent = a
   *  background refresh — a failure keeps what is on screen. */
  const loadView = useCallback(async (d: string, v: LeadView, mode: 'skeleton' | 'seeded' | 'silent') => {
    const id = ++reqIds.current[v];
    if (mode === 'skeleton') setPanes((p) => ({ ...p, [v]: { ...p[v], loading: true, error: null } }));
    try {
      const res = await fetchLeadDay(d, v);
      if (id !== reqIds.current[v]) return;
      setPanes((p) => ({ ...p, [v]: { data: res, loading: false, error: null, forbidden: false } }));
      setCounts(res.counts);
    } catch (err: any) {
      if (id !== reqIds.current[v]) return;
      if (mode === 'silent' && !panesRef.current[v].loading) return;
      setPanes((p) => ({ ...p, [v]: { data: null, loading: false, error: errorText(err, 'Could not load the emails.'), forbidden: isForbidden(err) } }));
    }
  }, []);

  // [READY 2026-10-14, user] Leads Identified → "Ready to be Assigned": Leads Tracker 2.0's Hubspot tab rows assigned that
  // day (server listReadyDay). Loaded with the day, so the switch shows it at once.
  const [readyPane, setReadyPane] = useState<{ day: string; data: ReadyDay | null; loading: boolean; error: string | null }>(
    { day: '', data: null, loading: true, error: null });
  const readyReq = useRef(0);
  const loadReady = useCallback(async (d: string, mode: 'skeleton' | 'silent') => {
    const id = ++readyReq.current;
    if (mode === 'skeleton') setReadyPane({ day: d, data: null, loading: true, error: null });
    try {
      const res = await fetchReadyDay(d);
      if (id === readyReq.current) setReadyPane({ day: d, data: res, loading: false, error: null });
    } catch (err: any) {
      if (id !== readyReq.current || mode === 'silent') return;
      setReadyPane({ day: d, data: null, loading: false, error: errorText(err, 'Could not load Ready to be Assigned.') });
    }
  }, []);

  /** All three views of a day — from the app's cache at once where it has them. */
  const loadDay = useCallback((d: string, first = false) => {
    if (!rawOnlyRef.current) void loadReady(d, 'skeleton'); // [READY] [LEADS-RAW-ONLY] not for the Leads type
    for (const v of (teamLeaderRef.current ? NO_LISTS : rawOnlyRef.current ? RAW_ONLY : VIEWS)) { // [TL-REPORT-ONLY] no list for a TL
      const seed = first ? undefined : peekCache<LeadDay>('/lead-assignment', { date: d, view: v });
      if (seed) {
        setPanes((p) => ({ ...p, [v]: { data: seed, loading: false, error: null, forbidden: false } }));
        if (v === 'raw') setCounts(seed.counts);
      }
      void loadView(d, v, seed ? 'seeded' : 'skeleton');
    }
  }, [loadView, loadReady]);

  useEffect(() => { loadDay(todayIst(), true); }, [loadDay]);

  const refreshQuietly = useCallback(() => {
    // [LEADS-RAW-ONLY] only the views this person loads (a locked view never loads, so it would wait for ever)
    const views = teamLeaderRef.current ? NO_LISTS : rawOnlyRef.current ? RAW_ONLY : VIEWS; // [TL-REPORT-ONLY] no list for a TL
    if (views.some((v) => panesRef.current[v].loading)) return;
    invalidateGetCacheByPrefix(['/lead-assignment|', '/lead-assignment/ready|']); // the lists only: an email's text never changes
    for (const v of views) void loadView(dayRef.current, v, 'silent');
    if (!rawOnlyRef.current) void loadReady(dayRef.current, 'silent'); // [READY]
  }, [loadView, loadReady]);
  const reloadAll = () => { for (const v of loadable()) void loadView(dayRef.current, v, 'silent'); };

  // A Leads / Not Leads click anywhere (the server bumps 'leadAssignment') refreshes this tab within seconds.
  useRealtime(['leadAssignment'], refreshQuietly);
  // New emails: every minute while the tab is on screen, and at once when it comes back on screen.
  useEffect(() => {
    const t = window.setInterval(() => { if (!document.hidden) refreshQuietly(); }, REFRESH_MS);
    const onVis = () => { if (!document.hidden) refreshQuietly(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { window.clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, [refreshQuietly]);

  const onPickDay = (d: string) => {
    if (d === day) return;
    setDay(d);
    setGenRange({ from: d, to: d }); // [GEN-RANGE] the report follows to that day
    setOpenId(null);
    // [PAGE-VIEW] a new day's page view starts at each view's first email
    setPageIds({ raw: null, leads: null, irrelevant: null });
    pageLast.current = { raw: 0, leads: 0, irrelevant: 0 };
    loadDay(d);
  };

  // [LEADS-3-DAYS] the days the date box offers; for the Leads type the 5 days before today to today ([LEADS-5-DAYS]). A day that falls out of
  // them (the page left open past midnight) moves to today.
  const firstDay = rawOnly ? (addDays(today, -(LEADS_TEAM_DAYS - 1)) > FIRST_DAY ? addDays(today, -(LEADS_TEAM_DAYS - 1)) : FIRST_DAY) : FIRST_DAY;
  const lastDay = rawOnly ? today : addDays(today, LATEST_DAYS_AHEAD);
  const onPickDayRef = useRef(onPickDay);
  onPickDayRef.current = onPickDay;
  useEffect(() => {
    if (rawOnly && (day < firstDay || day > lastDay)) onPickDayRef.current(today);
  }, [rawOnly, day, firstDay, lastDay, today]);
  // [GEN-RANGE 2026-10-27] the report's two dates stay inside the same days (the Leads type: the 5 days before today to today);
  // a Start date picked never goes past the End date, nor the End date before the Start date
  useEffect(() => {
    setGenRange((r) => {
      const from = r.from < firstDay ? firstDay : r.from > lastDay ? lastDay : r.from;
      const to = r.to > lastDay ? lastDay : r.to < from ? from : r.to;
      return from === r.from && to === r.to ? r : { from, to };
    });
  }, [firstDay, lastDay]);
  const pickGenFrom = useCallback((d: string) => setGenRange((r) => ({ from: d, to: r.to < d ? d : r.to })), []);
  const pickGenTo = useCallback((d: string) => setGenRange((r) => ({ from: r.from > d ? d : r.from, to: d })), []);

  /* [ASSIGN-PROCESS-AB 2026-10-14, user] "A will have the code for the row rows only and in the dropdown if i click on that
     then the code will hit and it will perform the operations, and B for the other rows where ROW tag is not there": picking
     A or B runs it at once on the day's Leads Data (the page goes there), the rows fill in, the counts say what it did. The
     dropdown shows "Running A…" until the server answers; C stays as it was (nothing yet). */
  const onAssignPick = async (value: string) => {
    // [RUN-IN-PLACE 2026-10-15] the box stays usable while a step runs — picking another step says which one is running
    if (assignBusy && value) { toast(`${BUSY_NAME[assignBusy]} is still running — the next step can start when it ends.`, { kind: 'info' }); return; }
    // [ASSIGN-ALL 2026-10-19] Add Assignee → Enrich Data → Assign to HubSpot → [ASSIGN-LEADS-RAW 2026-10-19] Transfer to Leads Raw
    if (value === 'E') { void onAssignAll(); return; }
    // [DATA-STATUS-FETCH 2026-10-19, user] "for the date status row and non row i want to merge it so that when i run this then for
    // all the rows it works … single part only, 'Data Status Fetch'": ONE step, every row of the day's Leads Data (the server
    // gives each row its own part's rule — services/leadProcess.ts kind AB)
    if (value !== 'AB') { setAssignProcess(value); return; }
    if (assignBusy) return;
    const d = dayRef.current;
    setAssignProcess('');
    setAssignBusy('AB');
    if (view !== 'leads') onPickView('leads');
    setLeadsSub('identified');
    const what = BUSY_NAME.AB;
    try {
      const r = await runAssignmentProcess(d, 'AB');
      invalidateGetCacheByPrefix(['/lead-assignment|']);
      void loadView(dayRef.current, 'leads', 'silent');
      // [NO-DETAILS-SKIP 2026-10-14] rows with no email and no website are left for a later run (they would be Fresh blind)
      const left = r.skipped ? ` ${r.skipped} row${r.skipped === 1 ? '' : 's'} left for later: no email and no website yet.` : '';
      if (!r.rows) {
        toast(`${what} · ${dayLabel(d)}: nothing to process — every row is already Processed.`, { kind: 'info' });
      } else if (!r.processed) {
        toast(`${what} · ${dayLabel(d)}: nothing Processed.${left}`, { kind: 'info' });
      } else {
        const ls = ['Fresh', 'Re-generated', 'Duplicate', 'Invalid'].filter((k) => r.leadStatus[k]).map((k) => `${k} ${r.leadStatus[k]}`).join(' · ');
        const parts = r.parts ? ` (ROW ${r.parts.A} · Non ROW ${r.parts.B})` : '';
        toastSuccess(`${what} · ${dayLabel(d)}: ${r.processed} row${r.processed === 1 ? '' : 's'} Processed${parts} — ${ls} (found in HubSpot ${r.found.website + r.found.email}).${left}`);
      }
    } catch (e: any) {
      toastError(errorText(e, 'The Assignment Process did not run. Nothing was saved — try again.'));
    } finally {
      setAssignBusy(null);
    }
  };

  /* [ASSIGNEE-C 2026-10-14, user] "i want that code to be in place of c where it will work for the ready to be assigned
     section … in the assignee column it will add the names and in the assignee email and assignee hubspot id column it will
     take [them] from the hubspot": C = Add Assignee — the Leads Tracker's add_assignee() on the day's Ready to be Assigned (the
     page goes there). ROW rows get the Team ROW - Sales names, the other rows the Assignee-names.html names: the one the email
     names first, else the one with the fewest rows. A name already there stays. */
  /* [ASSIGN-ALL 2026-10-19, user] "i run add assignee and then enrich data and then assign to hubspot, so i want to merge all so that
     i only run one function then after date status which is 'Assign to Hubspot' and it will add the name properly then enrich it then
     assign": Assign to HubSpot = Add Assignee here first (names exactly as before; its held-leads window is answered before anything
     else), then ONE server job — Enrich Data, then Assign to HubSpot (+ notes, Geography). The capsule names the stage; the message
     at the end gives every stage's counts. Each stage skips what it already did, so running it again finishes the rest. */
  /* [ASSIGN-BY-TIME 2026-10-19, user] "when i run that button before 5 pm ist then it should process the rows for the ROW and when i
     run the code after 5pm ist then it should work for the other remaining rows, because i want to assign the ROW leads first": Add
     Assignee is asked with part 'auto' — the SERVER's clock decides (ROW rows only before 17:00 IST, every row still left from 17:00)
     and answers with the part; the held-leads release and the run then use that same part, even if 5 PM passes in between. */
  const chainNames = useRef<{ day: string; added: number; held: number; released: number; noNames: number; part: AssignPart } | null>(null);
  /** after the names: the server's Enrich Data → Assign to HubSpot run, followed as before */
  const continueChain = async (d: string) => {
    setJobProgress({ phase: 'Starting', done: 0, total: 0 });
    try {
      const part = chainNames.current && chainNames.current.day === d ? chainNames.current.part : 'auto'; // [ASSIGN-BY-TIME]
      const job = await startLeadJob('hubspot', d, undefined, { enrichFirst: true, part });
      void followRef.current('hubspot', job);
    } catch (e: any) {
      const names = chainNames.current;
      chainNames.current = null;
      setAssignBusy(null);
      setJobProgress(null);
      setAssignPart(null);
      toastError(`${errorText(e, 'Assign to HubSpot did not start.')}${names?.added
        ? ` The ${names.added} name${names.added === 1 ? '' : 's'} Add Assignee gave stay saved.` : ''} Nothing was sent to HubSpot — run Assign to HubSpot again.`);
    }
  };
  const onAssignAll = async () => {
    if (assignBusy) return;
    const d = dayRef.current;
    setAssignProcess('');
    setAssignBusy('E');
    setJobProgress({ phase: 'Add Assignee', done: 0, total: 0 });
    if (view !== 'leads') onPickView('leads');
    setLeadsSub('ready');
    let r: AddAssigneeResult;
    try {
      r = await runAddAssignee(d, undefined, 'auto'); // [ASSIGN-BY-TIME] the server decides the part by its clock
    } catch (e: any) {
      setAssignBusy(null);
      setJobProgress(null);
      toastError(errorText(e, 'Assign to HubSpot did not run: Add Assignee did not answer. Nothing was saved — try again.'));
      return;
    }
    invalidateGetCacheByPrefix(['/lead-assignment|', '/lead-assignment/ready|']);
    void loadReady(dayRef.current, 'silent');
    const part: AssignPart = r.part === 'ROW' ? 'ROW' : 'all';
    if (!r.rows) {
      setAssignBusy(null);
      setJobProgress(null);
      const others = r.dayRows ?? 0;
      if (part === 'ROW' && others) { // [ASSIGN-BY-TIME] before 5 PM IST and no ROW lead: the others wait for the run from 5 PM IST
        toast(`Assign to HubSpot · ${dayLabel(d)}: before 5 PM IST it works on the ROW leads only — there is no ROW lead in Ready to be Assigned. The other ${others} lead${others === 1 ? '' : 's'} go when you run it from 5 PM IST.`, { kind: 'info' });
      } else {
        toast(`Assign to HubSpot · ${dayLabel(d)}: Ready to be Assigned is empty — run Data Status Fetch first.`, { kind: 'info' }); // [DATA-STATUS-FETCH]
      }
      return;
    }
    setAssignPart(part);
    chainNames.current = { day: d, added: r.assigned, held: r.held ?? 0, released: 0, noNames: r.noNames, part };
    // [ASSIGNEE-HOLD 2026-10-16] rows given no name because their email CCs someone outside their list: the window first, then on
    if (r.held && r.holds?.length) {
      setJobProgress({ phase: 'Held', done: r.holds.length, total: 0 });
      setHeld({ day: d, holds: r.holds });
      return;
    }
    await continueChain(d);
  };
  /** [ASSIGNEE-HOLD] "Leave them": the held leads keep no name (Assign to HubSpot skips them) and the run goes on. */
  const onLeaveHeld = () => {
    if (!held || releasing) return;
    const d = held.day;
    setHeld(null);
    void continueChain(d);
  };
  /** [ASSIGNEE-HOLD] "Assign them the usual way": Add Assignee again with the held rows released (named in the email, else the
   *  fewest), then the run goes on. A failure keeps the window (try again, or Leave them). */
  const onReleaseHeld = async () => {
    if (!held || releasing) return;
    const { day: d, holds } = held;
    setReleasing(true);
    let ok = false;
    try {
      const r = await runAddAssignee(d, holds.map((h) => h.id), chainNames.current && chainNames.current.day === d ? chainNames.current.part : 'auto'); // [ASSIGN-BY-TIME]
      invalidateGetCacheByPrefix(['/lead-assignment|', '/lead-assignment/ready|']);
      void loadReady(dayRef.current, 'silent');
      if (chainNames.current && chainNames.current.day === d) chainNames.current.released = r.released ?? 0;
      ok = true;
      setHeld(null);
    } catch (e: any) {
      toastError(errorText(e, 'Add Assignee did not run. Nothing was saved — try again, or Leave them.'));
    } finally {
      setReleasing(false);
    }
    if (ok) await continueChain(d);
  };

  /* [ENRICH-DATA] [ASSIGN-HUBSPOT 2026-10-14, user] "below the add assignee name in the assignment process dropdown I want to add
     a dropdown for the enrich data" + "it will only have the one code below the enriched data … for every row, it will assign it
     on the HubSpot. If already there, then update it. And then it will add the note": D = Enrich Data, E = Assign to HubSpot, on
     the day's Ready to be Assigned (the page goes there). Both run on the server in the background: the box says how far it is,
     the rows fill in as it goes, the message at the end gives the counts. A run already going (another tab, or this page
     opened again) is followed too. */
  const JOB_LABEL: Record<LeadJobKind, string> = { enrich: 'Enrich Data', hubspot: 'Assign to HubSpot', leadsraw: 'Transfer to Leads Raw' };
  const JOB_BUSY: Record<LeadJobKind, 'D' | 'E' | 'F'> = { enrich: 'D', hubspot: 'E', leadsraw: 'F' };
  const followJob = async (kind: LeadJobKind, first: LeadJob) => {
    setAssignBusy(JOB_BUSY[kind]);
    let job: LeadJob | null = first;
    let lastDone = -1;
    try {
      while (job && job.state === 'running') {
        setJobProgress(job.progress);
        if (job.stopRequested) setStopping(true); // [E-FAST] Stop pressed here, in another tab, or before this page opened
        if (kind === 'hubspot' && job.part) setAssignPart(job.part); // [ASSIGN-BY-TIME] a run started elsewhere shows its part too
        if (job.progress.done !== lastDone) {
          lastDone = job.progress.done;
          invalidateGetCacheByPrefix(['/lead-assignment/ready|']);
          void loadReady(dayRef.current, 'silent');
        }
        await new Promise((r) => setTimeout(r, 2000));
        try { job = await fetchLeadJob(kind); } catch { /* a missed answer: ask again */ }
      }
      invalidateGetCacheByPrefix(['/lead-assignment|', '/lead-assignment/ready|']);
      void loadReady(dayRef.current, 'silent');
      if (!job) return;
      const what = `${JOB_LABEL[kind]} · ${dayLabel(job.day)}`;
      if (job.state === 'failed') { toastError(`${what}: ${job.error || 'it stopped.'}`); return; }
      if (kind === 'enrich') {
        const r = job.result as EnrichResult;
        if (!r.rows) toast(`${what}: nothing to enrich — every row has its Enrichment Status.`, { kind: 'info' });
        else {
          toastSuccess(`${what}: ${r.done} row${r.done === 1 ? '' : 's'} Done${r.errors ? ` · ${r.errors} Error` : ''}${r.notFound
            ? ` · ${r.notFound} not found by ReachIQ (asked again next run)` : ''}${r.emailsFixed ? ` · ${r.emailsFixed} email${r.emailsFixed === 1 ? '' : 's'} fixed` : ''}.`);
        }
      } else if (kind === 'leadsraw') {
        // [LEADS-RAW-APP] the Assigned rows are in Leads_Raw now (kept for every sheet rebuild too)
        const r = job.result as LeadsRawResult;
        const notYet = r.notAssigned ? ` · ${r.notAssigned} not Assigned yet (Assign to HubSpot first)` : '';
        if (!r.transferred) toast(`${what}: nothing to transfer — no row is Assigned yet.${notYet}`, { kind: 'info' });
        else toastSuccess(`${what}: ${r.transferred} row${r.transferred === 1 ? '' : 's'} in Leads Raw (${r.added} new · ${r.updated} updated)${notYet}.`);
      } else {
        const r = job.result as AssignHubspotResult;
        // [ASSIGN-BY-TIME 2026-10-19] the ROW run (before 5 PM IST) says so, and that the other leads go from 5 PM IST
        const rowRun = r.part === 'ROW';
        const whatH = rowRun ? `${what} · ROW leads` : what;
        const later = rowRun ? ' The other leads go when you run it from 5 PM IST.' : '';
        // [ASSIGN-ALL 2026-10-19] the names this page's Add Assignee gave and Enrich Data's counts lead the message
        const nm = chainNames.current && chainNames.current.day === job.day ? chainNames.current : null;
        chainNames.current = null;
        const stillHeld = nm ? nm.held - nm.released : 0;
        const en = r.enrich;
        // a stage that did nothing (every row already had its name / its enrichment) adds no words
        const pre = [nm && (nm.added + nm.released || stillHeld > 0 || nm.noNames)
          ? `names added ${nm.added + nm.released}${stillHeld > 0 ? ` (${stillHeld} held)` : ''}${nm.noNames ? ` · ${nm.noNames} with no sales name in HubSpot` : ''}` : '',
        en && (en.done || en.errors || en.notFound)
          ? `enriched ${en.done}${en.errors ? ` · ${en.errors} Error` : ''}${en.notFound ? ` · ${en.notFound} not found by ReachIQ` : ''}` : ''].filter(Boolean).join(' · ');
        const head = pre ? `${pre} · ` : '';
        // [ASSIGN-LEADS-RAW 2026-10-19] the run ends with Transfer to Leads Raw: every Assigned row of the day — the new ones added, the
        // ones already there updated (never twice)
        const lr = r.leadsRaw;
        const lrFailed = !!lr && 'error' in lr;
        const lrNew = lr && !('error' in lr) ? lr.added : 0;
        const lrTxt = !lr ? '' : 'error' in lr ? ` Transfer to Leads Raw did not run (${lr.error.replace(/\.\s*$/, '')}) — run Assign to HubSpot again; it sends every Assigned row of the day.`
          : lr.transferred ? ` Leads Raw: ${lr.transferred} row${lr.transferred === 1 ? '' : 's'} (${lr.added} new · ${lr.updated} updated).` : '';
        if (r.stoppedAt === 'enrich') { // [ASSIGN-ALL] Stop came during Enrich Data: nothing went to HubSpot
          toast(`${whatH}: stopped during Enrich Data${pre ? ` (${pre})` : ''} — nothing was sent to HubSpot. Run Assign to HubSpot again to finish.`, { kind: 'info' });
          return;
        }
        if (r.stopped) { // [E-FAST 2026-10-16] what the stopped run finished, and what waits for the next one
          const rest = r.left ? `${r.left} row${r.left === 1 ? '' : 's'} not done yet — run Assign to HubSpot again to finish them (and Geography).`
            : 'Geography was not filled — run Assign to HubSpot again to fill it.';
          toast(`${whatH}: stopped · ${head}${r.assigned} row${r.assigned === 1 ? '' : 's'} Assigned · notes added ${r.notes.added}${r.notes.failed
            ? ` (${r.notes.failed} not added)` : ''}${r.failed ? ` · ${r.failed} failed` : ''}.${lrTxt} ${rest}`, { kind: 'info' });
          return;
        }
        const left = [r.skipped.noAssignee ? `${r.skipped.noAssignee} with no Assignee (held, or no sales name in HubSpot)` : '', // [ASSIGN-ALL]
          r.skipped.noWebsite ? `${r.skipped.noWebsite} with no website` : '', r.skipped.noEmail ? `${r.skipped.noEmail} with no email` : ''].filter(Boolean).join(', ');
        // [GEOGRAPHY-A 2026-10-14] what the Geography step wrote into HubSpot (blank companies only)
        const g = r.geography;
        const geoN = g && 'written' in g ? Object.values(g.written).reduce((a, n) => a + n, 0) : 0;
        // [GEO-LATEST 2026-10-15] …and the set Geographies changed to the latest lead's email country
        const fixN = g && 'written' in g && g.corrected ? Object.values(g.corrected).reduce((a, n) => a + n, 0) : 0;
        const fixed = fixN && g && 'written' in g && g.corrected
          ? ` · corrected to the lead's country: ${['US', 'CAN', 'ROW'].filter((k) => g.corrected![k]).map((k) => `${k} ${g.corrected![k]}`).join(' · ')}` : '';
        const geo = !g ? '' : 'error' in g ? ` Geography not filled this time (${g.error}) — the next run tries again.`
          : geoN || g.leftBlank || fixN ? ` Geography in HubSpot: ${['US', 'CAN', 'ROW'].filter((k) => g.written[k]).map((k) => `${k} ${g.written[k]}`).join(' · ') || 'none written'}${g.leftBlank
            ? ` · ${g.leftBlank} left blank (no clear country)` : ''}${fixed}.` : '';
        // [EMAIL-FIX] an email the step fixed first (e.g. a stray comma) is named in the message
        const msg = `${whatH}: ${head}${r.assigned} row${r.assigned === 1 ? '' : 's'} Assigned${r.failed ? ` · ${r.failed} failed (tried again next run)` : ''} · notes added ${r.notes.added}${r.notes.failed
          ? ` (${r.notes.failed} not added)` : ''}${r.emailsFixed ? ` · ${r.emailsFixed} email${r.emailsFixed === 1 ? '' : 's'} fixed` : ''}.${left ? ` Left out: ${left}.` : ''}${geo}${lrTxt}${later}`;
        const allDone = r.rows && r.skipped.assigned === r.rows && !r.notes.rows;
        const allDoneTxt = `${whatH}: ${head}every ${rowRun ? 'ROW lead' : 'row'} is Assigned already.${geo}${lrTxt}${later}`;
        if (allDone && (geoN || lrNew) && !lrFailed) toastSuccess(allDoneTxt);
        else if (!lrFailed && (r.assigned || r.notes.added || geoN || (nm && nm.added + nm.released) || (en && en.done) || lrNew)) toastSuccess(msg);
        else toast(allDone ? allDoneTxt : msg, { kind: 'info' });
      }
    } finally {
      setAssignBusy(null);
      setJobProgress(null);
      setStopping(false);
      setAssignPart(null); // [ASSIGN-BY-TIME]
    }
  };
  const followRef = useRef(followJob);
  followRef.current = followJob;
  // [E-FAST 2026-10-16, user] "do it have the option to stop if midway": the server ends the run after the group of rows in hand
  // (each of them assigned AND noted) — never inside one; the capsule says so until it ends, then the message gives the counts
  const onStopHubspot = async () => {
    if (assignBusy !== 'E' || stopping) return;
    setStopping(true);
    try {
      await stopLeadJob('hubspot');
    } catch (e: any) {
      setStopping(false);
      toastError(errorText(e, 'Stop did not reach the server — the run goes on. Try again.'));
    }
  };
  useEffect(() => {
    if (!isAdmin) return undefined;
    let off = false;
    void (async () => {
      for (const kind of ['enrich', 'hubspot', 'leadsraw'] as const) {
        try {
          const j = await fetchLeadJob(kind);
          if (!off && j && j.state === 'running') { void followRef.current(kind, j); return; }
        } catch { /* none running */ }
      }
    })();
    return () => { off = true; };
  }, [isAdmin]);

  const onPickView = (v: PageTab) => {
    if (v === view || !tabs.includes(v)) return; // [LEADS-TEAM-TABS] only the tabs this person has
    setView(v);
    // A view whose load failed tries again when it is picked; the others are already there.
    if (v !== 'report' && panesRef.current[v].error) void loadView(day, v, 'skeleton');
  };

  const setRowSending = (id: number, on: boolean) => setSending((s) => {
    const n = new Set(s);
    if (on) n.add(id); else n.delete(id);
    return n;
  });
  const dropFromRaw = (id: number) => setPanes((p) => (p.raw.data
    ? { ...p, raw: { ...p.raw, data: { ...p.raw.data, rows: p.raw.data.rows.filter((r) => r.id !== id) } } }
    : p));

  // [LEAD-ASSIGNMENT] Leads / Not Leads. The row leaves Raw Data at once; "moved to …" shows only when the server has
  // stored it (tag + copy, one transaction). A refusal brings the row back; "already marked" (someone else was quicker)
  // leaves it gone. Every click goes through the app's save queue, in order.
  // [LEAD-DATE 2026-10-13] Leads carry the day picked in the "which day?" box.
  const mark = (row: LeadRow, tag: LeadTag, leadDay?: string) => {
    if (sending.has(row.id)) return;
    setRowSending(row.id, true);
    const { promise, ahead } = enqueue(() => markLeadEmail(row.id, tag, leadDay));
    if (ahead > 0) queuedToast(tag === 'Lead' ? 'Leads' : 'Not Leads', ahead);
    promise.then((res) => {
      const listedOn = res.countedDate || leadDay || dayRef.current;
      // [UNIQUE-SITE 2026-10-14, user] Leads Data keeps one row per website, with its newest email
      const site = row.website || 'This website';
      const same = res.sameSite === 'replaced' ? ` — ${site} was already there, its row now shows this newest email`
        : res.sameSite === 'older' ? ` — ${site} is already there with a newer email, which stays shown` : '';
      // [LEAD-WINS 2026-10-27] the same person's NAL email of this website that day was replaced by this lead (the fixed data)
      const won = res.replacedNal?.length ? ` · its NAL email of ${row.website || 'this website'} from the same day is replaced by this lead` : '';
      toastSuccess(tag === 'Lead'
        ? `${whoOf(row)} moved to Leads Identified${listedOn !== dayRef.current ? ` of ${dayLabel(listedOn)}` : ''}${same}${won}`
        : `${whoOf(row)} moved to Irrelevant Leads`);
      dropFromRaw(row.id);
      setCounts((c) => (c ? {
        ...c,
        raw: Math.max(0, c.raw - 1),
        ...(tag === 'Lead' ? (listedOn === dayRef.current && !res.sameSite ? { leads: c.leads + 1 } : {}) : { irrelevant: c.irrelevant + 1 }),
      } : c));
      setRowSending(row.id, false);
      reloadAll(); // the server's own lists (and counts) confirm it
    }).catch((e) => {
      const status = e?.response?.status;
      if (status === 409 || status === 404) {
        toast(errorText(e, 'Someone else marked this email just now.'), { kind: 'info' });
        dropFromRaw(row.id);
        setRowSending(row.id, false);
        reloadAll();
        return;
      }
      setRowSending(row.id, false); // the row comes back
      toastError(errorText(e, 'Could not save. Please try again.'));
    });
  };

  // [DUPLICATE 2026-10-13, user] The row stays in Raw Data with the tag "Duplicate" and goes to the bottom at once; the
  // message waits for the server; a refusal puts the list back as the server has it.
  // [DUPLICATE-TOGGLE 2026-10-13, user] "if i click on the duplicate again the process will become undo": the same button
  // takes the mark off — the row goes back to its place by time. [NAL-MARK 2026-10-13, user] NAL works the same, red, at
  // the very bottom. A row has at most one of the two (NAL on a duplicate takes the Duplicate off).
  // [NAL-NOTE 2026-10-22] note = why it is NAL (a new NAL mark only — the box asked for it first)
  const toggleMark = (row: LeadRow, mark: LeadMark, note?: string) => {
    if (sending.has(row.id)) return;
    const on = !(mark === 'duplicate' ? row.duplicate : row.nal);
    const next: LeadRow = {
      ...row,
      duplicate: mark === 'duplicate' ? on : on ? false : !!row.duplicate,
      nal: mark === 'nal' ? on : on ? false : !!row.nal,
      nalNote: mark === 'nal' ? (on ? note ?? null : null) : on ? null : row.nalNote ?? null, // [NAL-NOTE] the note goes with its mark
    };
    setPanes((p) => (p.raw.data ? {
      ...p,
      raw: { ...p.raw, data: { ...p.raw.data, rows: placeRow(p.raw.data.rows, next) } },
    } : p));
    const word = mark === 'duplicate' ? 'Duplicate' : 'NAL';
    const { promise, ahead } = enqueue(() => setLeadMark(row.id, mark, on, mark === 'nal' && on ? note : undefined));
    if (ahead > 0) queuedToast(word, ahead);
    promise.then((res) => {
      // [NAL-ONE-PER-SITE 2026-10-15] one NAL per website a day: an older NAL email of this website is replaced (out of Raw)
      const moved = res?.moved?.length ? ` · the older NAL email of ${row.website || 'this website'} is replaced (one NAL per website)` : '';
      // [LEAD-WINS 2026-10-27] the website is already this person's lead today: this NAL email is replaced at once (out of Raw)
      if (on && res?.replacedByLead) toastSuccess(`${whoOf(row)} — ${row.website || 'this website'} is already their lead today, so this NAL email is replaced by the lead`);
      else toastSuccess(on ? `${whoOf(row)} marked ${word}${moved}` : `${whoOf(row)} — ${word} mark removed`);
      void loadView(dayRef.current, 'raw', 'silent');
    }).catch((e) => {
      if (e?.response?.status === 409) toast(errorText(e, 'Someone else changed this email just now.'), { kind: 'info' });
      else toastError(errorText(e, 'Could not save. Please try again.'));
      void loadView(dayRef.current, 'raw', 'silent');
    });
  };

  // [UNDO-IRRELEVANT 2026-10-13, user] Irrelevant Leads → back to Raw Data. The row leaves the list at once; "back in Raw
  // Data" shows only when the server has done it; a refusal brings the row back.
  const undoNotLead = (row: LeadRow) => {
    if (sending.has(row.id)) return;
    setRowSending(row.id, true);
    const { promise, ahead } = enqueue(() => undoLeadNotLead(row.id));
    if (ahead > 0) queuedToast('Undo', ahead);
    promise.then((res) => {
      toastSuccess(`${whoOf(row)} is back in Raw Data`);
      setPanes((p) => (p.irrelevant.data ? { ...p, irrelevant: { ...p.irrelevant, data: { ...p.irrelevant.data, rows: p.irrelevant.data.rows.filter((r) => r.id !== row.id) } } } : p));
      setCounts((c) => (c ? { ...c, irrelevant: Math.max(0, c.irrelevant - 1), ...(res.day === dayRef.current ? { raw: c.raw + 1 } : {}) } : c));
      setRowSending(row.id, false);
      reloadAll();
    }).catch((e) => {
      setRowSending(row.id, false);
      toastError(errorText(e, 'Could not undo. Please try again.'));
      reloadAll();
    });
  };

  // [UNDO-LEAD 2026-10-14, user] Leads Identified → back to Raw Mailbox Data ("if someone has mistakenly marked it as lead").
  // As the Irrelevant undo: the row leaves the list at once; the message waits for the server; a refusal brings it back.
  const undoLead = (row: LeadRow) => {
    if (sending.has(row.id)) return;
    setRowSending(row.id, true);
    const { promise, ahead } = enqueue(() => undoLeadToRaw(row.id));
    if (ahead > 0) queuedToast('Undo', ahead);
    promise.then((res) => {
      toastSuccess(`${whoOf(row)} is back in Raw Mailbox Data`);
      setPanes((p) => (p.leads.data ? { ...p, leads: { ...p.leads, data: { ...p.leads.data, rows: p.leads.data.rows.filter((r) => r.id !== row.id) } } } : p));
      setCounts((c) => (c ? { ...c, leads: Math.max(0, c.leads - 1), ...(res.day === dayRef.current ? { raw: c.raw + 1 } : {}) } : c));
      setRowSending(row.id, false);
      reloadAll();
    }).catch((e) => {
      setRowSending(row.id, false);
      const status = e?.response?.status;
      if (status === 409 || status === 404) toast(errorText(e, 'Someone else changed this email just now.'), { kind: 'info' });
      else toastError(errorText(e, 'Could not undo. Please try again.'));
      reloadAll();
    });
  };

  // [RAW-KIND 2026-10-13] The Raw Data filter (what kind of row). Kept across days.
  const [rawFilter, setRawFilter] = useState<RawFilter>('all');
  // [LEADS-IDENTIFIED] Leads Identified | Ready to be Assigned (kept across days and views).
  const [leadsSub, setLeadsSub] = useState<LeadsSub>('identified');
  // [LEAD-SORT 2026-10-15, user] each list's header sort (none = the list's own order), kept across days and views
  type SortList = LeadView | 'ready';
  const [sorts, setSorts] = useState<Record<SortList, SortConfig>>({ raw: NO_SORT, leads: NO_SORT, irrelevant: NO_SORT, ready: NO_SORT });
  const onSortOf = useMemo(() => {
    const by = (l: SortList) => (key: string) => setSorts((s) => ({ ...s, [l]: nextSort(s[l], key) }));
    return { raw: by('raw'), leads: by('leads'), irrelevant: by('irrelevant'), ready: by('ready') } as Record<SortList, (key: string) => void>;
  }, []);

  // [DROP-IN 2026-10-15, user] "the animation should show from top to bottom … dropdown is coming from top so animation should
  // also sync with it": a change in the page's dropdown (SubSelect) drops the page's line + table in from the top. A page
  // change keeps the left / right slide, which follows the page switch in the middle. The pane itself slides on a transform,
  // so its children move, not the pane. A layout effect: the move starts in the frame the new rows first show.
  const paneViewportRef = useRef<HTMLDivElement>(null);
  const subKey = `${rawFilter}|${leadsSub}`;
  const lastSubKey = useRef(subKey);
  useLayoutEffect(() => {
    if (lastSubKey.current === subKey) return;
    lastSubKey.current = subKey;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const pane = paneViewportRef.current?.querySelector(`[data-lead-pane="${view}"]`);
    if (!pane) return;
    Array.from(pane.children).forEach((el) => {
      if (el instanceof HTMLElement && el.style.display !== 'none') {
        el.animate?.([{ transform: 'translateY(-18px)', opacity: 0 }, { transform: 'none', opacity: 1 }],
          { duration: 260, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
      }
    });
  }, [subKey, view]);

  // The rows on show: Raw Data without the rows on their way to the server (and through the filter); Irrelevant Leads
  // likewise during an undo. Kept as the same arrays between renders, so a table repaints only when its rows change.
  const rawAll = useMemo(() => (panes.raw.data ? panes.raw.data.rows.filter((r) => !sending.has(r.id)) : []), [panes.raw.data, sending]);
  const rawRows = useMemo(() => sortedBy(rawAll.filter((r) => inFilter(r, rawFilter)), sorts.raw, LEAD_SORT_VALUE.raw), [rawAll, rawFilter, sorts.raw]); // [LEAD-SORT]
  const rawCounts = useMemo(() => Object.fromEntries(RAW_FILTERS.map((f) => [f.key, rawAll.filter((r) => inFilter(r, f.key)).length])) as Record<RawFilter, number>,
    [rawAll]); // [DUP-AFTER-OFF]
  const leadRows = useMemo(() => (panes.leads.data ? panes.leads.data.rows.filter((r) => !sending.has(r.id)) : []), [panes.leads.data, sending]); // [UNDO-LEAD]
  // [LEAD-STATUS-COUNTS] the rows on show by Lead Status ("Re-generated" → "regenerated")
  const statusCounts = useMemo(() => leadRows.reduce<Record<string, number>>((m, r) => {
    const k = statusKey(r.leadStatus);
    m[k] = (m[k] || 0) + 1;
    return m;
  }, {}), [leadRows]);
  // [ROW-STATUS-COUNTS 2026-10-14, user] "after that part it will show the count for the row status if it has the row tag the
  // count for row and for the blank row it will come to the count of US": ROW = rows tagged ROW, US = every other row.
  const regionCounts = useMemo(() => {
    const row = leadRows.filter((r) => r.region === 'ROW').length;
    return { row, us: leadRows.length - row };
  }, [leadRows]);
  // [DONE-COUNTS 2026-10-16, user] "on the right side … 23 process of the total rows, so that at the time of date add i can check
  // that all the row got the processed word or not": the rows on show whose Confirmation is Processed (set by Data Status Fetch — was Date Status A / B)
  const processedCount = useMemo(() => leadRows.filter((r) => String(r.confirmation ?? '').trim().toLowerCase() === 'processed').length, [leadRows]);
  const irrelevantRows = useMemo(() => (panes.irrelevant.data ? panes.irrelevant.data.rows.filter((r) => !sending.has(r.id)) : []), [panes.irrelevant.data, sending]);
  // [LEAD-SORT] the lists on show in their header sort's order — the table, its popup's ‹ › and the page view all follow it
  const leadShown = useMemo(() => sortedBy(leadRows, sorts.leads, LEAD_SORT_VALUE.leads), [leadRows, sorts.leads]);
  const irrelevantShown = useMemo(() => sortedBy(irrelevantRows, sorts.irrelevant, LEAD_SORT_VALUE.irrelevant), [irrelevantRows, sorts.irrelevant]);
  const rowsOf: Record<LeadView, LeadRow[]> = { raw: rawRows, leads: leadShown, irrelevant: irrelevantShown };

  // [ROW-POPUP] The open row, found again in its list on every render (so it follows reloads); gone = closed.
  const openList = rowsOf[openView];
  const openIdx = openId == null ? -1 : openList.findIndex((r) => r.id === openId);
  const openRow = openIdx >= 0 ? openList[openIdx] : null;
  useEffect(() => { if (openId != null && openIdx < 0 && !askRow) setOpenId(null); }, [openId, openIdx, askRow]);
  /** The row to show after this one leaves the list (or moves to its bottom): the next, else the one before, else none. */
  const stepAfter = (r: LeadRow) => {
    const i = openList.findIndex((x) => x.id === r.id);
    const next = openList[i + 1] ?? openList[i - 1] ?? null;
    setOpenId(next ? next.id : null);
  };

  // [PAGE-VIEW 2026-10-14] A view's page: the email picked, else the one now at the same place, else the first.
  // Ready rows are sheet rows: no page for them
  // [PAGE-VIEW-RAW-ONLY 2026-10-15, user] "the single page view will only work for the raw mailbox data section only and not for
  // others": Leads Identified / Irrelevant Leads always show their table (+ the row's popup), whatever the button says.
  const pageMode = (v: LeadView) => pageViewOn && v === 'raw';
  const pageAt = (v: LeadView) => {
    const list = rowsOf[v];
    let idx = list.findIndex((r) => r.id === pageIds[v]);
    if (idx < 0) idx = Math.min(pageLast.current[v], list.length - 1);
    return { list, idx, row: idx >= 0 ? list[idx] : null };
  };
  const pageGo = (v: LeadView, idx: number) => {
    const list = rowsOf[v];
    if (idx < 0 || idx >= list.length) return;
    pageLast.current[v] = idx;
    setPageIds((p) => ({ ...p, [v]: list[idx].id }));
  };
  /** After an action takes the email away (or to the bottom): the next one, else the one before — as the popup does. */
  const pageStep = (r: LeadRow) => {
    if (view === 'report') return; // [LEAD-GEN-TAB] the report has no email page
    const list = rowsOf[view];
    const i = list.findIndex((x) => x.id === r.id);
    if (i < 0) return;
    const next = list[i + 1] ?? list[i - 1] ?? null;
    pageLast.current[view] = list[i + 1] ? i : Math.max(0, i - 1);
    setPageIds((p) => ({ ...p, [view]: next ? next.id : null }));
  };
  const stepOn = (r: LeadRow) => (pageViewOn && view === 'raw' ? pageStep(r) : stepAfter(r)); // [PAGE-VIEW-RAW-ONLY]

  // Buttons: in a table row, or in the popup (then it steps on to the next email).
  // [SAME-DAY-LEAD 2026-10-14, user] "for the same day if … someone click on the lead then it should not ask for the date
  // selector it will auto add to the same date but if someone click the lead for the previous date then they will be ask":
  // an email that came TODAY goes straight to today's Leads Identified; an earlier day's email still asks which day.
  const askLead = (r: LeadRow, fromPopup: boolean) => {
    const own = r.receivedDay || r.date.slice(0, 10);
    const now = todayIst();
    if (own === now) {
      if (fromPopup) stepOn(r);
      mark(r, 'Lead', now);
      return;
    }
    askFromPopup.current = fromPopup;
    setAskRow(r);
  };
  const onDayPicked = (r: LeadRow, leadDay: string) => {
    setAskRow(null);
    if (askFromPopup.current) stepOn(r);
    mark(r, 'Lead', leadDay);
  };
  // [NAL-NOTE 2026-10-22, user] "text is mantatory to map the nal there": a NEW NAL mark asks why first (NalNoteDialog) — Cancel leaves
  // the row as it was; Save marks it, then the popup / Single page view step on as before. Taking a mark off asks nothing and stays.
  const stepFrom = (r: LeadRow, from: MarkFrom) => { if (from === 'popup') stepAfter(r); else if (from === 'page') pageStep(r); };
  const askMark = (r: LeadRow, m: LeadMark, from: MarkFrom) => {
    if (sending.has(r.id)) return;
    const on = !(m === 'duplicate' ? r.duplicate : r.nal);
    if (on && m === 'nal') { setNalAsk({ row: r, from }); return; }
    if (on) stepFrom(r, from); // marking steps on to the next email (the old Duplicate marks only — no button)
    toggleMark(r, m);
  };
  const onNalSaved = (r: LeadRow, note: string) => {
    const from = nalAsk ? nalAsk.from : 'table';
    setNalAsk(null);
    // the row as the list holds it now (a reload may have come while the box was open)
    const list = panesRef.current.raw.data?.rows;
    const now = list ? list.find((x) => x.id === r.id) : r;
    if (!now) { toast(`${whoOf(r)} is not in Raw Mailbox Data any more — someone else moved it just now.`, { kind: 'info' }); return; }
    if (now.nal) { toast(`${whoOf(now)} is marked NAL already.`, { kind: 'info' }); return; }
    stepFrom(now, from);
    toggleMark(now, 'nal', note);
  };
  const markRef = useRef({ askLead, mark, toggleMark, askMark, undoNotLead, undoLead });
  markRef.current = { askLead, mark, toggleMark, askMark, undoNotLead, undoLead };

  const onTableClick = (v: LeadView) => (e: React.MouseEvent) => {
    if (v === 'leads' && leadsSub === 'ready') return; // [READY] a Leads Tracker 2.0 row, not an email of ours
    const t = e.target as HTMLElement;
    if (t.closest('button, a, input, select, textarea')) return;
    if (window.getSelection && String(window.getSelection() || '').trim()) return; // selecting text to copy
    const tr = t.closest('tr[data-row-index]') as HTMLElement | null;
    if (!tr) return;
    const row = rowsOf[v][Number(tr.dataset.rowIndex)];
    if (!row) return;
    setOpenView(v);
    setOpenId(row.id);
  };

  // Stable, so the tables' rows do not all re-render on every render of the page.
  const columns = useMemo(() => {
    const base: Column[] = [
      { key: 'date', label: 'Date', render: (v: string) => emailTime(v) },
      { key: 'firstName', label: 'First Name', render: (v) => v || <Muted>—</Muted> },
      { key: 'lastName', label: 'Last', render: (v) => v || <Muted>—</Muted> },
      // [EMAIL-LABEL 2026-10-13, user] "the email address is not written properly in the column name, it is merged"
      { key: 'email', label: 'Email Address', render: (v) => v || <Muted>—</Muted> },
      { key: 'preview', label: 'Prospect Email Copy', render: (v: string) => (v ? <CopyPreview>{v}</CopyPreview> : <Muted>(no text)</Muted>) },
      { key: 'website', label: 'Website', render: (v) => v || <Muted>—</Muted> },
      { key: 'phone', label: 'Phone Number', render: (v) => v || <Muted>—</Muted> },
      { key: 'leadBy', label: 'Lead Generated By', render: (v) => v || <Muted>—</Muted> },
      // [LEAD-GENERATORS 2026-10-13, user] "after all the leads generated by i want a column for the lead generated by (names)
      // and the team manager" — from Team_Email_Marketing_Reporting_Structure (the server matches the email).
      { key: 'leadByName', label: 'Lead Generated By (Name)', render: (v) => v || <Muted>—</Muted> },
      { key: 'teamManager', label: 'Team Manager', render: (v) => v || <Muted>—</Muted> },
      { key: 'geography', label: 'Geography', render: (v) => v || <Muted>—</Muted> },
      // [US-ROW 2026-10-13, user] "before the tag column, add a column for the geography … US … ROW"
      { key: 'region', label: 'US / ROW', render: (v: LeadRow['region']) => <RegionCell region={v} /> },
    ];
    // Raw Data's Tag: [RAW-KIND] what the row is (To map / Team: … / Reply · … / Already in Leads / Not a lead email) and
    // [DUPLICATE] "Duplicate".
    const tagCol: Column = { key: 'alreadyInLeads', label: 'Tag', render: (_v, row: LeadRow) => <TagCell row={row} /> };
    // [NAL-NOTE-COL 2026-10-22, user] "after tag column add a column for the nal note": the row's NAL note (one line, "…" when long, the
    // whole note on hover); "—" when it is not NAL or was marked before the note
    const nalNoteCol: Column = { key: 'nalNote', label: 'NAL Note', render: (v: string | null) => (v ? <ClipText $w={280} title={v}>{v}</ClipText> : <Muted>—</Muted>) };
    const raw: Column = {
      key: 'actions',
      label: 'Leads / NAL', // [NO-DUP-BUTTON 2026-10-19] no Duplicate button any more · [NO-NOT-LEAD 2026-10-27] no Not Leads either
      headerNoWrap: true,
      render: (_v, row: LeadRow) => (
        <Actions>
          <LeadBtn type="button" onClick={() => markRef.current.askLead(row, false)}>Leads</LeadBtn>
          <MarkButtons row={row} onToggle={(m) => markRef.current.askMark(row, m, 'table')} />
        </Actions>
      ),
    };
    const marked: Column = {
      key: 'markedBy',
      label: 'Marked By',
      render: (_v, row: LeadRow) => (
        <Marked>
          <b>{row.markedBy || '—'}</b>
          <span>{markedTime(row.markedAt)}</span>
        </Marked>
      ),
    };
    // [UNDO-IRRELEVANT 2026-10-13, user] "for the irrelevant leads add a section for the undo as well"
    const undoCol: Column = {
      key: 'undo',
      label: 'Undo',
      render: (_v, row: LeadRow) => (
        <NotLeadBtn type="button" onClick={() => markRef.current.undoNotLead(row)} title="Back to Raw Data, to be decided again">
          <i className="fas fa-rotate-left" /> Undo
        </NotLeadBtn>
      ),
    };
    // [LEADS-IDENTIFIED 2026-10-13, user] "leads identified side in the below toggle will have these columns … just like i have
    // the columns in the mailbox tab in the lead tracker 2.0 sheet" (its "Mailbox (New)" tab; its "Comment" = Activity here).
    // A column whose data the user has not defined yet shows "—" ("ask me one by one for all the columns and i will tell you
    // how the data will work"; Status Confirmation + ROW Status are not in that tab — the user says what they hold).
    const shown = (key: string, label: string): Column => ({ key, label, render: (v) => v || <Muted>—</Muted> });
    const identified: Column[] = [
      ...base.slice(0, 8), // Date · First Name · Last · EmailAddress · Prospect Email Copy · Website · Phone Number · Lead Generated By
      // [BUSINESS-TYPE 2026-10-13, user] "in the business type it will have Scale Ranking in all"
      // [ASSIGN-PROCESS-AB 2026-10-14] the value the Assignment Process stored (the sheet's rule), else Scale Ranking as before
      { key: 'businessType', label: 'Business Type', render: (v: string | null) => v || 'Scale Ranking' },
      // [LEADS-IDENTIFIED-FILL 2026-10-13, user] "fill the data for the leads same as we have in the mailbox tab in the lead
      // sheet 2.0" — kept on the lead (Leads_Identified), from its Leads Tracker 2.0 "Mailbox (New)" row.
      shown('status', 'Status'), shown('activity', 'Activity'), shown('leadStatus', 'Lead Status'),
      { key: 'leadByName', label: 'Lead Generated By Name', render: (v) => v || <Muted>—</Muted> },
      // [TEAM-LEADER 2026-10-13, user] "the team leader name will also fill by the same db table" — its Team Leader Name
      { key: 'teamLeader', label: 'Team Leader Name', render: (v) => v || <Muted>—</Muted> },
      shown('nalStatus', 'NAL Status'), shown('leadType', 'Lead Type'),
      { key: 'createDate', label: 'Create Date', render: (v: string | null) => (v ? hubspotDay(v) : <Muted>—</Muted>) },
      { key: 'lastActivityDate', label: 'Last Activity Date', render: (v: string | null) => (v ? hubspotDay(v) : <Muted>—</Muted>) },
      shown('confirmation', 'Confirmation'),
      // [ROW-STATUS 2026-10-13, user] "for the row which is eligible for the ROW tag it will have the 'ROW' name in the row
      // status" — the Leads Tracker script ROW_Tag_Mailbox's rule on the email's Country line (not US / Canada → ROW), which
      // the mailbox job applies to every email (country_group); "Status Confirmation" removed (user).
      { key: 'region', label: 'ROW Status', render: (v: LeadRow['region']) => (v === 'ROW' ? <RegionCell region="ROW" /> : <Muted>—</Muted>) },
      // [UNDO-LEAD 2026-10-14, user] "after ROW status column, it should have also a column name as action, and it will have
      // the undo button … if someone has mistakenly marked it as lead … the data will again go back to the raw mailbox data".
      {
        key: 'action',
        label: 'Action',
        render: (_v, row: LeadRow) => (
          <NotLeadBtn type="button" onClick={() => markRef.current.undoLead(row)} title="Back to Raw Mailbox Data, to be decided again">
            <i className="fas fa-rotate-left" /> Undo
          </NotLeadBtn>
        ),
      },
    ];
    // [RAW-COLOURS 2026-10-14, user] Raw Mailbox Data only: the sheet's colours on Email Address, Website and Phone Number.
    const coloured = (tone: Tone | null, v: string | null) => (v ? (tone ? <Flagged $tone={tone}>{v}</Flagged> : v) : <Muted>—</Muted>);
    const rawBase = base.map((c): Column => (
      c.key === 'email' ? { ...c, render: (v: string | null) => coloured(emailTone(v), v) }
        : c.key === 'website' ? { ...c, render: (v: string | null) => coloured(websiteTone(v), v) }
          : c.key === 'phone' ? { ...c, render: (v: string | null) => coloured(phoneTone(v), v) }
            : c));
    // [LEAD-SORT] every column sorts on a header click (not the Undo / Action buttons)
    return { raw: withSort([...rawBase, tagCol, nalNoteCol, raw]), leads: withSort(identified), irrelevant: withSort([...base, marked, undoCol]) } as Record<LeadView, Column[]>;
  }, []);

  // [READY 2026-10-14, user] "the column for the ready to be assigned will be this": the Leads Tracker Hubspot tab's columns, in
  // its order and its names. [READY-A-AO 2026-10-14, user] "in the ready to be assigned it should have the complete a to ao
  // column what we have in the sheet, add all to the section so that i can start working now for the lead assignment to
  // hubspot part": all 41 (A Date … AO Note Status), then our ROW Status. The tab's formula columns are worked out from the
  // row (S Lead Source, T Team Manager, W Leads Category, AK / AL Resubmission, AM Lead Status = NEW — server listReadyOurs);
  // the ones the assignment / HubSpot / note / enrichment steps write stay "—" until those steps are built.
  const readyColumns = useMemo<Column[]>(() => {
    const t = (key: keyof ReadyRow, label: string): Column => ({ key, label, render: (v) => v || <Muted>—</Muted> });
    // [READY-WIDTHS] a long value: one line, at most `w` px, "…", the whole value on hover
    const clip = (key: keyof ReadyRow, label: string, w: number): Column => ({ key, label,
      render: (v: string | null) => (v ? <ClipText $w={w} title={v}>{v}</ClipText> : <Muted>—</Muted>) });
    return withSort([ // [LEAD-SORT] every column sorts on a header click
      t('date', 'Date'), t('firstName', 'First Name'), t('lastName', 'Last Name'), t('email', 'Email Address'),
      { key: 'preview', label: 'Prospect Email Copy', render: (v: string) => (v ? <CopyPreview>{v}</CopyPreview> : <Muted>—</Muted>) },
      t('website', 'Website'), t('phone', 'Phone Number'), t('leadBy', 'Lead generated by'), t('businessType', 'Business Type'),
      t('statusCreation', 'Status - Creation'),
      // [ASSIGNEE-HOLD 2026-10-16] a held row (no name: its email CCs someone outside its list) says why, in amber
      { key: 'assignee', label: 'Assignee', render: (v: string | null, row: ReadyRow) => (v || (row.assigneeHold
        ? <HoldNote title={`Held by Add Assignee — ${row.assigneeHold}`} data-assignee-hold=""><i className="fas fa-hand" aria-hidden="true" /> Held — {row.assigneeHold}</HoldNote>
        : <Muted>—</Muted>)) },
      t('assigneeEmail', 'Assignee (Email ID)'),
      t('assigneeHubspotId', 'Assignee (HubSpot ID)'), t('nalStatus', 'NAL'), t('assignedDate', 'Assigned Date'), t('leadType', 'Lead Type'),
      t('leadByName', 'Lead Generated By Name'), t('teamName', 'Team Name'), t('leadSource', 'Lead Source'), t('teamManager', 'Team Manager'),
      // [READY-RULES 2026-10-14, user] "remove the column deal record id from this ready to be assigned section"
      t('hubspotRecordId', 'HubSpot Record ID'), t('leadsCategory', 'Leads Category'),
      clip('linkedin', 'Linkedin Handle/Linkedin Profile', 260), t('city', 'City'), t('state', 'State'), t('country', 'Country'), clip('industry', 'Industry', 220),
      t('employees', 'LinkedIn Employees'), t('yearFounded', 'Year Founded'), clip('linkedinDescription', 'LinkedIn Description', 320),
      clip('companyKeyword', 'Company Keyword', 320), clip('companySpecialities', 'Company Specialities', 300), clip('designation', 'Designation', 220),
      t('responseScore', 'Response Score'), t('enrichmentStatus', 'Enrichment Status'), t('resubmissionDate', 'Resubmission Date'),
      t('resubmissionSource', 'Resubmission Source'), t('leadStatusTab', 'Lead Status'), t('assignmentStatus', 'Assignment Status'),
      t('noteStatus', 'Note Status'),
      // [READY-ROW-STATUS 2026-10-14, user] "add a column and it will have the data for the non row and row rows" (the sheet
      // keeps them in two tabs, Hubspot and ROW; here one list with this column)
      // [READY-ROW-BLANK 2026-10-15, user] "in the table change non row to blank": ROW, else blank (as Leads Data's ROW Status)
      { key: 'rowStatus', label: 'ROW Status', render: (v: ReadyRow['rowStatus']) => (v === 'ROW' ? <Chip $tone="violet">ROW</Chip> : <Muted>—</Muted>) },
      // [READY-GEOGRAPHY 2026-10-14, user] "after row status i want to add a column for geography and i will tell you the logic"
      t('geography', 'Geography'),
      // [NAL-VERIF 2026-10-14, user] the 2.0 Hubspot tab's AB = the HubSpot company's NAL Verification Status
      t('nalVerification', 'NAL Verification Status'),
      // [LEAD-DEALS 2026-10-14, user] "after nal verification column i want to add a column for the Deal record id and deal amount":
      // Leads_Raw's Deals Record ID + Sale Amount = Leads Tracker 2.0's FetchDealsIDs rule (server scripts/lead-deals.js)
      clip('dealsRecordId', 'Deal Record ID', 260), t('saleAmount', 'Deal Amount'),
    ]);
  }, []);
  /* [READY-NOT-DONE 2026-10-14, user] "if after assign to hubspot code run there is a row which dont have assigned and note added
     to the row then that row will come to the top of the table so that i can easily see that and it will not miss": once a row of
     the day is Assigned (the step has run), every row not both Assigned AND Note Added goes first (in its own order) — no tint
     since 2026-10-16 ([READY-NO-RED]). */
  const readyRows = useMemo(() => {
    const rows = readyPane.day === day && readyPane.data ? readyPane.data.rows : [];
    const open = (r: ReadyRow) => r.assignmentStatus !== 'Assigned' || r.noteStatus !== 'Note Added';
    // [READY-OPEN-TOP 2026-10-15, user] "in the ready to be assigned if the row dont have assigned or note added then it will just
    // show on the top and red colour to that row": the open rows stay on top under a header sort too ([LEAD-SORT] orders each part)
    const sorted = sortedBy(rows, sorts.ready, readySortValue);
    return rows.some((r) => r.assignmentStatus) ? [...sorted.filter(open), ...sorted.filter((r) => !open(r))] : sorted;
  }, [readyPane, day, sorts.ready]);
  // [READY-ROW-COUNTS 2026-10-15, user] "above the table i want to see the data for the ROW status, the count of row and us
  // count … for the us count above the table the count of this blank will be shown": ROW = ROW Status ROW, US = the blank rest
  const readyRegion = useMemo(() => {
    const row = readyRows.filter((r) => r.rowStatus === 'ROW').length;
    return { row, us: readyRows.length - row };
  }, [readyRows]);
  // [DONE-COUNTS 2026-10-16, user] "same in the ready to be assigned side it should shows 78 assigned of the total number of rows":
  // the day's rows whose Assignment Status is Assigned (Assign to HubSpot)
  const readyAssigned = useMemo(() => readyRows.filter((r) => r.assignmentStatus === 'Assigned').length, [readyRows]);
  const readyLoading = readyPane.loading || readyPane.day !== day;
  /* [READY-NO-RED 2026-10-16, user] "i want to remove this red colour from the row": the rows not both Assigned AND Note Added still
     come first (readyRows above), with no red tint any more. */

  const emptyFor = (v: LeadView, p: PaneState) => {
    const when = dayLabel(day);
    if (p.error && p.forbidden) return { title: p.error };
    if (p.data && !p.data.ready) return { title: 'Lead Management switches on with the next update of the dashboard' };
    if (v === 'leads') return { title: `No leads on ${when}` };
    if (v === 'irrelevant') return { title: `No irrelevant leads on ${when}` };
    if (rawFilter !== 'all' && rawAll.length) {
      const label = RAW_FILTERS.find((f) => f.key === rawFilter)?.label || rawFilter;
      return { title: `No "${label}" rows on ${when}`, action: { label: 'Show all', onClick: () => setRawFilter('all') } };
    }
    const c = p.data?.counts;
    return c && c.leads > 0
      ? { title: `Every email of ${when} is marked`, hint: 'They are under Leads Identified.' } // [NO-NOT-LEAD 2026-10-27] no Irrelevant Leads
      : { title: `No emails on ${when}` };
  };

  return (
    // [APP-FIT 2026-10-26] laid out as on a 1920 px screen and scaled to the window by the app zoom (utils/appZoom)
    <Wrap data-lead-assignment="">
      {!isAdmin && <NoCopyStyle />}
      <TopBar>
        <TitleBox>
          <PageTitle>Lead Management</PageTitle>
          {/* [ASSIGNEE-NAMES 2026-10-16, user] left of the main switch — admins only, and on Leads Identified only ("assignee name will
              only be in the lead identified section, and not on the others"), like the Assignment Process */}
          {isAdmin && view === 'leads' && (
            <NamesBtn type="button" onClick={() => setNamesOpen(true)} title="Which Sales people Add Assignee may give leads to" data-names-btn="">
              <i className="fas fa-user-check" aria-hidden="true" /> Assignee Names
            </NamesBtn>
          )}
        </TitleBox>
        {/* [TOGGLE-SWAP 2026-10-15, user] "replace the main toggle with the page switch like lead identified, raw mailbox and the
            below toggle which is right now on the main side the all, duplicate, nal make it to the dropdown": the middle is the
            page switch again (the Leads team: Raw Mailbox Data | Lead Generator Report); a page changes with the left / right
            slide that follows this switch. */}
        <MiddleToggle>
          <KnobPill
            ariaLabel="Lead Assignment view"
            value={view}
            onPick={onPickView}
            options={tabs.map((v) => ({ key: v, label: VIEW_LABEL[v], count: counts && (v === 'leads' || v === 'irrelevant') ? counts[v] : null }))}
          />
        </MiddleToggle>
        <DateBox>
          {/* [TOGGLE-SWAP 2026-10-15, user] the page's own choice as a dropdown, left of Single page view (the Leads team: left
              of the date): Raw Mailbox Data → All · Need attention · Duplicate · NAL; Leads Identified → Leads Data · Ready to
              be Assigned; none on Irrelevant Leads / the report. A change here drops in from the top ([DROP-IN]). */}
          {view === 'raw' && (
            <SubSelect liquid /* [LIQUID-GLASS 2026-11-02] glass capsule + glass list */ value={rawFilter} onChange={(e) => setRawFilter(e.target.value as RawFilter)}>
              {/* the counts once the day's emails are in (no "· 0" while they load) */}
              {RAW_FILTERS.map((f) => <option key={f.key} value={f.key}>{`${f.label}${panes.raw.data ? ` · ${rawCounts[f.key] ?? 0}` : ''}`}</option>)}
            </SubSelect>
          )}
          {view === 'leads' && (
            <SubSelect liquid value={leadsSub} onChange={(e) => setLeadsSub(e.target.value as LeadsSub)}>
              {LEADS_SUBS.map((o) => (
                <option key={o.key} value={o.key}>
                  {`${o.label}${counts ? ` · ${o.key === 'identified' ? counts.leads : (counts.ready ?? 0)}` : ''}`}
                </option>
              ))}
            </SubSelect>
          )}
          {/* [PAGE-VIEW 2026-10-14, user] the table ↔ one email at a time (the row's page).
              [LEADS-PAGE-ONLY] not for the Leads type — they only ever have the page view.
              [PAGE-VIEW-RAW-ONLY 2026-10-15, user] "the single page view will only work for the raw mailbox data section only":
              shown on Raw Mailbox Data only (each control shows only where it works). */}
          {!rawOnly && view === 'raw' && (
            <PageViewBtn
              type="button"
              aria-pressed={pageView}
              aria-label={pageView ? 'Back to the table' : 'Single page view'}
              $on={pageView}
              onClick={() => setPageView((on) => !on)}
              data-page-view-btn=""
            >
              <i className="fas fa-file-lines" aria-hidden="true" />
            </PageViewBtn>
          )}
          {/* [E-FAST 2026-10-16, user] "do it have the option to stop if midway": only while Assign to HubSpot runs; the stop sign
              turns into a ring once pressed (same size) — the capsule says "stopping after these rows" */}
          {/* [ASSIGN-ALL 2026-10-19] once the server's run exists — not during Add Assignee / the held-leads window */}
          {isAdmin && view === 'leads' && assignBusy === 'E' && !!jobProgress && !['Add Assignee', 'Held', 'Starting'].includes(jobProgress.phase) && (
            <StopBtn type="button" onClick={() => void onStopHubspot()} disabled={stopping} aria-label="Stop Assign to HubSpot" data-stop-hubspot="">
              <i className={`fas fa-fw ${stopping ? 'fa-circle-notch fa-spin' : 'fa-stop'}`} aria-hidden="true" />
            </StopBtn>
          )}
          {/* [ASSIGN-LEADS-ONLY 2026-10-15, user] "dont show the assignment process dropdown in the raw mailbox data section, no
              need there, it will only be used in the lead identified section" */}
          {isAdmin && view === 'leads' && (
            /* [RUN-IN-PLACE 2026-10-15, user] "when i click on this to run the code then the layout for this should be as it is and
               the loading or processing thing will be show in the top middle area … and when i click the function in the dropdown
               then it will show the loading icon on the same design": while a step runs the box keeps its words, size and look
               (not disabled) with a turning ring for its arrow; the progress is the capsule at the top middle (runProgressText).
               [NO-HINTS 2026-10-15] no hover hint. */
            <AssignSelect liquid value={assignProcess} onChange={(e) => void onAssignPick(e.target.value)} loading={!!assignBusy}>
              {/* [DATE-STATUS-NAMES 2026-10-14, user] "rename the a row rows to 'Date status (ROW)' and for the B it will show 'Date Status (Non ROW)'" — merged 10-19 ↓ */}
              {/* [DATA-STATUS-FETCH 2026-10-19, user] the two Date Status steps are ONE now: "Data Status Fetch" (every row) */}
              {/* [ASSIGN-ALL 2026-10-19, user] "merge all so that i only run one function then after date status which is 'Assign to
                  Hubspot'": Add Assignee + Enrich Data + Assign to HubSpot are ONE step now (onAssignAll) */}
              <option value="">Assignment Process</option>
              <option value="AB">Data Status Fetch</option>
              {/* [ASSIGN-LEADS-RAW 2026-10-19, user] "i will only see date status fetch and assign to hubspot, and assing to hubspot
                  will assign and transfer it to the leads raw table also": Transfer to Leads Raw is Assign to HubSpot's last stage */}
              <option value="E">Assign to HubSpot</option>
            </AssignSelect>
          )}
          {/* [LEAD-DATE] Up to 30 days ahead, so a day a lead was sent forward to can be opened. [LEADS-3-DAYS] the Leads type:
              the 5 days before today to today only ([LEADS-5-DAYS 2026-10-19]). */}
          {view === 'report' ? (
            /* [GEN-RANGE 2026-10-27, user] the report: a Start date and an End date (Team Sameer Report's), inside the same days */
            <GenDates data-gen-dates="">
              <div className="f" data-date="from">
                <span className="cap">Start date</span>
                <SingleDateCalendar value={genRange.from} min={firstDay} max={genRange.to} onChange={pickGenFrom} hint={false} liquid />
              </div>
              <div className="f" data-date="to">
                <span className="cap">End date</span>
                <SingleDateCalendar value={genRange.to} min={genRange.from} max={lastDay} onChange={pickGenTo} hint={false} liquid />
              </div>
            </GenDates>
          ) : (
            <SingleDateCalendar value={day} min={firstDay} max={lastDay} onChange={onPickDay} hint={false} liquid /* [LIQUID-GLASS 2026-11-02] */ />
          )}
        </DateBox>
      </TopBar>

      {/* Raw Data rests on the LEFT, Irrelevant Leads on the RIGHT (their places in the switch). Each keeps its table. */}
      <PaneViewport ref={paneViewportRef}>
        {tabs.map((v) => {
          // [LEAD-GEN-TAB 2026-10-14] the report's own page (the server works it out — the Leads team reads it too)
          if (v === 'report') {
            return (
              <SlidePane key={v} $pos={slidePos(view, v, tabs)} $quiet $flat aria-hidden={view !== v} data-lead-pane={v}>
                <GeneratorReport from={genRange.from} to={genRange.to} active={view === 'report'} storageKey={`dt-lead-assignment-gen-report-colw:${user?.id ?? 'anon'}`} />
              </SlidePane>
            );
          }
          const p = panes[v];
          return (
            <SlidePane key={v} $pos={slidePos(view, v, tabs)} $quiet $flat aria-hidden={view !== v} data-lead-pane={v}>
              {/* [LEAD-STATUS-COUNTS 2026-10-14, user] Leads Data only: the day's rows by Lead Status. */}
              {v === 'leads' && leadsSub === 'identified' && (
                <StatusCounts aria-label="Leads Data by Lead Status">
                  <span className="t">Lead Status:</span>
                  {LEAD_STATUS_COUNTS.map((s) => (
                    <Chip key={s.key} $tone={s.tone} data-status-count={s.key}>{s.label} {p.loading ? '…' : (statusCounts[s.key] ?? 0)}</Chip>
                  ))}
                  {/* [ROW-STATUS-COUNTS 2026-10-14, user] the ROW Status column's colours: ROW violet, US blue */}
                  <span className="t g2">ROW Status:</span>
                  <Chip $tone="violet" data-region-count="ROW">ROW {p.loading ? '…' : regionCounts.row}</Chip>
                  <Chip $tone="blue" data-region-count="US">US {p.loading ? '…' : regionCounts.us}</Chip>
                  {/* [DONE-COUNTS 2026-10-16, user] right end: green when every row is Processed, amber while some are left */}
                  <Chip className="end" $tone={p.loading || processedCount === leadRows.length ? 'green' : 'amber'} data-processed-count="">
                    {p.loading ? '…' : processedCount} Processed of {p.loading ? '…' : leadRows.length} rows
                  </Chip>
                </StatusCounts>
              )}
              {/* [READY-ROW-COUNTS 2026-10-15, user] Ready to be Assigned: the day's rows by ROW Status (US = the blank ones) */}
              {v === 'leads' && leadsSub === 'ready' && (
                <StatusCounts aria-label="Ready to be Assigned by ROW Status">
                  <span className="t">ROW Status:</span>
                  <Chip $tone="violet" data-ready-region-count="ROW">ROW {readyLoading ? '…' : readyRegion.row}</Chip>
                  <Chip $tone="blue" data-ready-region-count="US">US {readyLoading ? '…' : readyRegion.us}</Chip>
                  {/* [DONE-COUNTS 2026-10-16, user] right end: green when every row is Assigned, amber while some are left */}
                  <Chip className="end" $tone={readyLoading || readyAssigned === readyRows.length ? 'green' : 'amber'} data-assigned-count="">
                    {readyLoading ? '…' : readyAssigned} Assigned of {readyLoading ? '…' : readyRows.length} rows
                  </Chip>
                </StatusCounts>
              )}
              {/* [PAGE-VIEW 2026-10-14] one email at a time in place of the table (the view on show only) */}
              {pageMode(v) && v === view && (() => {
                const pg = pageAt(v);
                return (
                  <InlinePage data-page-view={v} $noDock={leadsNoDock(user)}>
                    {/* [PAGE-LIST 2026-10-19] the emails by subject (20 %) — a click opens one, as Previous / Next do */}
                    <PageList rows={pg.list} currentId={pg.row ? pg.row.id : null} onPick={(i) => pageGo(v, i)} />
                    <PageMain>
                    {pg.row ? (
                      <EmailPage
                        inline
                        row={pg.row}
                        view={v}
                        pos={pg.idx + 1}
                        total={pg.list.length}
                        onPrev={pg.idx > 0 ? () => pageGo(v, pg.idx - 1) : null}
                        onNext={pg.idx < pg.list.length - 1 ? () => pageGo(v, pg.idx + 1) : null}
                        onClose={null}
                        onLead={(r) => markRef.current.askLead(r, true)}
                        onMark={(r, m) => markRef.current.askMark(r, m, 'page')} /* [NAL-NOTE] steps on once the NAL note is saved */
                        onUndo={(r) => { pageStep(r); markRef.current.undoNotLead(r); }}
                        keysOff={!!askRow || !!nalAsk}
                      />
                    ) : (
                      <PageEmpty>{p.loading ? 'Loading the emails…' : emptyFor(v, p).title}</PageEmpty>
                    )}
                    </PageMain>
                  </InlinePage>
                );
              })()}
              {/* the table stays mounted while the page is on show (its scroll + widths are kept) */}
              <TableScroll onClick={onTableClick(v)} style={pageMode(v) ? { display: 'none' } : undefined}>
                <DataTable
                  columns={v === 'leads' && leadsSub === 'ready' ? readyColumns : columns[v]}
                  data={v === 'leads' && leadsSub === 'ready' ? readyRows : rowsOf[v]}
                  sort={sorts[v === 'leads' && leadsSub === 'ready' ? 'ready' : v]} // [LEAD-SORT]
                  onSort={onSortOf[v === 'leads' && leadsSub === 'ready' ? 'ready' : v]}
                  loading={v === 'leads' && leadsSub === 'ready' ? (readyPane.loading || readyPane.day !== day) : p.loading}
                  disableMobileCards
                  parentScroll
                  rowBgFn={v === 'leads' && leadsSub === 'ready' ? undefined : rowBgFn} /* [READY-NO-RED] Ready rows: no tint */
                  // [COL-RESIZE 2026-10-13, user] "it should have the column resize thing which we have for the others": drag a
                  // column edge; widths kept per person and per view (the three views have different columns).
                  resizeStorageKey={`dt-lead-assignment-${v === 'leads' ? `leads-${leadsSub}` : v}-colw:${user?.id ?? 'anon'}`}
                  loadFailed={p.error && !p.forbidden && v === view ? { what: 'the emails', onRetry: () => loadView(day, v, 'skeleton') } : undefined}
                  empty={v === 'leads' && leadsSub === 'ready'
                    ? (readyPane.error ? { title: readyPane.error } : { title: `No leads assigned on ${dayLabel(day)} in Leads Tracker 2.0` })
                    : emptyFor(v, p)}
                />
              </TableScroll>
            </SlidePane>
          );
        })}
      </PaneViewport>

      <EmailPopup
        row={pageMode(openView) ? null : openRow /* [PAGE-VIEW] the page is on show instead — [PAGE-VIEW-RAW-ONLY] Raw only */}
        view={openView}
        pos={openIdx + 1}
        total={openList.length}
        onPrev={openIdx > 0 ? () => setOpenId(openList[openIdx - 1].id) : null}
        onNext={openIdx >= 0 && openIdx < openList.length - 1 ? () => setOpenId(openList[openIdx + 1].id) : null}
        onClose={() => setOpenId(null)}
        onLead={(r) => markRef.current.askLead(r, true)}
        // Marking steps on to the next email (like Leads); taking a mark off stays on this one. [NO-NOT-LEAD] no Not Leads
        // [NAL-NOTE 2026-10-22] NAL: once its note is saved (askMark → NalNoteDialog)
        onMark={(r, m) => markRef.current.askMark(r, m, 'popup')}
        onUndo={(r) => { stepAfter(r); markRef.current.undoNotLead(r); }}
        keysOff={!!askRow || !!nalAsk}
      />
      <LeadDayDialog row={askRow} onCancel={() => setAskRow(null)} onConfirm={onDayPicked} />
      <NalNoteDialog row={nalAsk ? nalAsk.row : null} onCancel={() => setNalAsk(null)} onSave={onNalSaved} /> {/* [NAL-NOTE] */}
      <HoldDialog held={held} busy={releasing} onLeave={onLeaveHeld} onRelease={() => { void onReleaseHeld(); }} /> {/* [ASSIGNEE-HOLD] [ASSIGN-ALL] either button → the run goes on */}
      {isAdmin && <AssigneeNamesDialog open={namesOpen} onClose={() => setNamesOpen(false)} />} {/* [ASSIGNEE-NAMES] */}
    </Wrap>
  );
};

/** [PURE-INK 2026-10-15, user] "also check the lead management tab and it will also not use the light shade of the colours and make
 *  sure in both the themes the colour should look proper": the page under the pure-ink theme (components/PureInk) — every plain
 *  text #ffffff in the dark theme, #000000 in the light theme; the coloured chips / marks keep their colours. */
const LeadAssignmentPureInk: React.FC = () => <PureInk><LeadAssignmentPage /></PureInk>;

export default LeadAssignmentPureInk;
